import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Car, Plane, MapPin, Users, User, Save, FileText, ArrowUpDown, ArrowUp, ArrowDown, X,
  Calendar, List, Clock, AlertTriangle, Download, ChevronLeft, ChevronRight, ChevronDown,
  TrendingUp, CheckCircle, AlertCircle, Filter, LayoutGrid, Map, Eye, Edit2, Settings, Ticket, Building2
} from 'lucide-react';
import axios from 'axios';
import logger from './utils/logger';
import TravelMapPanel from './TravelMapPanel';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ventro.energy';

// Airports list for flight mode
const AIRPORTS = {
  // Poland
  'WAW': { name: 'Warsaw Chopin', city: 'Warsaw', country: 'Poland' },
  'WMI': { name: 'Warsaw Modlin', city: 'Warsaw', country: 'Poland' },
  'KRK': { name: 'Kraków Balice', city: 'Kraków', country: 'Poland' },
  'GDN': { name: 'Gdańsk Lech Wałęsa', city: 'Gdańsk', country: 'Poland' },
  'WRO': { name: 'Wrocław Copernicus', city: 'Wrocław', country: 'Poland' },
  'POZ': { name: 'Poznań Ławica', city: 'Poznań', country: 'Poland' },
  'KTW': { name: 'Katowice Pyrzowice', city: 'Katowice', country: 'Poland' },
  'SZZ': { name: 'Szczecin Goleniów', city: 'Szczecin', country: 'Poland' },
  'RZE': { name: 'Rzeszów Jasionka', city: 'Rzeszów', country: 'Poland' },
  'LUZ': { name: 'Lublin', city: 'Lublin', country: 'Poland' },
  'BZG': { name: 'Bydgoszcz Paderewski', city: 'Bydgoszcz', country: 'Poland' },
  'LCJ': { name: 'Łódź Lublinek', city: 'Łódź', country: 'Poland' },

  // Estonia
  'TLL': { name: 'Tallinn Lennart Meri', city: 'Tallinn', country: 'Estonia' },
  'TAY': { name: 'Tartu', city: 'Tartu', country: 'Estonia' },
  'URE': { name: 'Kuressaare', city: 'Kuressaare', country: 'Estonia' },
  'EPU': { name: 'Pärnu', city: 'Pärnu', country: 'Estonia' },

  // Latvia
  'RIX': { name: 'Riga International', city: 'Riga', country: 'Latvia' },
  'LPX': { name: 'Liepāja', city: 'Liepāja', country: 'Latvia' },

  // Lithuania
  'VNO': { name: 'Vilnius International', city: 'Vilnius', country: 'Lithuania' },
  'KUN': { name: 'Kaunas', city: 'Kaunas', country: 'Lithuania' },
  'PLQ': { name: 'Palanga', city: 'Palanga', country: 'Lithuania' },

  // Belgium
  'BRU': { name: 'Brussels Airport', city: 'Brussels', country: 'Belgium' },
  'CRL': { name: 'Brussels South Charleroi', city: 'Charleroi', country: 'Belgium' },
  'ANR': { name: 'Antwerp International', city: 'Antwerp', country: 'Belgium' },
  'OST': { name: 'Ostend-Bruges', city: 'Ostend', country: 'Belgium' },
  'LGG': { name: 'Liège', city: 'Liège', country: 'Belgium' },

  // United Kingdom
  'LHR': { name: 'London Heathrow', city: 'London', country: 'UK' },
  'LGW': { name: 'London Gatwick', city: 'London', country: 'UK' },
  'STN': { name: 'London Stansted', city: 'London', country: 'UK' },
  'LTN': { name: 'London Luton', city: 'London', country: 'UK' },
  'LCY': { name: 'London City', city: 'London', country: 'UK' },
  'SEN': { name: 'London Southend', city: 'London', country: 'UK' },
  'MAN': { name: 'Manchester', city: 'Manchester', country: 'UK' },
  'BHX': { name: 'Birmingham', city: 'Birmingham', country: 'UK' },
  'EDI': { name: 'Edinburgh', city: 'Edinburgh', country: 'UK' },
  'GLA': { name: 'Glasgow', city: 'Glasgow', country: 'UK' },
  'BRS': { name: 'Bristol', city: 'Bristol', country: 'UK' },
  'LPL': { name: 'Liverpool John Lennon', city: 'Liverpool', country: 'UK' },
  'NCL': { name: 'Newcastle', city: 'Newcastle', country: 'UK' },
  'LBA': { name: 'Leeds Bradford', city: 'Leeds', country: 'UK' },
  'EMA': { name: 'East Midlands', city: 'Nottingham', country: 'UK' },
  'ABZ': { name: 'Aberdeen', city: 'Aberdeen', country: 'UK' },
  'BFS': { name: 'Belfast International', city: 'Belfast', country: 'UK' },
  'CWL': { name: 'Cardiff', city: 'Cardiff', country: 'UK' },

  // Germany
  'BER': { name: 'Berlin Brandenburg', city: 'Berlin', country: 'Germany' },
  'FRA': { name: 'Frankfurt', city: 'Frankfurt', country: 'Germany' },
  'MUC': { name: 'Munich', city: 'Munich', country: 'Germany' },
  'DUS': { name: 'Düsseldorf', city: 'Düsseldorf', country: 'Germany' },
  'HAM': { name: 'Hamburg', city: 'Hamburg', country: 'Germany' },
  'CGN': { name: 'Cologne Bonn', city: 'Cologne', country: 'Germany' },
  'STR': { name: 'Stuttgart', city: 'Stuttgart', country: 'Germany' },
  'HAJ': { name: 'Hannover', city: 'Hannover', country: 'Germany' },
  'NUE': { name: 'Nuremberg', city: 'Nuremberg', country: 'Germany' },
  'LEJ': { name: 'Leipzig/Halle', city: 'Leipzig', country: 'Germany' },
  'DRS': { name: 'Dresden', city: 'Dresden', country: 'Germany' },
  'BRE': { name: 'Bremen', city: 'Bremen', country: 'Germany' },

  // France
  'CDG': { name: 'Paris Charles de Gaulle', city: 'Paris', country: 'France' },
  'ORY': { name: 'Paris Orly', city: 'Paris', country: 'France' },
  'NCE': { name: 'Nice Côte d\'Azur', city: 'Nice', country: 'France' },
  'LYS': { name: 'Lyon Saint-Exupéry', city: 'Lyon', country: 'France' },
  'MRS': { name: 'Marseille Provence', city: 'Marseille', country: 'France' },
  'TLS': { name: 'Toulouse Blagnac', city: 'Toulouse', country: 'France' },
  'BOD': { name: 'Bordeaux', city: 'Bordeaux', country: 'France' },
  'NTE': { name: 'Nantes Atlantique', city: 'Nantes', country: 'France' },
  'SXB': { name: 'Strasbourg', city: 'Strasbourg', country: 'France' },

  // Netherlands
  'AMS': { name: 'Amsterdam Schiphol', city: 'Amsterdam', country: 'Netherlands' },
  'EIN': { name: 'Eindhoven', city: 'Eindhoven', country: 'Netherlands' },
  'RTM': { name: 'Rotterdam The Hague', city: 'Rotterdam', country: 'Netherlands' },

  // Scandinavia
  'CPH': { name: 'Copenhagen', city: 'Copenhagen', country: 'Denmark' },
  'BLL': { name: 'Billund', city: 'Billund', country: 'Denmark' },
  'AAL': { name: 'Aalborg', city: 'Aalborg', country: 'Denmark' },
  'OSL': { name: 'Oslo Gardermoen', city: 'Oslo', country: 'Norway' },
  'BGO': { name: 'Bergen Flesland', city: 'Bergen', country: 'Norway' },
  'TRD': { name: 'Trondheim Værnes', city: 'Trondheim', country: 'Norway' },
  'SVG': { name: 'Stavanger Sola', city: 'Stavanger', country: 'Norway' },
  'ARN': { name: 'Stockholm Arlanda', city: 'Stockholm', country: 'Sweden' },
  'GOT': { name: 'Gothenburg Landvetter', city: 'Gothenburg', country: 'Sweden' },
  'MMX': { name: 'Malmö Sturup', city: 'Malmö', country: 'Sweden' },
  'HEL': { name: 'Helsinki Vantaa', city: 'Helsinki', country: 'Finland' },
  'TMP': { name: 'Tampere-Pirkkala', city: 'Tampere', country: 'Finland' },
  'TKU': { name: 'Turku', city: 'Turku', country: 'Finland' },
  'OUL': { name: 'Oulu', city: 'Oulu', country: 'Finland' },
  'RVN': { name: 'Rovaniemi', city: 'Rovaniemi', country: 'Finland' },
  'KUO': { name: 'Kuopio', city: 'Kuopio', country: 'Finland' },
  'JYV': { name: 'Jyväskylä', city: 'Jyväskylä', country: 'Finland' },
  'VAA': { name: 'Vaasa', city: 'Vaasa', country: 'Finland' },

  // Spain
  'MAD': { name: 'Madrid Barajas', city: 'Madrid', country: 'Spain' },
  'BCN': { name: 'Barcelona El Prat', city: 'Barcelona', country: 'Spain' },
  'PMI': { name: 'Palma de Mallorca', city: 'Palma', country: 'Spain' },
  'AGP': { name: 'Málaga Costa del Sol', city: 'Málaga', country: 'Spain' },
  'ALC': { name: 'Alicante-Elche', city: 'Alicante', country: 'Spain' },
  'VLC': { name: 'Valencia', city: 'Valencia', country: 'Spain' },
  'SVQ': { name: 'Seville', city: 'Seville', country: 'Spain' },
  'BIO': { name: 'Bilbao', city: 'Bilbao', country: 'Spain' },

  // Italy
  'FCO': { name: 'Rome Fiumicino', city: 'Rome', country: 'Italy' },
  'CIA': { name: 'Rome Ciampino', city: 'Rome', country: 'Italy' },
  'MXP': { name: 'Milan Malpensa', city: 'Milan', country: 'Italy' },
  'LIN': { name: 'Milan Linate', city: 'Milan', country: 'Italy' },
  'BGY': { name: 'Milan Bergamo', city: 'Bergamo', country: 'Italy' },
  'VCE': { name: 'Venice Marco Polo', city: 'Venice', country: 'Italy' },
  'NAP': { name: 'Naples Capodichino', city: 'Naples', country: 'Italy' },
  'BLQ': { name: 'Bologna', city: 'Bologna', country: 'Italy' },
  'FLR': { name: 'Florence', city: 'Florence', country: 'Italy' },
  'PSA': { name: 'Pisa', city: 'Pisa', country: 'Italy' },
  'TRN': { name: 'Turin Caselle', city: 'Turin', country: 'Italy' },

  // Central Europe
  'PRG': { name: 'Prague Václav Havel', city: 'Prague', country: 'Czech Republic' },
  'BRQ': { name: 'Brno Tuřany', city: 'Brno', country: 'Czech Republic' },
  'VIE': { name: 'Vienna', city: 'Vienna', country: 'Austria' },
  'SZG': { name: 'Salzburg', city: 'Salzburg', country: 'Austria' },
  'INN': { name: 'Innsbruck', city: 'Innsbruck', country: 'Austria' },
  'GRZ': { name: 'Graz', city: 'Graz', country: 'Austria' },
  'ZRH': { name: 'Zürich', city: 'Zürich', country: 'Switzerland' },
  'GVA': { name: 'Geneva', city: 'Geneva', country: 'Switzerland' },
  'BSL': { name: 'Basel-Mulhouse', city: 'Basel', country: 'Switzerland' },
  'BUD': { name: 'Budapest Ferenc Liszt', city: 'Budapest', country: 'Hungary' },
  'DEB': { name: 'Debrecen', city: 'Debrecen', country: 'Hungary' },
  'BTS': { name: 'Bratislava', city: 'Bratislava', country: 'Slovakia' },
  'KSC': { name: 'Košice', city: 'Košice', country: 'Slovakia' },
  'LJU': { name: 'Ljubljana Jože Pučnik', city: 'Ljubljana', country: 'Slovenia' },
  'ZAG': { name: 'Zagreb Franjo Tuđman', city: 'Zagreb', country: 'Croatia' },
  'SPU': { name: 'Split', city: 'Split', country: 'Croatia' },
  'DBV': { name: 'Dubrovnik', city: 'Dubrovnik', country: 'Croatia' },

  // Other
  'DUB': { name: 'Dublin', city: 'Dublin', country: 'Ireland' },
  'SNN': { name: 'Shannon', city: 'Shannon', country: 'Ireland' },
  'ORK': { name: 'Cork', city: 'Cork', country: 'Ireland' },
  'LIS': { name: 'Lisbon', city: 'Lisbon', country: 'Portugal' },
  'OPO': { name: 'Porto', city: 'Porto', country: 'Portugal' },
  'FAO': { name: 'Faro', city: 'Faro', country: 'Portugal' },
  'ATH': { name: 'Athens', city: 'Athens', country: 'Greece' },
  'SKG': { name: 'Thessaloniki', city: 'Thessaloniki', country: 'Greece' },
  'HER': { name: 'Heraklion', city: 'Heraklion', country: 'Greece' },
  'SOF': { name: 'Sofia', city: 'Sofia', country: 'Bulgaria' },
  'OTP': { name: 'Bucharest Henri Coandă', city: 'Bucharest', country: 'Romania' },
  'CLJ': { name: 'Cluj-Napoca', city: 'Cluj-Napoca', country: 'Romania' },
  'BEG': { name: 'Belgrade Nikola Tesla', city: 'Belgrade', country: 'Serbia' },
  'SJJ': { name: 'Sarajevo', city: 'Sarajevo', country: 'Bosnia' },
  'TGD': { name: 'Podgorica', city: 'Podgorica', country: 'Montenegro' },
  'TIA': { name: 'Tirana', city: 'Tirana', country: 'Albania' },
  'SKP': { name: 'Skopje', city: 'Skopje', country: 'North Macedonia' },
  'IST': { name: 'Istanbul', city: 'Istanbul', country: 'Turkey' },
  'SAW': { name: 'Istanbul Sabiha Gökçen', city: 'Istanbul', country: 'Turkey' },
  'KEF': { name: 'Reykjavik Keflavik', city: 'Reykjavik', country: 'Iceland' },
  'LUX': { name: 'Luxembourg', city: 'Luxembourg', country: 'Luxembourg' },
};

const TravelCoordination = () => {
  const navigate = useNavigate();
  const [allPeople, setAllPeople] = useState([]); // People travels for table
  const [allTravels, setAllTravels] = useState([]); // All travels (people + cars) for filtering
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [cars, setCars] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'start_date', direction: 'asc' });
  const [activeTab, setActiveTab] = useState('check_in'); // 'check_in' or 'check_out'
  const [showModal, setShowModal] = useState(false);
  const [allCars, setAllCars] = useState([]); // All cars from API
  const [availableCars, setAvailableCars] = useState([]); // Filtered cars for selected dates
  const [usersData, setUsersData] = useState({}); // Map of user_id -> user data with addresses
  const [accommodationAssignments, setAccommodationAssignments] = useState([]); // Room assignments for accommodation
  const [localTransports, setLocalTransports] = useState([]); // Local transports (airport pickups/dropoffs)
  const [travelLocations, setTravelLocations] = useState([]); // Saved pickup/dropoff locations from database
  const [showAddLocationModal, setShowAddLocationModal] = useState({ show: false, type: 'both', defaultValue: '' });

  // View mode: 'table', 'calendar', 'timeline'
  const [viewMode, setViewMode] = useState('table');

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

  // Timeline days range and start date
  const [timelineDays, setTimelineDays] = useState(14);
  const [timelineStartDate, setTimelineStartDate] = useState(new Date());

  // Quick assign panel
  const [showQuickAssign, setShowQuickAssign] = useState(false);

  // Change history (audit log)
  const [changeHistory, setChangeHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Daily summary panel
  const [summaryDate, setSummaryDate] = useState(null);

  // Email preview modal
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Attachments panel
  const [showAttachmentsPanel, setShowAttachmentsPanel] = useState(false);

  // Export dropdown
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Note modal state
  const [noteModal, setNoteModal] = useState({ show: false, travelId: null, note: '', isEditing: false });

  // Person travel history modal state
  const [personHistoryModal, setPersonHistoryModal] = useState({ show: false, userId: null, userName: '' });

  // ML Prediction tooltip state
  const [mlPrediction, setMlPrediction] = useState(null);

  // Smart Alerts Panel state
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [projectConfigQueue, setProjectConfigQueue] = useState([]); // Queue of project groups to configure
  const [uploadingTicketId, setUploadingTicketId] = useState(null); // Track which travel is uploading
  const [uploadSuccessId, setUploadSuccessId] = useState(null); // Track successful upload for checkmark
  const [mlPredictionLoading, setMlPredictionLoading] = useState(false);
  const [showMlTooltip, setShowMlTooltip] = useState(false);
  const [hoveredTravelId, setHoveredTravelId] = useState(null);

  // Distance calculation from dropoff to home
  const [dropoffToHomeDistance, setDropoffToHomeDistance] = useState(null);
  const [dropoffToHomeLoading, setDropoffToHomeLoading] = useState(false);

  // Date filter
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  // Project filter
  const [projectFilter, setProjectFilter] = useState('');

  // Person name search
  const [searchQuery, setSearchQuery] = useState('');

  // Selected date from calendar/timeline click (for cross-view filtering)
  const [filterByDate, setFilterByDate] = useState(null);

  // Status filter for table view (multiple selection)
  const [filterByStatus, setFilterByStatus] = useState([]); // empty = all, or array of statuses

  // Form data for selected people
  const [formData, setFormData] = useState({
    selectedCar: '',
    transportType: 'car', // 'car' or 'plane'
    projectId: '',
    notes: '',
    // Flight-specific fields
    departureTime: '',
    arrivalTime: '',
    flightNumber: '',
    departureAirport: '',
    destinationAirport: '',
    // Organized transport (for plane mode)
    organizedTransport: false,
    transportCompany: '',
    meetingPoint: '',
    meetingTime: '',
    dropoffLocation: '',  // Final destination (for check-out: home address)
    // Driver assignment
    driverId: ''
  });

  // Load form data from selected draft(s) and auto-suggest airports for check-out
  useEffect(() => {
    if (selectedPeople.length > 0 && showModal) {
      const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
      const allAreDraftsOrPending = selectedTravels.every(t => ['draft', 'pending'].includes(getTravelStatus(t)));

      if (allAreDraftsOrPending && selectedTravels.length > 0) {
        // Check if all selected drafts have the same values
        const firstTravel = selectedTravels[0];

        // Check car_id
        const sameCar = selectedTravels.every(t => t.car_id === firstTravel.car_id);

        // Check transport_type
        const sameTransportType = selectedTravels.every(t => t.transport_type === firstTravel.transport_type);

        // Check notes/description
        const sameNotes = selectedTravels.every(t => t.description === firstTravel.description);

        // Check departure_time
        const sameDepartureTime = selectedTravels.every(t => t.departure_time === firstTravel.departure_time);

        // Check flight_number
        const sameFlightNumber = selectedTravels.every(t => t.flight_number === firstTravel.flight_number);

        // Try to extract project ID from address
        let projectId = '';
        if (firstTravel.address && !isAutoGeneratedAddress(firstTravel.address)) {
          const sameAddress = selectedTravels.every(t => t.address === firstTravel.address);
          if (sameAddress) {
            // Parse project name from address format: "ProjectName - street city country"
            const projectNameMatch = firstTravel.address.match(/^(.+?)\s*-\s*/);
            if (projectNameMatch) {
              const projectName = projectNameMatch[1].trim();
              const matchingProject = projects.find(p => p.name === projectName);
              if (matchingProject) {
                projectId = matchingProject.id.toString();
              }
            }
          }
        }

        // Check arrival time
        const sameArrivalTime = selectedTravels.every(t => t.arrival_time === firstTravel.arrival_time);

        // For check-out: Find the corresponding check-in to get reverse route
        let suggestedDepartureAirport = '';
        let suggestedDestinationAirport = '';
        let suggestedTransportType = sameTransportType ? (firstTravel.transport_type || 'car') : 'car';
        let suggestedDropoffLocation = '';

        if (firstTravel.travel_type === 'check_out' && selectedTravels.length === 1) {
          const userId = firstTravel.user_id;
          const checkOutDate = new Date(firstTravel.start_date);

          // Find recent check-in for this user (within last 30 days) with flight info
          const recentCheckIn = allPeople.find(travel => {
            if (travel.user_id !== userId) return false;
            if (travel.travel_type !== 'check_in') return false;
            if (!travel.departure_airport || !travel.arrival_airport) return false;

            const checkInDate = new Date(travel.start_date);
            const daysDiff = (checkOutDate - checkInDate) / (1000 * 60 * 60 * 24);
            return daysDiff > 0 && daysDiff <= 30;
          });

          if (recentCheckIn) {
            // Swap airports for return flight
            suggestedDepartureAirport = recentCheckIn.arrival_airport; // They arrived here, now depart from here
            suggestedDestinationAirport = recentCheckIn.departure_airport; // They departed from home, return there
            suggestedTransportType = 'plane';
            console.log(`Auto-suggesting return flight: ${suggestedDepartureAirport} → ${suggestedDestinationAirport} (based on check-in from ${recentCheckIn.start_date})`);
          }

          // Suggest home address for dropoff
          const homeAddress = usersData[userId]?.personal_address;
          if (homeAddress) {
            suggestedDropoffLocation = homeAddress;
          }
        }

        // Load existing draft data (only pre-fill if all selected have same value)
        // Use suggested airports if no existing data
        setFormData({
          selectedCar: sameCar ? (firstTravel.car_id || '') : '',
          transportType: suggestedTransportType,
          projectId: projectId,
          notes: sameNotes ? (firstTravel.description || '') : '',
          departureTime: sameDepartureTime ? (firstTravel.departure_time || '') : '',
          arrivalTime: sameArrivalTime ? (firstTravel.arrival_time || '') : '',
          flightNumber: sameFlightNumber ? (firstTravel.flight_number || '') : '',
          departureAirport: firstTravel.departure_airport || suggestedDepartureAirport,
          destinationAirport: firstTravel.arrival_airport || suggestedDestinationAirport,
          organizedTransport: firstTravel.organized_transport || false,
          transportCompany: firstTravel.transport_company || '',
          meetingPoint: firstTravel.meeting_point || '',
          meetingTime: firstTravel.meeting_time || '',
          dropoffLocation: firstTravel.dropoff_location || suggestedDropoffLocation,
          driverId: firstTravel.driver_id || ''
        });
      }
    }
  }, [selectedPeople, showModal, allPeople, projects, usersData]);

  // Calculate distance from dropoff to home when dropoff differs from home address
  const calculateDropoffToHomeDistance = async (dropoffAddress, homeAddress) => {
    if (!dropoffAddress || !homeAddress) return;
    if (dropoffAddress.trim().toLowerCase() === homeAddress.trim().toLowerCase()) {
      setDropoffToHomeDistance(null);
      return;
    }

    setDropoffToHomeLoading(true);
    try {
      // Geocode both addresses using Nominatim
      const geocodeAddress = async (address) => {
        const cleanAddress = address.replace(/\d{2}-\d{3}/, '').replace(/\/\d+/, '').trim();
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cleanAddress + ', Poland')}&limit=1`,
          { headers: { 'User-Agent': 'WindunoApp/1.0' } }
        );
        const data = await response.json();
        if (data && data.length > 0) {
          return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
        }
        return null;
      };

      const [dropoffCoords, homeCoords] = await Promise.all([
        geocodeAddress(dropoffAddress),
        geocodeAddress(homeAddress)
      ]);

      if (dropoffCoords && homeCoords) {
        // Calculate distance using Haversine formula
        const R = 6371; // Earth's radius in km
        const dLat = (homeCoords.lat - dropoffCoords.lat) * Math.PI / 180;
        const dLon = (homeCoords.lon - dropoffCoords.lon) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(dropoffCoords.lat * Math.PI / 180) * Math.cos(homeCoords.lat * Math.PI / 180) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;

        // Estimate driving time (avg 60 km/h with traffic)
        const drivingHours = distance / 60;
        const hours = Math.floor(drivingHours);
        const minutes = Math.round((drivingHours - hours) * 60);

        setDropoffToHomeDistance({
          km: Math.round(distance),
          time: hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`,
          dropoffCoords,
          homeCoords,
          mapsUrl: `https://www.google.com/maps/dir/?api=1&origin=${dropoffCoords.lat},${dropoffCoords.lon}&destination=${homeCoords.lat},${homeCoords.lon}&travelmode=driving`
        });
      } else {
        setDropoffToHomeDistance(null);
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      setDropoffToHomeDistance(null);
    } finally {
      setDropoffToHomeLoading(false);
    }
  };

  // Save a new travel location (pickup/dropoff point) to the database
  const saveNewTravelLocation = async (locationData) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/travel-locations/`,
        locationData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Add the new location to the state
      setTravelLocations(prev => [...prev, response.data]);
      return response.data;
    } catch (error) {
      logger.error('Error saving travel location:', error);
      throw error;
    }
  };

  // Trigger distance calculation when dropoff changes (for any travel type)
  useEffect(() => {
    if (formData.organizedTransport && formData.dropoffLocation && selectedPeople.length > 0) {
      const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
      if (selectedTravels.length > 0) {
        // Get home address of the first selected person
        const firstTravel = selectedTravels[0];
        const homeAddress = usersData[firstTravel.user_id]?.personal_address;
        console.log('Dropoff to home check:', { dropoff: formData.dropoffLocation, home: homeAddress, userId: firstTravel.user_id });

        if (homeAddress && formData.dropoffLocation.trim().toLowerCase() !== homeAddress.trim().toLowerCase()) {
          const timeoutId = setTimeout(() => {
            calculateDropoffToHomeDistance(formData.dropoffLocation, homeAddress);
          }, 500); // Debounce
          return () => clearTimeout(timeoutId);
        } else {
          setDropoffToHomeDistance(null);
        }
      }
    } else {
      setDropoffToHomeDistance(null);
    }
  }, [formData.dropoffLocation, formData.organizedTransport, selectedPeople, allPeople, usersData]);

  // Store ticket files for each person (plane tickets or ferry tickets for car) - supports multiple files
  const [ticketFiles, setTicketFiles] = useState({});

  const handleFileChange = (travelId, files) => {
    if (!files || files.length === 0) return;
    setTicketFiles(prev => ({
      ...prev,
      [travelId]: [...(prev[travelId] || []), ...Array.from(files)]
    }));
  };

  const removeFile = (travelId, fileIndex) => {
    setTicketFiles(prev => ({
      ...prev,
      [travelId]: prev[travelId].filter((_, idx) => idx !== fileIndex)
    }));
  };

  // Load change history from backend (called when opening history modal)
  const loadChangeHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/travel-coordination-activity/recent/?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const activityData = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.results) ? response.data.results : []);

      // Transform backend format to frontend format
      const formattedHistory = activityData.map(activity => ({
        timestamp: new Date(activity.created_at),
        action: activity.action_type,
        description: activity.description,
        peopleCount: activity.travel_ids?.length || 0,
        people: activity.details?.people || [],
        car: activity.details?.car || null,
        project: activity.project_data?.name || activity.details?.project || null,
        transportType: activity.details?.transport_type || null,
        changedBy: activity.user_data
          ? `${activity.user_data.first_name || ''} ${activity.user_data.last_name || ''}`.trim() || activity.user_data.email
          : 'Unknown'
      }));
      setChangeHistory(formattedHistory);
    } catch (error) {
      // Backend not available or migration not run - silently use local history only
      console.log('Backend history not available, using local history');
    }
  };

  // Log activity to backend (fire-and-forget)
  const logActivity = async (actionType, description, details = {}, travelIds = [], projectId = null) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API_BASE_URL}/api/travel-coordination-activity/`, {
        action_type: actionType,
        description: description,
        details: details,
        travel_ids: travelIds,
        project: projectId
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      // Backend not available - activity logging is not critical, silently ignore
    }
  };

  // Get all travels for a specific person (for history modal)
  const getPersonTravelHistory = (userId) => {
    if (!userId) return [];

    // Get all travels for this user from allPeople (check-in/check-out)
    const userTravels = allPeople
      .filter(t => t.user_id === userId)
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date)); // Most recent first

    return userTravels;
  };

  // Open person history modal
  const openPersonHistory = (userId, userName) => {
    setPersonHistoryModal({ show: true, userId, userName });
  };

  // Quick upload ticket directly (without opening modal)
  const handleQuickTicketUpload = async (travelId, file) => {
    if (!file) return;

    setUploadingTicketId(travelId);
    setUploadSuccessId(null);

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('attachment_flight', file);

      await axios.patch(`${API_BASE_URL}/api/travel/${travelId}/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Get travel info for logging
      const travel = allPeople.find(t => t.id === travelId);
      const personName = travel ? getUserName(travel) : 'Unknown';

      // Update local state
      setAllPeople(prev => prev.map(t =>
        t.id === travelId ? { ...t, attachment_flight: URL.createObjectURL(file) } : t
      ));

      // Log to local history
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const currentUserName = userData.first_name || userData.last_name
        ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
        : userData.email || 'Unknown';

      const historyEntry = {
        timestamp: new Date(),
        action: 'ticket_uploaded',
        description: `Uploaded flight ticket for ${personName}`,
        peopleCount: 1,
        people: [personName],
        car: null,
        project: null,
        transportType: 'plane',
        changedBy: currentUserName
      };
      setChangeHistory(prev => [historyEntry, ...prev].slice(0, 50));

      // Log to backend (async, don't wait)
      logActivity('ticket_uploaded', `Uploaded flight ticket for ${personName}`, {
        people: [personName],
        transport_type: 'plane',
        file_name: file.name
      }, [travelId], null);

      // Show success checkmark
      setUploadingTicketId(null);
      setUploadSuccessId(travelId);

      // Clear success after 2 seconds
      setTimeout(() => {
        setUploadSuccessId(null);
      }, 2000);

      console.log('✅ Ticket uploaded for travel', travelId);
    } catch (error) {
      console.error('Error uploading ticket:', error);
      setUploadingTicketId(null);
      alert('Failed to upload ticket');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-detect project for selected people based on check-in/check-out dates
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/resource-planning-assignments/?page_size=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const assignmentsData = Array.isArray(response.data?.results)
          ? response.data.results
          : (Array.isArray(response.data) ? response.data : []);

        logger.info('✅ Fetched assignments:', assignmentsData.length);
        setAssignments(assignmentsData);
      } catch (error) {
        logger.error('Error fetching assignments:', error);
      }
    };
    fetchAssignments();
  }, []);

  // Reset formData when modal closes
  useEffect(() => {
    if (!showModal) {
      setFormData({
        selectedCar: '',
        transportType: 'car',
        projectId: '',
        notes: '',
        departureTime: '',
        arrivalTime: '',
        flightNumber: '',
        departureAirport: '',
        destinationAirport: '',
        organizedTransport: false,
        transportCompany: '',
        meetingPoint: '',
        meetingTime: '',
        dropoffLocation: '',
        driverId: ''
      });
    }
  }, [showModal]);

  // Auto-detect project when modal opens or selection changes
  useEffect(() => {
    if (!showModal || selectedPeople.length === 0) return;

    logger.info('🔍 Auto-detect project triggered:', {
      showModal,
      selectedPeopleCount: selectedPeople.length,
      assignmentsCount: assignments.length
    });

    const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
    if (selectedTravels.length === 0) return;

    logger.info('Selected travels:', selectedTravels.map(t => ({
      id: t.id,
      userId: t.user_id,
      date: t.start_date,
      type: t.travel_type
    })));

    // Try to find a matching project for these travels
    const detectedProjects = new Set();

    selectedTravels.forEach(travel => {
      const userId = travel.user_id;
      const travelDate = new Date(travel.start_date);
      const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
      const travelType = travel.travel_type; // 'check_in' or 'check_out'

      logger.info(`\n🔎 Analyzing travel:`, {
        userId,
        travelDate: travelDateOnly.toISOString().split('T')[0],
        travelType
      });

      // Find user's assignments - handle both a.user as ID or as object with id
      const userAssignments = assignments.filter(a => {
        const aUserId = typeof a.user === 'object' ? a.user?.id : a.user;
        return aUserId === userId || a.user_id === userId;
      });
      logger.info(`  Found ${userAssignments.length} assignments for user ${userId}`);

      if (userAssignments.length > 0) {
        logger.info('  User assignments:', userAssignments.map(a => ({
          projectId: a.project_id || a.project,
          startDate: a.start_date,
          endDate: a.end_date
        })));
      }

      if (travelType === 'check_in') {
        // Check-in: find project that starts 0-2 days after this travel
        const matchingAssignment = userAssignments.find(assignment => {
          const projectStart = new Date(assignment.start_date);
          const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
          const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));

          logger.info(`    Checking assignment: projectStart=${projectStartOnly.toISOString().split('T')[0]}, daysBefore=${daysBefore}`);

          return daysBefore >= 0 && daysBefore <= 2;
        });
        if (matchingAssignment) {
          const projectId = matchingAssignment.project_id || matchingAssignment.project;
          logger.info(`  ✅ Found matching check-in assignment! Project ID: ${projectId}`);
          if (projectId) detectedProjects.add(projectId);
        } else {
          logger.info(`  ❌ No matching check-in assignment found`);
        }
      } else if (travelType === 'check_out') {
        // Check-out: find project that ends 0-2 days before this travel
        const matchingAssignment = userAssignments.find(assignment => {
          const projectEnd = new Date(assignment.end_date);
          const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
          const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));

          logger.info(`    Checking assignment: projectEnd=${projectEndOnly.toISOString().split('T')[0]}, daysAfter=${daysAfter}`);

          return daysAfter >= 0 && daysAfter <= 2;
        });
        if (matchingAssignment) {
          const projectId = matchingAssignment.project_id || matchingAssignment.project;
          logger.info(`  ✅ Found matching check-out assignment! Project ID: ${projectId}`);
          if (projectId) detectedProjects.add(projectId);
        } else {
          logger.info(`  ❌ No matching check-out assignment found`);
        }
      }
    });

    logger.info('\n🎯 Detected projects:', Array.from(detectedProjects));

    // If all selected people have the same project, auto-fill it
    if (detectedProjects.size === 1) {
      const detectedProjectId = Array.from(detectedProjects)[0];
      logger.info(`✨ Auto-filling project: ${detectedProjectId}`);
      setFormData(prev => {
        // Only auto-fill if projectId is currently empty
        if (!prev.projectId) {
          logger.info(`  Setting formData.projectId to ${detectedProjectId}`);
          return { ...prev, projectId: detectedProjectId.toString() };
        } else {
          logger.info(`  Skipping auto-fill, projectId already set to ${prev.projectId}`);
        }
        return prev;
      });
    } else if (detectedProjects.size > 1) {
      logger.info(`⚠️ Multiple projects detected (${detectedProjects.size}), not auto-filling`);
    } else {
      logger.info(`⚠️ No projects detected`);
    }
  }, [showModal, selectedPeople, allPeople, assignments]);

  // Show all company cars when people are selected (for assignment)
  useEffect(() => {
    if (selectedPeople.length === 0) {
      setAvailableCars([]);
      return;
    }

    // Filter only cars (equipment_type === 'car'), exclude machines and other equipment
    const carsOnly = allCars.filter(car => car.equipment_type === 'car');
    setAvailableCars(carsOnly);

    logger.info('Available cars for assignment:', carsOnly.length, '(filtered from', allCars.length, 'total vehicles)');
  }, [selectedPeople, allCars]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      // Fetch travels, cars, projects, users, room assignments, local transports and travel locations
      const [travelsResponse, carsResponse, projectsResponse, usersResponse, roomAssignmentsResponse, localTransportsResponse, travelLocationsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/travel/?page_size=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/cars/?page_size=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/projects/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/users/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/room-assignments/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/local-transports/?page_size=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/travel-locations/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Extract travels from paginated response
      const travels = Array.isArray(travelsResponse.data?.results)
        ? travelsResponse.data.results
        : (Array.isArray(travelsResponse.data) ? travelsResponse.data : []);

      // Filter people with check-in or check-out (for table display)
      const filteredPeople = travels.filter(travel => {
        return (travel.travel_type === 'check_in' || travel.travel_type === 'check_out') &&
               travel.user_id;
      });

      setAllPeople(filteredPeople);
      setAllTravels(travels); // Store all travels (including car travels) for filtering cars

      // Parse cars response
      logger.info('Cars API response:', carsResponse.data);
      const carsData = Array.isArray(carsResponse.data?.results)
        ? carsResponse.data.results
        : (Array.isArray(carsResponse.data) ? carsResponse.data : []);

      logger.info('Parsed cars data:', carsData);
      setAllCars(carsData);
      setCars(carsData); // For backward compatibility
      logger.info('Loaded cars:', carsData.length);

      setProjects(Array.isArray(projectsResponse.data) ? projectsResponse.data : []);

      // Process users data into a map for quick lookup
      const usersArray = Array.isArray(usersResponse.data) ? usersResponse.data : [];
      const usersMap = {};
      usersArray.forEach(user => {
        usersMap[user.id] = user;
      });
      setUsersData(usersMap);
      logger.info('Loaded users:', usersArray.length);

      // Process room assignments
      const roomAssignmentsData = Array.isArray(roomAssignmentsResponse.data)
        ? roomAssignmentsResponse.data
        : (Array.isArray(roomAssignmentsResponse.data?.results) ? roomAssignmentsResponse.data.results : []);
      setAccommodationAssignments(roomAssignmentsData);
      logger.info('Loaded room assignments:', roomAssignmentsData.length);

      // Process local transports
      const localTransportsData = Array.isArray(localTransportsResponse.data)
        ? localTransportsResponse.data
        : (Array.isArray(localTransportsResponse.data?.results) ? localTransportsResponse.data.results : []);
      setLocalTransports(localTransportsData);
      logger.info('Loaded local transports:', localTransportsData.length);

      // Process travel locations (pickup/dropoff points)
      const travelLocationsData = Array.isArray(travelLocationsResponse.data)
        ? travelLocationsResponse.data
        : (Array.isArray(travelLocationsResponse.data?.results) ? travelLocationsResponse.data.results : []);
      setTravelLocations(travelLocationsData);
      logger.info('Loaded travel locations:', travelLocationsData.length);

    } catch (error) {
      logger.error('Error fetching travel coordination data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ML prediction for a travel using smart endpoint
  const fetchMlPrediction = async (travel) => {
    if (!travel) return;

    setMlPredictionLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      // Get origin address (user's home)
      const userAddress = usersData[travel.user_id]?.personal_address || '';

      // Get destination address - extract clean address without project prefix
      let destinationAddress = travel.address || '';

      // Remove project name prefix if present (format: "ProjectName - actual address")
      // But only if the result is a valid address (longer than 5 chars)
      const addressMatch = destinationAddress.match(/^.+?\s+-\s+(.{6,})$/);
      if (addressMatch) {
        destinationAddress = addressMatch[1].trim();
      }

      // If destination is too short (like "in" from "check_in"), it's not a valid address
      // Try to get project address instead
      if (destinationAddress.length < 6 || destinationAddress === 'in' || destinationAddress === 'out') {
        // Try to find project address from the travel's project assignment
        const projectMatch = (travel.address || '').match(/^(.+?)\s+-\s+/);
        if (projectMatch) {
          const projectName = projectMatch[1].trim();
          const project = projects.find(p => p.name === projectName);
          if (project && project.address) {
            destinationAddress = project.address;
          }
        }
      }

      // Skip if we don't have both addresses
      if (!userAddress || !destinationAddress || destinationAddress.length < 6) {
        setMlPrediction({
          travel_id: travel.id,
          error: true,
          message: !userAddress ? 'User has no home address set' : 'No destination address'
        });
        setShowMlTooltip(true);
        setMlPredictionLoading(false);
        return;
      }

      // Use smart prediction endpoint that handles addresses and suggests transport type
      const response = await axios.post(`${API_BASE_URL}/api/ml/smart-predict/`, {
        origin: userAddress,
        destination: destinationAddress
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const predictionData = {
        travel_id: travel.id,
        userName: getUserName(travel),
        ...response.data
      };
      console.log('ML Prediction Response:', predictionData);
      setMlPrediction(predictionData);
      setShowMlTooltip(true);
    } catch (error) {
      logger.error('Error fetching ML prediction:', error);
      setMlPrediction({
        travel_id: travel.id,
        error: true,
        message: 'Failed to get prediction'
      });
      setShowMlTooltip(true);
    } finally {
      setMlPredictionLoading(false);
    }
  };

  // Debounced ML prediction fetch on hover - DISABLED FOR NOW
  // useEffect(() => {
  //   let timeoutId;
  //   if (hoveredTravelId) {
  //     const travel = allPeople.find(t => t.id === hoveredTravelId);
  //     if (travel) {
  //       timeoutId = setTimeout(() => {
  //         fetchMlPrediction(travel);
  //       }, 500); // 500ms delay before fetching
  //     }
  //   } else {
  //     setShowMlTooltip(false);
  //   }
  //
  //   return () => {
  //     if (timeoutId) clearTimeout(timeoutId);
  //   };
  // }, [hoveredTravelId, allPeople, usersData]);

  const togglePersonSelection = (travelId) => {
    setSelectedPeople(prev =>
      prev.includes(travelId)
        ? prev.filter(id => id !== travelId)
        : [...prev, travelId]
    );
  };

  const handleSelectAll = () => {
    // Only select/deselect filtered people from current tab
    const filteredIds = getFilteredPeople().map(p => p.id);
    const allFilteredSelected = filteredIds.every(id => selectedPeople.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered
      setSelectedPeople(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered (preserve any selected from other tabs)
      setSelectedPeople(prev => {
        const newSelection = [...prev];
        filteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  // Open assign modal and pre-fill with data from first selected person (if they have data)
  const openAssignModal = (peopleIds = selectedPeople) => {
    if (peopleIds.length === 0) return;

    // Find the first travel with existing data
    const firstTravel = allPeople.find(t => peopleIds.includes(t.id));

    if (firstTravel && firstTravel.transport_type) {
      // Pre-fill form with existing data
      setFormData(prev => ({
        ...prev,
        transportType: firstTravel.transport_type || 'car',
        selectedCar: firstTravel.car_id?.toString() || '',
        departureAirport: firstTravel.departure_airport || '',
        destinationAirport: firstTravel.arrival_airport || '',
        flightNumber: firstTravel.flight_number || '',
        departureTime: firstTravel.departure_time || '',
        arrivalTime: firstTravel.arrival_time || '',
        notes: firstTravel.description || '',
        organizedTransport: firstTravel.organized_transport || false,
        transportCompany: firstTravel.transport_company || '',
        meetingPoint: firstTravel.meeting_point || '',
        meetingTime: firstTravel.meeting_time || ''
      }));
    }

    setShowModal(true);
  };

  // Open assign modal for multiple projects sequentially
  const openAssignModalByProject = (travels) => {
    console.log('🚀 openAssignModalByProject called with', travels?.length, 'travels');
    console.log('📋 Assignments available:', assignments?.length);
    console.log('📋 First assignment structure:', assignments?.[0]);
    console.log('📋 First travel structure:', travels?.[0]);
    if (!travels || travels.length === 0) return;

    // Helper to get project from travel
    const getProjectForTravel = (travel) => {
      // Check direct project data from Travel model (preferred - set during travel creation)
      if (travel.project_data?.id) {
        return { id: travel.project_data.id, name: travel.project_data.name || 'Unknown' };
      }

      // Fallback: Check legacy properties (for older travels without project_data)
      if (travel.project_id) return { id: travel.project_id, name: travel.project_name || 'Unknown' };
      if (travel.project?.id) return { id: travel.project.id, name: travel.project.name || 'Unknown' };

      // Last resort fallback: try to detect from assignments (for travels created before project field was added)
      const travelDate = new Date(travel.start_date);
      const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
      const userAssignments = assignments.filter(a => a.user === travel.user_id);

      let matchingAssignment = null;

      if (travel.travel_type === 'check_in') {
        matchingAssignment = userAssignments.find(a => {
          const projectStart = new Date(a.start_date);
          const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
          const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));
          return daysBefore >= 0 && daysBefore <= 2;
        });
      } else if (travel.travel_type === 'check_out') {
        matchingAssignment = userAssignments.find(a => {
          const projectEnd = new Date(a.end_date);
          const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
          const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));
          return daysAfter >= 0 && daysAfter <= 2;
        });
      }

      if (matchingAssignment) {
        const projectId = matchingAssignment.project_id || matchingAssignment.project;
        const project = projects.find(p => p.id === projectId);
        return { id: projectId, name: project?.name || 'Unknown' };
      }

      return { id: 'unknown', name: 'Unknown Project' };
    };

    // Group travels by project
    const byProject = {};
    travels.forEach(t => {
      const proj = getProjectForTravel(t);
      console.log(`🔍 Travel: ${t.first_name} ${t.last_name} (user_id: ${t.user_id}, travel_type: ${t.travel_type}) => Project: ${proj.name} (${proj.id})`);
      if (!byProject[proj.id]) {
        byProject[proj.id] = { projectId: proj.id, projectName: proj.name, travels: [] };
      }
      byProject[proj.id].travels.push(t);
    });

    const projectGroups = Object.values(byProject);
    console.log('📊 Project groups:', projectGroups.map(g => `${g.projectName}: ${g.travels.length} people`));

    // If only one project, open directly
    if (projectGroups.length === 1) {
      const ids = projectGroups[0].travels.map(t => t.id);
      setSelectedPeople(ids);
      openAssignModal(ids);
      setShowAlertsPanel(false);
      return;
    }

    // Multiple projects - queue them
    // Start with the first project, queue the rest
    const [first, ...rest] = projectGroups;
    setProjectConfigQueue(rest);

    const ids = first.travels.map(t => t.id);
    setSelectedPeople(ids);
    openAssignModal(ids);
    setShowAlertsPanel(false);
  };

  // Process next project from queue (called after modal close/save)
  const processNextProjectInQueue = () => {
    if (projectConfigQueue.length === 0) return false;

    const [next, ...remaining] = projectConfigQueue;
    setProjectConfigQueue(remaining);

    const ids = next.travels.map(t => t.id);
    setSelectedPeople(ids);

    // Reset form data for new project
    setFormData(prev => ({
      ...prev,
      transportType: 'car',
      selectedCar: '',
      departureAirport: '',
      destinationAirport: '',
      flightNumber: '',
      departureTime: '',
      arrivalTime: '',
      notes: '',
      organizedTransport: false,
      transportCompany: '',
      meetingPoint: '',
      meetingTime: ''
    }));

    openAssignModal(ids);
    return true;
  };

  // Save planner note to API
  const handleSaveNote = async (travelId, note) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API_BASE_URL}/api/travel/${travelId}/`,
        { planner_note: note },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Update local state
      setAllPeople(prev => prev.map(t =>
        t.id === travelId ? { ...t, planner_note: note } : t
      ));
      setNoteModal({ show: false, travelId: null, note: '', isEditing: false });
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note');
    }
  };

  // Handle setting pickup driver
  const handleSetPickupDriver = async (passengerId, driverId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/travel/${passengerId}/set_pickup_driver/`,
        { driver_travel_id: driverId },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error setting pickup driver:', error);
      alert('Failed to set pickup driver');
    }
  };

  // Handle clearing pickup driver
  const handleClearPickupDriver = async (passengerId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/travel/${passengerId}/set_pickup_driver/`,
        { driver_travel_id: null },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error clearing pickup driver:', error);
      alert('Failed to clear pickup driver');
    }
  };

  const handleSaveAssignments = async (saveAsDraft = false) => {
    if (selectedPeople.length === 0) {
      alert('Please select at least one person');
      return;
    }

    // Car is optional now - can be used for plane transport too (company drop-off)

    try {
      const token = localStorage.getItem('access_token');
      const updatePromises = selectedPeople.map(travelId => {
        const travel = allPeople.find(t => t.id === travelId);
        const updateData = new FormData();

        updateData.append('transport_type', formData.transportType);

        if (formData.notes) {
          updateData.append('description', formData.notes);
        }

        // If car selected, add car reference (skip if "own_car")
        if (formData.selectedCar && formData.selectedCar !== 'own_car') {
          updateData.append('car_id', formData.selectedCar);
        }
        // For own car, we could add a flag or note - for now just don't set car_id
        // The transport_type will indicate they're traveling by car

        // Flight-specific fields
        if (formData.transportType === 'plane') {
          if (formData.departureAirport) {
            updateData.append('departure_airport', formData.departureAirport);
          }
          if (formData.destinationAirport) {
            updateData.append('arrival_airport', formData.destinationAirport);
          }
          if (formData.flightNumber) {
            updateData.append('flight_number', formData.flightNumber);
          }
          if (formData.departureTime) {
            updateData.append('departure_time', formData.departureTime);
          }
          if (formData.arrivalTime) {
            updateData.append('arrival_time', formData.arrivalTime);
          }
          // Add flight ticket if provided for this person (first file only - backend limitation)
          if (ticketFiles[travelId] && ticketFiles[travelId].length > 0) {
            updateData.append('attachment_flight', ticketFiles[travelId][0]);
          }
          // Organized transport fields
          updateData.append('organized_transport', formData.organizedTransport ? 'true' : 'false');
          if (formData.organizedTransport) {
            if (formData.transportCompany) {
              updateData.append('transport_company', formData.transportCompany);
            }
            if (formData.meetingPoint) {
              updateData.append('meeting_point', formData.meetingPoint);
            }
            if (formData.meetingTime) {
              updateData.append('meeting_time', formData.meetingTime);
            }
          }
        }

        // Car-specific fields (ferry tickets, driver)
        if (formData.transportType === 'car') {
          // Add ferry ticket if provided for this person (first file only - backend limitation)
          if (ticketFiles[travelId] && ticketFiles[travelId].length > 0) {
            updateData.append('attachment_ferry', ticketFiles[travelId][0]);
          }
          // Add driver if selected
          if (formData.driverId) {
            updateData.append('driver_id', formData.driverId);
          }
        }

        // If project selected, add project address to travel
        if (formData.projectId) {
          const project = projects.find(p => p.id === parseInt(formData.projectId));
          if (project) {
            updateData.append('address', `${project.name} - ${project.street_address || ''} ${project.city || ''} ${project.country || ''}`);
          }
        }

        // Set email_sent based on saveAsDraft
        if (saveAsDraft) {
          updateData.append('email_sent', 'false');
        } else {
          updateData.append('email_sent', 'true');
        }

        return axios.patch(`${API_BASE_URL}/api/travel/${travelId}/`, updateData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      });

      await Promise.all(updatePromises);

      // Log change to history
      const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
      const car = formData.selectedCar ? allCars.find(c => c.id === parseInt(formData.selectedCar)) : null;
      const project = formData.projectId ? projects.find(p => p.id === parseInt(formData.projectId)) : null;

      // Get current user info
      let currentUserName = 'Unknown';
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        if (userData.first_name || userData.last_name) {
          currentUserName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
        } else if (userData.username) {
          currentUserName = userData.username;
        } else if (userData.email) {
          currentUserName = userData.email;
        }
      } catch (e) {
        console.error('Error getting user data:', e);
      }

      const historyEntry = {
        timestamp: new Date(),
        action: saveAsDraft ? 'config_draft' : 'config_saved',
        peopleCount: selectedPeople.length,
        people: selectedTravels.map(t => getUserName(t)),
        car: car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : null,
        project: project?.name || null,
        transportType: formData.transportType,
        changedBy: currentUserName
      };
      setChangeHistory(prev => [historyEntry, ...prev].slice(0, 50)); // Keep last 50 entries

      // Log to backend (async, don't wait)
      const actionType = saveAsDraft ? 'config_draft' : 'config_saved';
      const description = `${saveAsDraft ? 'Saved draft' : 'Configured and sent'} ${selectedPeople.length} travel(s): ${selectedTravels.map(t => getUserName(t)).join(', ')}`;
      logActivity(actionType, description, {
        people: selectedTravels.map(t => getUserName(t)),
        car: car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : null,
        project: project?.name || null,
        transport_type: formData.transportType
      }, selectedPeople, project?.id || null);

      alert(`Successfully ${saveAsDraft ? 'saved as draft' : 'saved and sent'} ${selectedPeople.length} travel ${selectedPeople.length === 1 ? 'entry' : 'entries'}`);

      // Reset form and refresh data
      setSelectedPeople([]);
      setFormData({
        selectedCar: '',
        transportType: 'car',
        projectId: '',
        notes: '',
        departureTime: '',
        arrivalTime: '',
        flightNumber: '',
        departureAirport: '',
        destinationAirport: '',
        organizedTransport: false,
        transportCompany: '',
        meetingPoint: '',
        meetingTime: '',
        dropoffLocation: '',
        driverId: ''
      });
      setTicketFiles({});
      fetchData();

    } catch (error) {
      logger.error('Error saving travel assignments:', error);
      alert('Failed to save assignments. Please try again.');
    }
  };

  const handleMarkAsPlanned = async () => {
    if (selectedPeople.length === 0) {
      alert('Please select at least one person');
      return;
    }

    // Check if all selected people have valid draft status
    const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id));
    const allAreDrafts = selectedTravels.every(t => getTravelStatus(t) === 'draft');

    if (!allAreDrafts) {
      alert('Only draft travels can be marked as planned. Please ensure all selected items are drafts.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const updatePromises = selectedPeople.map(travelId => {
        const updateData = new FormData();
        updateData.append('email_sent', 'true');

        return axios.patch(`${API_BASE_URL}/api/travel/${travelId}/`, updateData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      });

      await Promise.all(updatePromises);

      alert(`Successfully marked ${selectedPeople.length} travel ${selectedPeople.length === 1 ? 'entry' : 'entries'} as planned`);

      // Reset form and refresh data
      setSelectedPeople([]);
      fetchData();

    } catch (error) {
      logger.error('Error marking as planned:', error);
      alert('Failed to mark as planned. Please try again.');
    }
  };

  const getUserName = (travel) => {
    // Backend now returns just the name string (without email)
    if (travel.user && typeof travel.user === 'string') {
      return travel.user;
    }

    // Fallback if user data is not available
    if (travel.user_id) {
      return `User ${travel.user_id}`;
    }

    return 'Unknown User';
  };

  // Format name as "Surname Firstname" for table display
  const getDisplayName = (travel) => {
    const name = getUserName(travel);
    if (!name || name === 'Unknown User' || name.startsWith('User ')) {
      return name;
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      // Move last part (surname) to front
      const surname = parts.pop();
      return `${surname} ${parts.join(' ')}`;
    }
    return name;
  };

  // Check if address is auto-generated (not real)
  const isAutoGeneratedAddress = (address) => {
    if (!address) return true;
    const autoPatterns = [
      'Travel check-in',
      'Travel check-out',
      'Travel Check-in',
      'Travel Check-out',
      'Airport to accommodation',
      'Accommodation to airport'
    ];
    return autoPatterns.some(pattern => address.includes(pattern));
  };

  // Get clean address (null if auto-generated)
  const getCleanAddress = (travel) => {
    if (!travel.address || isAutoGeneratedAddress(travel.address)) {
      return null;
    }
    return travel.address;
  };

  // Get travel status: 'pending', 'draft', 'sent'
  const getTravelStatus = (travel) => {
    const hasTransport = !!travel.transport_type;
    const hasValidAddress = !!getCleanAddress(travel);
    const isEmailSent = travel.email_sent === true;

    if (!hasTransport || !hasValidAddress) {
      return 'pending'; // Not configured yet
    }

    if (hasTransport && hasValidAddress && !isEmailSent) {
      return 'draft'; // Configured but not sent
    }

    return 'sent'; // Configured and sent
  };

  // Helper function to get project ID from travel
  const getTravelProjectId = (travel) => {
    // Try to extract project name from address first
    let projectName = '';
    if (travel.address && !isAutoGeneratedAddress(travel.address)) {
      const projectMatch = travel.address.match(/^(.+?)\s*-\s*/);
      if (projectMatch) {
        projectName = projectMatch[1].trim();
      }
    }

    // If found project name, find its ID
    if (projectName) {
      const project = projects.find(p => p.name === projectName);
      if (project) return project.id;
    }

    // If no project name from address, try to auto-detect based on dates
    if (!projectName && assignments.length > 0) {
      const userId = travel.user_id;
      const travelDate = new Date(travel.start_date);
      const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
      const travelType = travel.travel_type;

      const userAssignments = assignments.filter(a => {
        const aUserId = typeof a.user === 'object' ? a.user?.id : a.user;
        return aUserId === userId || a.user_id === userId;
      });

      let matchingAssignment = null;
      if (travelType === 'check_in') {
        matchingAssignment = userAssignments.find(assignment => {
          const projectStart = new Date(assignment.start_date);
          const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
          const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));
          return daysBefore >= 0 && daysBefore <= 2;
        });
      } else if (travelType === 'check_out') {
        matchingAssignment = userAssignments.find(assignment => {
          const projectEnd = new Date(assignment.end_date);
          const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
          const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));
          return daysAfter >= 0 && daysAfter <= 2;
        });
      }

      if (matchingAssignment) {
        return matchingAssignment.project_id || matchingAssignment.project;
      }
    }

    return null;
  };

  // Get filtered people by tab and date
  const getFilteredPeople = () => {
    let filtered = allPeople.filter(travel => travel.travel_type === activeTab);

    // Apply filterByDate from calendar/timeline click (takes priority)
    if (filterByDate) {
      const filterDateStr = filterByDate.toDateString();
      filtered = filtered.filter(travel => {
        const travelDate = new Date(travel.start_date);
        return travelDate.toDateString() === filterDateStr;
      });
    }
    // Apply date range filter if set
    else if (dateFilter.startDate || dateFilter.endDate) {
      filtered = filtered.filter(travel => {
        const travelDate = new Date(travel.start_date);
        travelDate.setHours(0, 0, 0, 0);

        let matchesStart = true;
        let matchesEnd = true;

        if (dateFilter.startDate) {
          const startDate = new Date(dateFilter.startDate);
          startDate.setHours(0, 0, 0, 0);
          matchesStart = travelDate >= startDate;
        }

        if (dateFilter.endDate) {
          const endDate = new Date(dateFilter.endDate);
          endDate.setHours(0, 0, 0, 0);
          matchesEnd = travelDate <= endDate;
        }

        return matchesStart && matchesEnd;
      });
    }

    // Apply project filter if set
    if (projectFilter) {
      filtered = filtered.filter(travel => {
        const travelProjectId = getTravelProjectId(travel);
        return travelProjectId && travelProjectId.toString() === projectFilter;
      });
    }

    // Apply person name search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(travel => {
        const name = getUserName(travel).toLowerCase();
        return name.includes(query);
      });
    }

    // Apply status filter (multiple selection)
    if (filterByStatus.length > 0) {
      filtered = filtered.filter(travel => {
        const status = getTravelStatus(travel);
        return filterByStatus.includes(status);
      });
    }

    return filtered;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPeople = () => {
    const filteredPeople = getFilteredPeople();
    const sorted = [...filteredPeople].sort((a, b) => {
      // First: push 'sent' status items to the bottom
      const aStatus = getTravelStatus(a);
      const bStatus = getTravelStatus(b);
      const aIsSent = aStatus === 'sent';
      const bIsSent = bStatus === 'sent';

      if (aIsSent && !bIsSent) return 1;  // a goes to bottom
      if (!aIsSent && bIsSent) return -1; // b goes to bottom

      // Then apply normal sorting within each group
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'start_date':
          aValue = new Date(a.start_date);
          bValue = new Date(b.start_date);
          break;
        case 'person':
          aValue = getUserName(a).toLowerCase();
          bValue = getUserName(b).toLowerCase();
          break;
        case 'type':
          aValue = a.travel_type || '';
          bValue = b.travel_type || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-4 w-4 text-brand-600" />
      : <ArrowDown className="h-4 w-4 text-brand-600" />;
  };

  // ============ SUMMARY STATISTICS ============
  const summaryStats = useMemo(() => {
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay() + 1); // Monday
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const thisWeekTravels = allPeople.filter(t => {
      const travelDate = new Date(t.start_date);
      return travelDate >= weekStart && travelDate <= weekEnd;
    });

    const checkIns = thisWeekTravels.filter(t => t.travel_type === 'check_in');
    const checkOuts = thisWeekTravels.filter(t => t.travel_type === 'check_out');

    const pending = allPeople.filter(t => getTravelStatus(t) === 'pending').length;
    const drafts = allPeople.filter(t => getTravelStatus(t) === 'draft').length;
    const sent = allPeople.filter(t => getTravelStatus(t) === 'sent').length;

    // Find next upcoming travel
    const upcomingTravels = allPeople
      .filter(t => new Date(t.start_date) >= now)
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    // Cars needed per day (next 7 days)
    const carsNeededByDay = {};
    for (let i = 0; i < 7; i++) {
      const date = new Date(now);
      date.setDate(now.getDate() + i);
      const dateStr = date.toDateString();
      const dayTravels = allPeople.filter(t => new Date(t.start_date).toDateString() === dateStr);
      const pendingCount = dayTravels.filter(t => getTravelStatus(t) === 'pending').length;
      if (pendingCount > 0) {
        carsNeededByDay[dateStr] = Math.ceil(pendingCount / 4); // Assume 4 people per car
      }
    }

    return {
      thisWeekCheckIns: checkIns.length,
      thisWeekCheckOuts: checkOuts.length,
      thisWeekTotal: thisWeekTravels.length,
      pending,
      drafts,
      sent,
      totalPeople: allPeople.length,
      nextTravel: upcomingTravels[0] || null,
      carsNeededByDay,
      withoutTransport: pending
    };
  }, [allPeople]);

  // ============ TRAVEL GROUPS (same day + same project) ============
  const travelGroups = useMemo(() => {
    const groups = {};

    allPeople.forEach(travel => {
      const dateStr = new Date(travel.start_date).toDateString();
      const projectId = getTravelProjectId(travel);
      const groupKey = `${dateStr}-${projectId || 'no-project'}-${travel.travel_type}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          date: new Date(travel.start_date),
          projectId,
          projectName: projectId ? projects.find(p => p.id === projectId)?.name : null,
          travelType: travel.travel_type,
          travels: []
        };
      }
      groups[groupKey].travels.push(travel);
    });

    // Only return groups with more than 1 person
    return Object.values(groups).filter(g => g.travels.length > 1);
  }, [allPeople, projects, assignments]);

  // ============ SMART SUGGESTIONS ============
  const smartSuggestions = useMemo(() => {
    const suggestions = [];

    // Find people going to same project on same day without assigned car
    travelGroups.forEach(group => {
      const unassigned = group.travels.filter(t => !t.car_id && getTravelStatus(t) === 'pending');
      if (unassigned.length >= 2) {
        // Group by travel type
        const checkIns = unassigned.filter(t => t.travel_type === 'check_in');
        const checkOuts = unassigned.filter(t => t.travel_type === 'check_out');

        let travelType = 'mixed';
        if (checkIns.length > 0 && checkOuts.length === 0) {
          travelType = 'check_in';
        } else if (checkOuts.length > 0 && checkIns.length === 0) {
          travelType = 'check_out';
        }

        suggestions.push({
          type: 'group_travel',
          priority: 'high',
          date: group.date,
          peopleCount: unassigned.length,
          projectName: group.projectName || 'Unknown',
          travelType: travelType,
          checkInCount: checkIns.length,
          checkOutCount: checkOuts.length,
          action: 'Assign to same car',
          travels: unassigned
        });
      }
    });

    // Find tomorrow's unassigned travels
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toDateString();
    const tomorrowUnassigned = allPeople.filter(t =>
      new Date(t.start_date).toDateString() === tomorrowStr &&
      getTravelStatus(t) === 'pending'
    );
    if (tomorrowUnassigned.length > 0) {
      const checkIns = tomorrowUnassigned.filter(t => t.travel_type === 'check_in');
      const checkOuts = tomorrowUnassigned.filter(t => t.travel_type === 'check_out');

      let travelType = 'mixed';
      if (checkIns.length > 0 && checkOuts.length === 0) {
        travelType = 'check_in';
      } else if (checkOuts.length > 0 && checkIns.length === 0) {
        travelType = 'check_out';
      }

      suggestions.push({
        type: 'urgent',
        priority: 'critical',
        date: tomorrow,
        peopleCount: tomorrowUnassigned.length,
        projectName: 'Tomorrow',
        travelType: travelType,
        checkInCount: checkIns.length,
        checkOutCount: checkOuts.length,
        action: 'Assign now',
        travels: tomorrowUnassigned
      });
    }

    // Sort by date (closest first)
    suggestions.sort((a, b) => a.date - b.date);

    return suggestions.slice(0, 5); // Limit to 5 suggestions
  }, [allPeople, travelGroups]);

  // ============ ENHANCED STATISTICS ============
  const enhancedStats = useMemo(() => {
    const now = new Date();
    const stats = {
      todayTravels: 0,
      tomorrowTravels: 0,
      urgentUnassigned: 0,
      carsInUseToday: new Set(),
      totalCarsNeeded: 0
    };

    const today = now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toDateString();

    allPeople.forEach(travel => {
      const travelDate = new Date(travel.start_date).toDateString();

      if (travelDate === today) {
        stats.todayTravels++;
        if (travel.car_id) stats.carsInUseToday.add(travel.car_id);
      }
      if (travelDate === tomorrowStr) {
        stats.tomorrowTravels++;
        if (getTravelStatus(travel) === 'pending') {
          stats.urgentUnassigned++;
        }
      }
    });

    stats.totalCarsNeeded = Math.ceil(stats.todayTravels / 4);

    return stats;
  }, [allPeople]);

  // ============ CONFLICT DETECTION ============
  const conflicts = useMemo(() => {
    const conflictList = [];
    const travelsByDate = {};
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Group travels by date and user
    allPeople.forEach(travel => {
      const dateKey = new Date(travel.start_date).toDateString();
      const userKey = `${dateKey}-${travel.user_id}`;

      if (!travelsByDate[userKey]) {
        travelsByDate[userKey] = [];
      }
      travelsByDate[userKey].push(travel);
    });

    // Find conflicts (same user, same day, multiple travels) - only within 14 days
    Object.entries(travelsByDate).forEach(([key, travels]) => {
      if (travels.length > 1) {
        const travelDate = new Date(travels[0].start_date);
        const daysUntil = Math.ceil((travelDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntil >= 0 && daysUntil <= 14) {
          conflictList.push({
            type: 'duplicate',
            message: `${getUserName(travels[0])} has ${travels.length} travels on ${new Date(travels[0].start_date).toLocaleDateString()}`,
            travels,
            daysUntil
          });
        }
      }
    });

    // Check for unconfigured travels within 14 days
    const unconfiguredTravels = allPeople.filter(t => {
      const status = getTravelStatus(t);
      const travelDate = new Date(t.start_date);
      const daysUntil = Math.ceil((travelDate - now) / (1000 * 60 * 60 * 24));
      return status === 'pending' && daysUntil >= 0 && daysUntil <= 14;
    });

    unconfiguredTravels.forEach(travel => {
      const travelDate = new Date(travel.start_date);
      const daysUntil = Math.ceil((travelDate - now) / (1000 * 60 * 60 * 24));
      conflictList.push({
        type: daysUntil <= 7 ? 'urgent' : 'warning',
        message: `${getUserName(travel)} travel on ${travelDate.toLocaleDateString()} needs configuration`,
        travels: [travel],
        daysUntil
      });
    });

    // Sort by urgency (closest dates first)
    conflictList.sort((a, b) => (a.daysUntil || 0) - (b.daysUntil || 0));

    return conflictList;
  }, [allPeople]);

  // ============ SMART ALERTS ANALYZER ============
  const smartAlerts = useMemo(() => {
    const alerts = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const today = now.toDateString();

    // Helper to calculate days until travel
    const getDaysUntil = (date) => {
      const travelDate = new Date(date);
      travelDate.setHours(0, 0, 0, 0);
      return Math.ceil((travelDate - now) / (1000 * 60 * 60 * 24));
    };

    // Group travels by date
    const travelsByDate = {};
    allPeople.forEach(travel => {
      const dateKey = new Date(travel.start_date).toDateString();
      if (!travelsByDate[dateKey]) {
        travelsByDate[dateKey] = { checkIn: [], checkOut: [] };
      }
      if (travel.travel_type === 'check_in') {
        travelsByDate[dateKey].checkIn.push(travel);
      } else {
        travelsByDate[dateKey].checkOut.push(travel);
      }
    });

    // 1. TODAY'S TRAVELS - highest priority
    const todaysTravels = travelsByDate[today];
    if (todaysTravels) {
      if (todaysTravels.checkIn.length > 0) {
        // Check for missing tickets (planes without flight number or ticket file)
        const missingTicketsCheckIn = todaysTravels.checkIn.filter(t =>
          t.transport_type === 'plane' && !t.flight_number && !t.attachment_flight
        );
        // Check for unconfigured (pending status)
        const pendingCheckIn = todaysTravels.checkIn.filter(t => getTravelStatus(t) === 'pending');
        // Check for drafts (configured but not sent)
        const draftsCheckIn = todaysTravels.checkIn.filter(t => getTravelStatus(t) === 'draft');
        // Check if all are sent
        const allSentCheckIn = todaysTravels.checkIn.every(t => getTravelStatus(t) === 'sent');

        const hasIssues = missingTicketsCheckIn.length > 0 || pendingCheckIn.length > 0 || draftsCheckIn.length > 0;

        alerts.push({
          id: 'today-checkin',
          type: allSentCheckIn ? 'success' : hasIssues ? 'critical' : 'info',
          icon: allSentCheckIn ? '✅' : hasIssues ? '🔴' : '🟢',
          title: `Today: ${todaysTravels.checkIn.length} check-in${todaysTravels.checkIn.length > 1 ? 's' : ''}`,
          description: todaysTravels.checkIn.map(t => getUserName(t)).join(', '),
          issues: allSentCheckIn ? ['All configured and sent'] : [
            ...(missingTicketsCheckIn.length > 0 ? [`✈️ ${missingTicketsCheckIn.length} missing flight tickets`] : []),
            ...(pendingCheckIn.length > 0 ? [`📋 ${pendingCheckIn.length} not configured`] : []),
            ...(draftsCheckIn.length > 0 ? [`📝 ${draftsCheckIn.length} draft (not sent)`] : [])
          ],
          travels: todaysTravels.checkIn,
          daysUntil: 0
        });
      }
      if (todaysTravels.checkOut.length > 0) {
        // Check for missing tickets (planes without flight number or ticket file)
        const missingTicketsCheckOut = todaysTravels.checkOut.filter(t =>
          t.transport_type === 'plane' && !t.flight_number && !t.attachment_flight
        );
        // Check for unconfigured (pending status)
        const pendingCheckOut = todaysTravels.checkOut.filter(t => getTravelStatus(t) === 'pending');
        // Check for drafts (configured but not sent)
        const draftsCheckOut = todaysTravels.checkOut.filter(t => getTravelStatus(t) === 'draft');
        // Check if all are sent
        const allSentCheckOut = todaysTravels.checkOut.every(t => getTravelStatus(t) === 'sent');

        const hasIssues = missingTicketsCheckOut.length > 0 || pendingCheckOut.length > 0 || draftsCheckOut.length > 0;

        alerts.push({
          id: 'today-checkout',
          type: allSentCheckOut ? 'success' : hasIssues ? 'critical' : 'info',
          icon: allSentCheckOut ? '✅' : hasIssues ? '🔴' : '🟢',
          title: `Today: ${todaysTravels.checkOut.length} check-out${todaysTravels.checkOut.length > 1 ? 's' : ''}`,
          issues: allSentCheckOut ? ['All configured and sent'] : [
            ...(missingTicketsCheckOut.length > 0 ? [`✈️ ${missingTicketsCheckOut.length} missing flight tickets`] : []),
            ...(pendingCheckOut.length > 0 ? [`📋 ${pendingCheckOut.length} not configured`] : []),
            ...(draftsCheckOut.length > 0 ? [`📝 ${draftsCheckOut.length} draft (not sent)`] : [])
          ],
          description: todaysTravels.checkOut.map(t => getUserName(t)).join(', '),
          actions: ['Confirm return flights', 'Arrange pickup'],
          travels: todaysTravels.checkOut,
          daysUntil: 0
        });
      }
    }

    // 2. UPCOMING 7 DAYS - group by day
    for (let i = 1; i <= 7; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(checkDate.getDate() + i);
      const dateKey = checkDate.toDateString();
      const dayTravels = travelsByDate[dateKey];

      if (dayTravels) {
        const total = dayTravels.checkIn.length + dayTravels.checkOut.length;
        if (total > 0) {
          const dayName = i === 1 ? 'Tomorrow' : checkDate.toLocaleDateString('en-US', { weekday: 'long' });
          const allTravels = [...dayTravels.checkIn, ...dayTravels.checkOut];

          // Check which travels are completely unconfigured (nothing set at all)
          const unconfiguredTravels = allTravels.filter(t => {
            const hasTransportDetails = t.flight_number || t.attachment_flight || t.departure_time || t.arrival_time;
            return !hasTransportDetails && getTravelStatus(t) === 'pending';
          });

          // Check for configured travels that are just missing tickets
          const configuredButMissingTickets = allTravels.filter(t => {
            const hasTransportDetails = t.flight_number || t.attachment_flight || t.departure_time || t.arrival_time;
            return hasTransportDetails && t.transport_type === 'plane' && !t.flight_number && !t.attachment_flight;
          });

          // Check for missing accommodation (only for configured check-ins)
          const missingAccommodation = dayTravels.checkIn.filter(t => {
            const hasTransportDetails = t.flight_number || t.attachment_flight || t.departure_time || t.arrival_time;
            if (!hasTransportDetails) return false; // Skip unconfigured
            const hasAccommodation = accommodationAssignments.some(a =>
              a.user_id === t.user_id &&
              new Date(a.start_date) <= new Date(t.start_date) &&
              new Date(a.end_date) >= new Date(t.start_date)
            );
            return !hasAccommodation;
          });

          // Build issues list with names and labels
          const issues = [];
          if (unconfiguredTravels.length > 0) {
            issues.push({ type: 'unconfigured', label: 'Travel not planned', travels: unconfiguredTravels });
          }
          if (configuredButMissingTickets.length > 0) {
            issues.push({ type: 'missing_tickets', label: 'Missing tickets', travels: configuredButMissingTickets });
          }
          if (missingAccommodation.length > 0) {
            issues.push({ type: 'missing_accommodation', label: 'No accommodation', travels: missingAccommodation });
          }

          alerts.push({
            id: `day-${i}`,
            type: i <= 2 ? 'urgent' : 'warning',
            icon: i <= 2 ? '🟠' : '🟡',
            title: `${dayName}: ${total} travel${total > 1 ? 's' : ''} (${dayTravels.checkIn.length} in / ${dayTravels.checkOut.length} out)`,
            description: allTravels.slice(0, 3).map(t => getUserName(t)).join(', ') + (total > 3 ? ` +${total - 3} more` : ''),
            issues,
            travels: allTravels,
            daysUntil: i
          });
        }
      }
    }

    // 3. MISSING FLIGHT TICKETS (within 14 days)
    const missingTickets = allPeople.filter(t => {
      const daysUntil = getDaysUntil(t.start_date);
      return daysUntil >= 0 && daysUntil <= 14 &&
        t.transport_type === 'plane' &&
        !t.flight_number && !t.attachment_flight;
    });
    if (missingTickets.length > 0) {
      alerts.push({
        id: 'missing-tickets',
        type: 'warning',
        icon: '✈️',
        title: `${missingTickets.length} flight${missingTickets.length > 1 ? 's' : ''} without tickets`,
        description: 'Upload flight tickets or add flight numbers',
        travels: missingTickets,
        expandable: true
      });
    }

    // 4. MISSING ACCOMMODATION (check-ins within 14 days)
    const checkInsWithoutAccommodation = allPeople.filter(t => {
      if (t.travel_type !== 'check_in') return false;
      const daysUntil = getDaysUntil(t.start_date);
      if (daysUntil < 0 || daysUntil > 14) return false;

      const hasAccommodation = accommodationAssignments.some(a =>
        a.user_id === t.user_id &&
        new Date(a.start_date) <= new Date(t.start_date) &&
        new Date(a.end_date) >= new Date(t.start_date)
      );
      return !hasAccommodation;
    });
    if (checkInsWithoutAccommodation.length > 0) {
      alerts.push({
        id: 'missing-accommodation',
        type: 'warning',
        icon: '🏨',
        title: `${checkInsWithoutAccommodation.length} check-in${checkInsWithoutAccommodation.length > 1 ? 's' : ''} need accommodation`,
        description: 'Assign rooms before arrival',
        travels: checkInsWithoutAccommodation,
        expandable: true
      });
    }

    // 5. UNCONFIGURED TRAVELS (pending status within 14 days)
    const pendingTravels = allPeople.filter(t => {
      const daysUntil = getDaysUntil(t.start_date);
      return daysUntil >= 0 && daysUntil <= 14 && getTravelStatus(t) === 'pending';
    });
    if (pendingTravels.length > 0) {
      alerts.push({
        id: 'pending-config',
        type: 'info',
        icon: '📋',
        title: `${pendingTravels.length} travel${pendingTravels.length > 1 ? 's' : ''} need configuration`,
        description: 'Set transport type and details',
        travels: pendingTravels,
        expandable: true
      });
    }

    // 6. SUMMARY STATS FOR NEXT 30 DAYS
    const next30Days = allPeople.filter(t => {
      const daysUntil = getDaysUntil(t.start_date);
      return daysUntil >= 0 && daysUntil <= 30;
    });
    const planeCount = next30Days.filter(t => t.transport_type === 'plane').length;
    const carCount = next30Days.filter(t => t.transport_type === 'car').length;

    if (next30Days.length > 0) {
      alerts.push({
        id: 'monthly-summary',
        type: 'info',
        icon: '📊',
        title: `Next 30 days: ${next30Days.length} travels`,
        description: `✈️ ${planeCount} flights, 🚗 ${carCount} car trips`,
        isStats: true
      });
    }

    return alerts;
  }, [allPeople, accommodationAssignments]);

  // ============ CAR CAPACITY HELPER ============
  // Calculate how many people are assigned to each car on each date
  const getCarCapacity = (carId, travelDate) => {
    if (!carId) return null;

    const dateStr = new Date(travelDate).toDateString();
    const car = allCars.find(c => c.id === carId);
    const maxSeats = car?.number_of_seats || 5;

    // Count how many people are in this car on this date
    const peopleInCar = allPeople.filter(t =>
      t.car_id === carId &&
      new Date(t.start_date).toDateString() === dateStr
    ).length;

    return { used: peopleInCar, max: maxSeats, isFull: peopleInCar >= maxSeats };
  };

  // ============ DAILY SUMMARY HELPER ============
  const getDailySummary = (date) => {
    if (!date) return null;

    const dateStr = date.toDateString();
    const dayTravels = allPeople.filter(t => new Date(t.start_date).toDateString() === dateStr);

    const checkIns = dayTravels.filter(t => t.travel_type === 'check_in');
    const checkOuts = dayTravels.filter(t => t.travel_type === 'check_out');

    const pending = dayTravels.filter(t => getTravelStatus(t) === 'pending');
    const drafts = dayTravels.filter(t => getTravelStatus(t) === 'draft');
    const sent = dayTravels.filter(t => getTravelStatus(t) === 'sent');

    // Cars used on this day
    const carsUsed = new Set(dayTravels.filter(t => t.car_id).map(t => t.car_id));
    const carsNeeded = Math.ceil(pending.length / 4);

    // Group by project
    const byProject = {};
    dayTravels.forEach(t => {
      const projectId = getTravelProjectId(t);
      const projectName = projectId ? projects.find(p => p.id === projectId)?.name : 'No Project';
      if (!byProject[projectName || 'Unknown']) {
        byProject[projectName || 'Unknown'] = [];
      }
      byProject[projectName || 'Unknown'].push(t);
    });

    return {
      date,
      total: dayTravels.length,
      checkIns: checkIns.length,
      checkOuts: checkOuts.length,
      pending: pending.length,
      drafts: drafts.length,
      sent: sent.length,
      carsUsed: carsUsed.size,
      carsNeeded,
      byProject,
      travels: dayTravels
    };
  };

  // ============ DAILY SUMMARY PANEL COMPONENT ============
  const DailySummaryPanel = () => {
    const summary = summaryDate ? getDailySummary(summaryDate) : null;

    if (!summary) return null;

    return (
      <div className="bg-white rounded-xl shadow border border-steel-200 p-3 mb-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-brand-600" />
            {summary.date.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h3>
          <button
            onClick={() => setSummaryDate(null)}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {summary.total === 0 ? (
          <p className="text-sm text-slate-500 text-center py-2">No travels on this day</p>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              <div className="bg-yellow-50 rounded p-2 text-center">
                <div className="text-lg font-bold text-yellow-700">{summary.checkIns}</div>
                <div className="text-xs text-yellow-600">Check-in</div>
              </div>
              <div className="bg-orange-50 rounded p-2 text-center">
                <div className="text-lg font-bold text-orange-700">{summary.checkOuts}</div>
                <div className="text-xs text-orange-600">Check-out</div>
              </div>
              <div className="bg-brand-50 rounded p-2 text-center">
                <div className="text-lg font-bold text-brand-700">{summary.carsUsed}</div>
                <div className="text-xs text-brand-600">Cars</div>
              </div>
              <div className={`rounded p-2 text-center ${summary.pending > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <div className={`text-lg font-bold ${summary.pending > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  {summary.pending}
                </div>
                <div className={`text-xs ${summary.pending > 0 ? 'text-red-600' : 'text-green-600'}`}>Pending</div>
              </div>
            </div>

            {/* By Project */}
            <div className="border-t border-steel-100 pt-2">
              <div className="text-xs font-medium text-slate-500 mb-1">By Project:</div>
              <div className="space-y-1">
                {Object.entries(summary.byProject).map(([projectName, travels]) => (
                  <div key={projectName} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 truncate">{projectName}</span>
                    <span className="text-slate-500">{travels.length} people</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="border-t border-steel-100 pt-2 mt-2 flex gap-2">
              <button
                onClick={() => {
                  setFilterByDate(summary.date);
                  setViewMode('table');
                }}
                className="flex-1 text-xs px-2 py-1.5 bg-steel-100 text-slate-700 rounded hover:bg-steel-200"
              >
                View in Table
              </button>
              {summary.pending > 0 && (
                <button
                  onClick={() => {
                    const pendingIds = summary.travels
                      .filter(t => getTravelStatus(t) === 'pending')
                      .map(t => t.id);
                    setSelectedPeople(pendingIds);
                    openAssignModal(pendingIds);
                  }}
                  className="flex-1 text-xs px-2 py-1.5 bg-brand-500 text-white rounded hover:bg-brand-600"
                >
                  Assign {summary.pending} Pending
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // ============ ACCOMMODATION HELPER ============
  const getUserAccommodation = (userId, travelDate) => {
    // Find room assignment for user that covers the travel date
    const dateStr = travelDate instanceof Date
      ? travelDate.toISOString().split('T')[0]
      : travelDate;

    const assignment = accommodationAssignments.find(a => {
      const assignmentUserId = typeof a.user === 'object' ? a.user.id : a.user;
      if (assignmentUserId !== userId) return false;

      const startDate = a.start_date;
      const endDate = a.end_date;
      return dateStr >= startDate && dateStr <= endDate;
    });

    if (assignment) {
      return {
        roomNumber: assignment.room_number,
        accommodationName: assignment.accommodation_name,
        accommodationId: assignment.accommodation_id,
        startDate: assignment.start_date,
        endDate: assignment.end_date,
        address: assignment.accommodation_address,
        city: assignment.accommodation_city,
        phone: assignment.accommodation_phone,
        email: assignment.accommodation_email
      };
    }
    return null;
  };

  // Helper to find airport pickup transport for a user on a specific date
  const getAirportPickupTransport = (userId, travelDate) => {
    // Normalize date to YYYY-MM-DD format
    let dateStr;
    if (travelDate instanceof Date) {
      dateStr = travelDate.toISOString().split('T')[0];
    } else if (typeof travelDate === 'string') {
      // Handle various date formats: "2025-12-08", "2025-12-08T08:00:00+0100", etc.
      dateStr = travelDate.split('T')[0];
    } else {
      dateStr = travelDate;
    }

    logger.info('getAirportPickupTransport called:', { userId, dateStr, localTransportsCount: localTransports.length });

    // Find airport_pickup transport where this user is a passenger
    const found = localTransports.find(transport => {
      if (transport.transport_type !== 'airport_pickup') return false;
      if (transport.transport_date !== dateStr) return false;

      // Check if user is in passengers
      const passengerIds = transport.passengers || [];
      const passengersData = transport.passengers_data || [];
      const isPassenger = passengerIds.includes(userId) ||
                          passengersData.some(p => p.id === userId || p.user_id === userId);

      // Also check if user is the driver
      const isDriver = transport.driver === userId;

      logger.info('Checking transport:', {
        transportId: transport.id,
        transportDate: transport.transport_date,
        passengerIds,
        passengersData: passengersData.map(p => ({ id: p.id, user_id: p.user_id })),
        driver: transport.driver,
        isPassenger,
        isDriver
      });

      return isPassenger || isDriver;
    });

    logger.info('getAirportPickupTransport result:', found);
    return found;
  };

  // ============ EMAIL PREVIEW GENERATOR ============
  const generateEmailPreview = (travel) => {
    const car = travel.car_id ? allCars.find(c => c.id === travel.car_id) : null;
    const project = formData.projectId ? projects.find(p => p.id === parseInt(formData.projectId)) : null;
    const travelDate = new Date(travel.start_date).toLocaleDateString('pl-PL', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    const isCheckIn = travel.travel_type === 'check_in';

    // Get accommodation info for this user
    const accommodation = getUserAccommodation(travel.user_id, travel.start_date);

    // Get airport details
    const depAirport = formData.departureAirport ? AIRPORTS[formData.departureAirport] : null;
    const destAirport = formData.destinationAirport ? AIRPORTS[formData.destinationAirport] : null;

    // Build organized transport section
    let organizedTransportSection = '';
    if (formData.transportType === 'plane' && formData.organizedTransport) {
      organizedTransportSection = `
🚐 ORGANIZED TRANSPORT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Company: ${formData.transportCompany || 'TBD'}
   📍 Pickup: ${formData.meetingPoint || 'At airport'}
   🏁 Dropoff: ${formData.dropoffLocation || 'TBD'}
   ⏰ Meeting Time: ${formData.meetingTime || 'TBD'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
    }

    // Check if there's a LocalTransport (airport pickup) assigned for this user - independent of transport type
    const airportPickup = isCheckIn ? getAirportPickupTransport(travel.user_id, travel.start_date) : null;

    // Build airport pickup section (shown for check-in if LocalTransport exists)
    let airportPickupSection = '';
    if (isCheckIn && airportPickup && airportPickup.car_data) {
      const pickupCar = airportPickup.car_data;
      const driverInfo = airportPickup.driver_data;
      airportPickupSection = `
🚐 AIRPORT PICKUP:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   🚗 Car: ${pickupCar.car_brand} ${pickupCar.car_model}
   📋 Plate: ${pickupCar.plate_number}
${driverInfo ? `   👤 Driver: ${driverInfo.first_name} ${driverInfo.last_name}` : ''}
${airportPickup.scheduled_time ? `   ⏰ Pickup time: ${airportPickup.scheduled_time}` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
    }

    // Build flight route section
    let flightSection = '';
    if (formData.transportType === 'plane') {
      // Info about transport after landing (only if no LocalTransport assigned yet)
      let transportAfterLandingInfo = '';
      if (!formData.organizedTransport && !airportPickup) {
        transportAfterLandingInfo = `
🚐 TRANSPORT AFTER LANDING:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   ℹ️ The Administrator will send you details about
   transport from the airport separately.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      }

      // Different route visualization for check_in vs check_out
      if (isCheckIn) {
        flightSection = `
FLIGHT ROUTE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🏠 Your Home
   ↓ (drive to airport)
🛫 DEPARTURE: ${formData.departureAirport || 'TBD'} - ${depAirport ? `${depAirport.name}, ${depAirport.city}` : 'Airport not selected'}
   ↓ (flight${formData.flightNumber ? ` ${formData.flightNumber}` : ''})
🛬 ARRIVAL: ${formData.destinationAirport || 'TBD'} - ${destAirport ? `${destAirport.name}, ${destAirport.city}` : 'Airport not selected'}
${formData.organizedTransport && formData.meetingPoint ? `   ↓ (pickup at: ${formData.meetingPoint})
📍 PICKUP POINT` : ''}
${formData.organizedTransport && formData.dropoffLocation ? `   ↓ (transfer)
🏁 DROPOFF: ${formData.dropoffLocation}` : `   ↓ (transfer to project)
🏗️ PROJECT: ${project ? project.name : 'TBD'}`}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${organizedTransportSection}${airportPickupSection}${transportAfterLandingInfo}`;
      } else {
        // check_out - return journey
        flightSection = `
RETURN JOURNEY:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🏗️ PROJECT: ${project ? project.name : 'Current location'}
   ↓ (transfer to airport)
🛫 DEPARTURE: ${formData.departureAirport || 'TBD'} - ${depAirport ? `${depAirport.name}, ${depAirport.city}` : 'Airport not selected'}
   ↓ (flight${formData.flightNumber ? ` ${formData.flightNumber}` : ''})
🛬 ARRIVAL: ${formData.destinationAirport || 'TBD'} - ${destAirport ? `${destAirport.name}, ${destAirport.city}` : 'Airport not selected'}
${formData.organizedTransport && formData.meetingPoint ? `   ↓ (pickup at: ${formData.meetingPoint})
📍 PICKUP POINT` : ''}
${formData.organizedTransport && formData.dropoffLocation ? `   ↓ (transfer)
🏁 DROPOFF: ${formData.dropoffLocation}` : `   ↓ (drive home)
🏠 Your Home`}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${organizedTransportSection}`;
      }
    }

    // Build car section with pickup info
    let carSection = '';
    if (formData.transportType === 'car') {
      // Check pickup relationships
      const isPassenger = !!travel.pickup_driver_id;
      const hasPassengers = travel.passengers_data && travel.passengers_data.length > 0;
      const driverData = travel.pickup_driver_data;
      const passengersData = travel.passengers_data || [];

      if (car) {
        carSection = `
TRANSPORT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🚗 Company Car: ${car.car_brand} ${car.car_model}
   Plate: ${car.plate_number}
${formData.driverId ? `   Driver assigned` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      } else if (formData.selectedCar === 'own_car') {
        carSection = `
TRANSPORT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🚙 Own Car (private vehicle)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      }

      // Add pickup route info
      if (isPassenger && driverData) {
        const driverPhone = driverData.phone_number || usersData[driverData.user_id]?.phone_number;
        carSection += `
🚕 PICKUP ARRANGEMENT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   You will be picked up by: ${driverData.user_name}
   Driver phone: ${driverPhone || 'N/A'}
   From your address: ${travel.user?.personal_address || usersData[travel.user_id]?.personal_address || 'Your home address'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      } else if (hasPassengers && passengersData.length > 0) {
        const pickupList = passengersData.map(p => {
          const passengerPhone = p.phone_number || usersData[p.user_id]?.phone_number;
          return `   • ${p.user_name}
      📍 ${p.address || 'address on file'}
      📞 ${passengerPhone || 'N/A'}`;
        }).join('\n');
        carSection += `
🚕 PICKUP ROUTE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   You will pick up the following colleagues:
${pickupList}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      } else if (!isPassenger && !hasPassengers) {
        carSection += `
   ℹ️ You are traveling independently to the project.
`;
      }
    }

    // Build accommodation section
    let accommodationSection = '';
    if (isCheckIn) {
      if (accommodation) {
        accommodationSection = `
🏨 ACCOMMODATION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   ${accommodation.accommodationName}
   Room: ${accommodation.roomNumber}
   ${accommodation.address ? `Address: ${accommodation.address}${accommodation.city ? `, ${accommodation.city}` : ''}` : ''}
   ${accommodation.phone ? `Phone: ${accommodation.phone}` : ''}
   Period: ${accommodation.startDate} - ${accommodation.endDate}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      } else {
        accommodationSection = `
🏨 ACCOMMODATION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   ⚠️ No accommodation assigned yet.
   Please contact the Administrator to arrange your accommodation.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;
      }
    }

    return {
      subject: `Travel Confirmation - ${isCheckIn ? 'Check-in' : 'Check-out'} ${travelDate}${formData.transportType === 'plane' && formData.departureAirport && formData.destinationAirport ? ` (${formData.departureAirport}→${formData.destinationAirport})` : ''}`,
      body: `
Dear ${getUserName(travel)},

This is to confirm your upcoming ${isCheckIn ? 'check-in' : 'check-out'} travel:

📅 DATE: ${travelDate}
${formData.departureTime ? `⏰ DEPARTURE TIME: ${formData.departureTime}` : ''}
${formData.arrivalTime ? `🛬 ARRIVAL TIME: ${formData.arrivalTime}` : ''}
${formData.transportType === 'plane' ? flightSection : carSection}
${formData.transportType !== 'plane' && airportPickupSection ? airportPickupSection : ''}
${isCheckIn && project ? `📍 PROJECT LOCATION:\n   ${project.name}\n   ${[project.street_address, project.postal_code, project.city, project.country].filter(Boolean).join(', ')}` : ''}
${accommodationSection}

${formData.notes ? `📝 NOTES:\n${formData.notes}` : ''}

IMPORTANT REMINDERS:
${formData.transportType === 'plane' ? `• Arrive at ${formData.departureAirport || 'departure airport'} at least 2 hours before flight
• Have your ID/passport ready
• Check baggage allowance for your flight${formData.organizedTransport ? `
• Meet at: ${formData.meetingPoint || 'designated meeting point'} at ${formData.meetingTime || 'scheduled time'}
• Look for ${formData.transportCompany || 'transport company'} representative` : ''}` : `${travel.pickup_driver_id ? `• Be ready at your home address for pickup
• Your driver will contact you regarding exact pickup time` : travel.passengers_data && travel.passengers_data.length > 0 ? `• Coordinate pickup times with your passengers
• Plan your route: home → passengers → project` : `• Be ready at your location on time`}
• Have all necessary documents ready`}

If you have any questions, please contact your coordinator.

Best regards,
Travel Coordination Team
      `.trim()
    };
  };

  // ============ COPY FROM PREVIOUS TRAVEL ============
  const getPreviousTravelSettings = (userId) => {
    // Find the most recent completed travel for this user
    const userTravels = allPeople
      .filter(t => t.user_id === userId && (t.car_id || t.transport_type))
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

    if (userTravels.length > 0) {
      const prev = userTravels[0];
      return {
        car_id: prev.car_id,
        transport_type: prev.transport_type || 'car',
        hasSettings: true,
        travelDate: new Date(prev.start_date).toLocaleDateString('pl-PL')
      };
    }
    return { hasSettings: false };
  };

  // ============ CALENDAR HELPERS ============
  const getCalendarDays = (month) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startDay = firstDay.getDay() || 7; // Monday = 1
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month days
    for (let i = startDay - 1; i > 0; i--) {
      const date = new Date(year, monthIndex, 1 - i);
      days.push({ date, isCurrentMonth: false });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, monthIndex, i);
      days.push({ date, isCurrentMonth: true });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, monthIndex + 1, i);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  };

  const getTravelsForDate = (date) => {
    const dateStr = date.toDateString();
    return allPeople.filter(t => new Date(t.start_date).toDateString() === dateStr);
  };

  // ============ EXPORT TO ICAL ============
  const exportToICal = () => {
    let icalContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Ventro//Travel Coordination//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];

    allPeople.forEach(travel => {
      const startDate = new Date(travel.start_date);
      const endDate = new Date(travel.end_date || travel.start_date);
      endDate.setDate(endDate.getDate() + 1); // All-day event

      const formatDate = (d) => {
        return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };

      const formatDateOnly = (d) => {
        return d.toISOString().split('T')[0].replace(/-/g, '');
      };

      icalContent.push(
        'BEGIN:VEVENT',
        `UID:travel-${travel.id}@ventro.energy`,
        `DTSTAMP:${formatDate(new Date())}`,
        `DTSTART;VALUE=DATE:${formatDateOnly(startDate)}`,
        `DTEND;VALUE=DATE:${formatDateOnly(endDate)}`,
        `SUMMARY:${travel.travel_type === 'check_in' ? '🛬 Check-in' : '🛫 Check-out'}: ${getUserName(travel)}`,
        `DESCRIPTION:${travel.transport_type ? `Transport: ${travel.transport_type}` : 'Pending configuration'}\\n${travel.address || ''}`,
        `LOCATION:${travel.address || ''}`,
        'END:VEVENT'
      );
    });

    icalContent.push('END:VCALENDAR');

    const blob = new Blob([icalContent.join('\r\n')], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'travel-coordination.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ============ EXPORT TICKETS (from selected) ============
  const exportTickets = async () => {
    if (selectedPeople.length === 0) {
      alert('Please select people from the table first');
      return;
    }

    const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
    const travelsWithTickets = selectedTravels.filter(t => t.attachment_flight || t.attachment_ferry);

    if (travelsWithTickets.length === 0) {
      alert('No tickets found for selected people');
      return;
    }

    // Download each ticket as file using fetch + blob
    for (let i = 0; i < travelsWithTickets.length; i++) {
      const travel = travelsWithTickets[i];
      const ticketUrl = travel.attachment_flight || travel.attachment_ferry;
      if (ticketUrl) {
        try {
          const response = await fetch(ticketUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          const userName = getUserName(travel).replace(/\s+/g, '_');
          const date = new Date(travel.start_date).toISOString().split('T')[0];
          const type = travel.travel_type === 'check_in' ? 'checkin' : 'checkout';
          const extension = ticketUrl.split('.').pop()?.split('?')[0] || 'pdf';
          link.download = `ticket_${userName}_${date}_${type}.${extension}`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // Delay between downloads
          if (i < travelsWithTickets.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 800));
          }
        } catch (error) {
          console.error('Error downloading ticket:', error);
        }
      }
    }
  };

  // ============ EXPORT LIST (CSV from selected) ============
  const exportList = () => {
    if (selectedPeople.length === 0) {
      alert('Please select people from the table first');
      return;
    }

    const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
    const headers = ['Name', 'Date', 'Type', 'Project', 'Status', 'Transport', 'Driver', 'Destination', 'Has Ticket'];

    const rows = selectedTravels.map(travel => {
      const projectName = (() => {
        if (travel.address && !isAutoGeneratedAddress(travel.address)) {
          const match = travel.address.match(/^(.+?)\s*-\s*/);
          if (match) return match[1].trim();
        }
        return '';
      })();

      return [
        getUserName(travel),
        new Date(travel.start_date).toLocaleDateString('en-GB'),
        travel.travel_type === 'check_in' ? 'Check-in' : 'Check-out',
        projectName,
        getTravelStatus(travel),
        travel.transport_type || '',
        travel.driver_name || '',
        getCleanAddress(travel) || '',
        (travel.attachment_flight || travel.attachment_ferry) ? 'Yes' : 'No'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `travel-list-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ============ SUMMARY DASHBOARD COMPONENT ============
  const SummaryDashboard = () => (
    <div className="mb-4">
      <div className="bg-white rounded-xl shadow-sm border border-steel-200 px-5 py-3">
        <div className="flex items-center justify-between">
          {/* This Week */}
          <div className="flex items-center space-x-4">
            <div className="bg-brand-100 rounded-xl p-2">
              <Calendar className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">This Week</p>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-slate-800">{summaryStats.thisWeekTotal}</span>
                <span className="text-slate-500 text-sm">travels</span>
                <span className="text-slate-400 text-sm">({summaryStats.thisWeekCheckIns} in / {summaryStats.thisWeekCheckOuts} out)</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-10 w-px bg-steel-200 mx-6"></div>

          {/* Status Summary */}
          <div className="flex items-center space-x-6">
            {/* Pending */}
            <div className="flex items-center space-x-2">
              <div className="w-2.5 h-2.5 bg-steel-400 rounded-full"></div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Pending</p>
                <p className="text-lg font-bold text-slate-700">{summaryStats.pending}</p>
              </div>
            </div>

            {/* Draft */}
            <div className="flex items-center space-x-2">
              <div className="w-2.5 h-2.5 bg-brand-500 rounded-full"></div>
              <div>
                <p className="text-xs text-brand-600 font-medium">Draft</p>
                <p className="text-lg font-bold text-brand-700">{summaryStats.drafts}</p>
              </div>
            </div>

            {/* Sent */}
            <div className="flex items-center space-x-2">
              <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
              <div>
                <p className="text-xs text-green-600 font-medium">Sent</p>
                <p className="text-lg font-bold text-green-700">{summaryStats.sent}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============ SMART SUGGESTIONS PANEL ============
  const SmartSuggestionsPanel = () => {
    if (smartSuggestions.length === 0) return null;

    return (
      <div className="mb-3 bg-brand-50 border border-brand-200 rounded-xl p-2">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold text-brand-800 flex items-center">
            <TrendingUp className="h-4 w-4 mr-1" />
            Smart Suggestions
          </h3>
        </div>
        <div className="space-y-1">
          {smartSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-2 rounded text-sm ${
                suggestion.priority === 'critical' ? 'bg-red-100' : 'bg-brand-100'
              }`}
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                {suggestion.type === 'group_travel' ? (
                  <Users className="h-4 w-4 text-brand-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                )}
                <span className={`truncate ${suggestion.priority === 'critical' ? 'text-red-700' : 'text-brand-700'}`}>
                  {suggestion.message}
                </span>
              </div>
              <button
                onClick={() => {
                  const ids = suggestion.travels.map(t => t.id);
                  setSelectedPeople(ids);
                  openAssignModal(ids);
                }}
                className={`text-sm px-2 py-1 rounded ml-2 flex-shrink-0 ${
                  suggestion.priority === 'critical'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-brand-500 text-white hover:bg-brand-600'
                }`}
              >
                {suggestion.action}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============ BULK ACTIONS BAR ============
  const BulkActionsBar = () => {
    if (selectedPeople.length === 0) return null;

    return (
      <div className="mb-3 bg-brand-50 border border-brand-200 rounded-xl p-2 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium text-brand-800">
            {selectedPeople.length} selected
          </span>
          <button
            onClick={() => setSelectedPeople([])}
            className="text-sm text-brand-600 hover:text-brand-800"
          >
            Clear
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => openAssignModal()}
            className="text-sm px-2 py-1 bg-brand-600 text-white rounded hover:bg-brand-700"
          >
            Assign Transport
          </button>
        </div>
      </div>
    );
  };

  // ============ SELECT ALL FOR DATE ============
  const selectAllForDate = (date) => {
    const dateStr = date.toDateString();
    const travelsOnDate = allPeople.filter(t =>
      new Date(t.start_date).toDateString() === dateStr
    );
    setSelectedPeople(prev => {
      const newSelected = new Set(prev);
      travelsOnDate.forEach(t => newSelected.add(t.id));
      return Array.from(newSelected);
    });
  };

  // ============ CALENDAR VIEW COMPONENT ============
  const CalendarView = () => {
    const calendarDays = getCalendarDays(currentMonth);
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Calculate monthly stats
    const monthlyStats = useMemo(() => {
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const monthTravels = allPeople.filter(t => {
        const travelDate = new Date(t.start_date);
        return travelDate >= monthStart && travelDate <= monthEnd;
      });

      return {
        total: monthTravels.length,
        checkIns: monthTravels.filter(t => t.travel_type === 'check_in').length,
        checkOuts: monthTravels.filter(t => t.travel_type === 'check_out').length,
        pending: monthTravels.filter(t => getTravelStatus(t) === 'pending').length,
        configured: monthTravels.filter(t => getTravelStatus(t) === 'draft' || getTravelStatus(t) === 'sent').length
      };
    }, [allPeople, currentMonth]);

    const goToToday = () => {
      setCurrentMonth(new Date());
      setSelectedCalendarDay(new Date());
    };

    const isCurrentMonthToday = currentMonth.getMonth() === new Date().getMonth() &&
                                 currentMonth.getFullYear() === new Date().getFullYear();

    return (
      <div className="flex gap-4 h-[calc(100vh-220px)]">
        {/* Main Calendar */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden flex flex-col">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-steel-100 bg-gradient-to-r from-steel-50 to-white">
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-steel-100 rounded-xl p-0.5">
                <button
                  onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                  className="p-2 hover:bg-white rounded-xl transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-slate-600" />
                </button>
                <button
                  onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                  className="p-2 hover:bg-white rounded-xl transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-slate-600" />
                </button>
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              {!isCurrentMonthToday && (
                <button
                  onClick={goToToday}
                  className="px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-xl hover:bg-brand-100 transition-colors"
                >
                  Today
                </button>
              )}
            </div>

            {/* Monthly Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 rounded-xl">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  {monthlyStats.checkIns} arrivals
                </span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-xl">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  {monthlyStats.checkOuts} departures
                </span>
                {monthlyStats.pending > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-700 rounded-xl">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    {monthlyStats.pending} pending
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 border-b border-steel-100 bg-steel-50">
            {weekDays.map((day, idx) => (
              <div
                key={day}
                className={`py-3 text-center text-xs font-semibold uppercase tracking-wider ${
                  idx >= 5 ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 flex-1">
            {calendarDays.map((day, index) => {
              const travels = getTravelsForDate(day.date);
              const checkIns = travels.filter(t => t.travel_type === 'check_in');
              const checkOuts = travels.filter(t => t.travel_type === 'check_out');
              const pendingCount = travels.filter(t => getTravelStatus(t) === 'pending').length;
              const isToday = day.date.toDateString() === new Date().toDateString();
              const isSelected = selectedCalendarDay?.toDateString() === day.date.toDateString();
              const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedCalendarDay(day.date);
                  }}
                  className={`min-h-[100px] p-2 border-b border-r border-steel-100 cursor-pointer transition-all relative ${
                    !day.isCurrentMonth ? 'bg-steel-50/50' : isWeekend ? 'bg-steel-50/30' : 'bg-white'
                  } ${isSelected ? 'bg-brand-50 ring-2 ring-brand-500 ring-inset z-10' : 'hover:bg-steel-50'}`}
                >
                  {/* Date number */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium transition-colors ${
                      !day.isCurrentMonth ? 'text-slate-300' :
                      isToday ? 'bg-brand-500 text-white' :
                      isSelected ? 'bg-brand-100 text-brand-700' :
                      'text-slate-700 hover:bg-steel-100'
                    }`}>
                      {day.date.getDate()}
                    </span>
                    {pendingCount > 0 && day.isCurrentMonth && (
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title={`${pendingCount} pending`}></span>
                    )}
                  </div>

                  {/* Travel indicators */}
                  {travels.length > 0 && day.isCurrentMonth && (
                    <div className="space-y-1">
                      {checkIns.length > 0 && (() => {
                        const sentCount = checkIns.filter(t => getTravelStatus(t) === 'sent').length;
                        const notSentCount = checkIns.length - sentCount;
                        return (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-xl text-xs font-medium">
                            <ArrowDown className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">
                              {checkIns.length} in
                              {sentCount > 0 && <span className="text-green-600 ml-1">({sentCount} sent)</span>}
                            </span>
                          </div>
                        );
                      })()}
                      {checkOuts.length > 0 && (() => {
                        const sentCount = checkOuts.filter(t => getTravelStatus(t) === 'sent').length;
                        const notSentCount = checkOuts.length - sentCount;
                        return (
                          <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-xl text-xs font-medium">
                            <ArrowUp className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">
                              {checkOuts.length} out
                              {sentCount > 0 && <span className="text-orange-600 ml-1">({sentCount} sent)</span>}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Sidebar - Day Details */}
        <div className="w-96 bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden flex flex-col">
          {/* Sidebar Header */}
          <div className="px-5 py-4 border-b border-steel-100 bg-gradient-to-r from-steel-50 to-white">
            {selectedCalendarDay ? (
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-slate-900">
                    {selectedCalendarDay.toLocaleDateString('en-US', { weekday: 'long' })}
                  </h4>
                  <p className="text-sm text-slate-500">
                    {selectedCalendarDay.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                {getTravelsForDate(selectedCalendarDay).length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => selectAllForDate(selectedCalendarDay)}
                      className="p-2 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-colors"
                      title="Select all"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setFilterByDate(selectedCalendarDay);
                        setViewMode('table');
                      }}
                      className="p-2 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-colors"
                      title="View in table"
                    >
                      <List className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-slate-500">Select a day to view details</p>
              </div>
            )}
          </div>

          {/* Day Content */}
          <div className="flex-1 overflow-y-auto">
            {selectedCalendarDay ? (
              getTravelsForDate(selectedCalendarDay).length > 0 ? (
                <div className="p-3 space-y-2">
                  {getTravelsForDate(selectedCalendarDay).map(travel => {
                    let projectName = '';
                    if (travel.address && !isAutoGeneratedAddress(travel.address)) {
                      const projectMatch = travel.address.match(/^(.+?)\s*-\s*/);
                      if (projectMatch) projectName = projectMatch[1].trim();
                    }
                    const status = getTravelStatus(travel);
                    const isCheckIn = travel.travel_type === 'check_in';

                    return (
                      <div
                        key={travel.id}
                        onClick={() => {
                          setSelectedPeople(prev =>
                            prev.includes(travel.id)
                              ? prev.filter(id => id !== travel.id)
                              : [...prev, travel.id]
                          );
                        }}
                        className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
                          selectedPeople.includes(travel.id)
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-steel-200 bg-white hover:border-steel-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedPeople.includes(travel.id)}
                            onChange={() => {}}
                            className="h-3.5 w-3.5 text-brand-600 rounded border-steel-300"
                          />

                          <div className="relative flex-shrink-0">
                            {usersData[travel.user_id]?.profile_picture ? (
                              <img
                                src={usersData[travel.user_id].profile_picture}
                                alt=""
                                className="w-8 h-8 rounded-xl object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-xl bg-steel-100 flex items-center justify-center text-slate-500 text-xs font-semibold">
                                {getUserName(travel).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ${
                              isCheckIn ? 'bg-green-500' : 'bg-orange-500'
                            }`}>
                              {isCheckIn ? (
                                <ArrowDown className="h-2.5 w-2.5 text-white" />
                              ) : (
                                <ArrowUp className="h-2.5 w-2.5 text-white" />
                              )}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPersonHistory(travel.user_id, getUserName(travel));
                                }}
                                className="font-medium text-sm text-slate-900 hover:text-brand-600 transition-colors truncate"
                              >
                                {getDisplayName(travel)}
                              </button>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                status === 'sent' ? 'bg-green-500' :
                                status === 'draft' ? 'bg-brand-500' : 'bg-steel-400'
                              }`}></span>
                            </div>
                            {projectName && (
                              <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                {projectName}
                              </p>
                            )}
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPeople([travel.id]);
                              openAssignModal([travel.id]);
                            }}
                            className="p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="w-16 h-16 bg-steel-100 rounded-full flex items-center justify-center mb-4">
                    <Calendar className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">No travels scheduled</p>
                  <p className="text-sm text-slate-400 mt-1">This day is free</p>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="w-16 h-16 bg-steel-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-500 font-medium">Select a day</p>
                <p className="text-sm text-slate-400 mt-1">Click on any day to see details</p>
              </div>
            )}
          </div>

          {/* Sidebar Footer - Actions */}
          {selectedPeople.length > 0 && (
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-700">
                  {selectedPeople.length} selected
                </span>
                <button
                  onClick={() => setSelectedPeople([])}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Clear
                </button>
              </div>
              <div className="flex gap-2">
                {(() => {
                  const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id));
                  const allAreDrafts = selectedTravels.every(t => getTravelStatus(t) === 'draft');
                  if (allAreDrafts) {
                    return (
                      <button
                        onClick={handleMarkAsPlanned}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Send
                      </button>
                    );
                  }
                  return null;
                })()}
                <button
                  onClick={() => openAssignModal()}
                  className="flex-1 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Configure
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ TIMELINE VIEW COMPONENT ============
  const TimelineView = () => {
    const [selectedTimelineDay, setSelectedTimelineDay] = useState(new Date());

    const days = [];
    for (let i = 0; i < timelineDays; i++) {
      const date = new Date(timelineStartDate);
      date.setDate(timelineStartDate.getDate() + i);
      days.push(date);
    }

    const navigateTimeline = (direction) => {
      const newDate = new Date(timelineStartDate);
      newDate.setDate(timelineStartDate.getDate() + (direction * 7));
      setTimelineStartDate(newDate);
    };

    const goToToday = () => {
      setTimelineStartDate(new Date());
      setSelectedTimelineDay(new Date());
    };

    // Calculate period stats
    const periodStats = useMemo(() => {
      const periodTravels = days.flatMap(day => getTravelsForDate(day));
      return {
        total: periodTravels.length,
        arrivals: periodTravels.filter(t => t.travel_type === 'check_in').length,
        departures: periodTravels.filter(t => t.travel_type === 'check_out').length,
        pending: periodTravels.filter(t => getTravelStatus(t) === 'pending').length,
        sent: periodTravels.filter(t => getTravelStatus(t) === 'sent').length
      };
    }, [days, allPeople]);

    // Group days by week for better visualization
    const weeks = useMemo(() => {
      const result = [];
      let currentWeek = [];

      days.forEach((day, index) => {
        currentWeek.push(day);
        if (day.getDay() === 0 || index === days.length - 1) {
          result.push([...currentWeek]);
          currentWeek = [];
        }
      });

      return result;
    }, [days]);

    const endDate = new Date(timelineStartDate);
    endDate.setDate(timelineStartDate.getDate() + timelineDays - 1);

    const isShowingToday = days.some(d => d.toDateString() === new Date().toDateString());

    return (
      <div className="flex gap-4 h-[calc(100vh-220px)]">
        {/* Main Timeline */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-steel-100 bg-gradient-to-r from-steel-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center bg-steel-100 rounded-xl p-0.5">
                  <button
                    onClick={() => navigateTimeline(-1)}
                    className="p-2 hover:bg-white rounded-xl transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => navigateTimeline(1)}
                    className="p-2 hover:bg-white rounded-xl transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-slate-600" />
                  </button>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {timelineStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' - '}
                    {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </h3>
                  <p className="text-xs text-slate-500">{timelineDays} days view</p>
                </div>

                {!isShowingToday && (
                  <button
                    onClick={goToToday}
                    className="px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-xl hover:bg-brand-100 transition-colors"
                  >
                    Today
                  </button>
                )}

                {/* Jump to date */}
                <input
                  type="date"
                  value={timelineStartDate.toISOString().split('T')[0]}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setTimelineStartDate(newDate);
                    setSelectedTimelineDay(newDate);
                  }}
                  className="px-2 py-1 text-sm border border-steel-200 rounded-xl focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              {/* Period Stats */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 rounded-xl">
                    <ArrowDown className="h-3 w-3" />
                    {periodStats.arrivals}
                  </span>
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-xl">
                    <ArrowUp className="h-3 w-3" />
                    {periodStats.departures}
                  </span>
                  {periodStats.pending > 0 && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-700 rounded-xl">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      {periodStats.pending} pending
                    </span>
                  )}
                </div>

                {/* Days range selector */}
                <div className="flex items-center bg-steel-100 rounded-xl p-0.5">
                  {[7, 14, 30].map(d => (
                    <button
                      key={d}
                      onClick={() => setTimelineDays(d)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${
                        timelineDays === d
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Grid */}
          <div className="flex-1 overflow-y-auto">
            {days.map((day, index) => {
              const travels = getTravelsForDate(day);
              const checkIns = travels.filter(t => t.travel_type === 'check_in');
              const checkOuts = travels.filter(t => t.travel_type === 'check_out');
              const isToday = day.toDateString() === new Date().toDateString();
              const isSelected = selectedTimelineDay?.toDateString() === day.toDateString();
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const pendingCount = travels.filter(t => getTravelStatus(t) === 'pending').length;
              const sentCount = travels.filter(t => getTravelStatus(t) === 'sent').length;
              const isNewWeek = day.getDay() === 1;

              return (
                <div key={index}>
                  <div
                    onClick={() => setSelectedTimelineDay(day)}
                    className={`flex items-stretch border-b border-steel-100 cursor-pointer transition-all flex-1 ${
                      isSelected ? 'bg-brand-50 border-l-4 border-l-brand-500' : 'hover:bg-steel-50'
                    } ${isWeekend && !isSelected ? 'bg-steel-50/50' : ''}`}
                  >
                    {/* Date Column */}
                    <div className={`w-20 flex-shrink-0 py-2.5 px-2 flex flex-col items-center justify-center border-r border-steel-100 ${
                      isToday ? 'bg-brand-100' : ''
                    }`}>
                      <span className={`text-xs font-medium uppercase ${
                        isToday ? 'text-brand-600' : isWeekend ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                      <span className={`text-2xl font-bold ${
                        isToday ? 'text-brand-600' : 'text-slate-800'
                      }`}>
                        {day.getDate()}
                      </span>
                      {isToday && (
                        <span className="text-[9px] font-semibold text-white bg-brand-500 px-1.5 py-0.5 rounded-full">
                          TODAY
                        </span>
                      )}
                    </div>

                    {/* Content Column */}
                    <div className="flex-1 p-2.5 min-h-0">
                      {travels.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {/* Arrivals */}
                          {checkIns.length > 0 && (
                            <>
                              <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                                <ArrowDown className="h-3.5 w-3.5" />
                                {checkIns.length}
                              </span>
                              {checkIns.map(travel => (
                                <div
                                  key={travel.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPeople(prev =>
                                      prev.includes(travel.id)
                                        ? prev.filter(id => id !== travel.id)
                                        : [...prev, travel.id]
                                    );
                                  }}
                                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition-all ${
                                    selectedPeople.includes(travel.id)
                                      ? 'bg-brand-200 text-brand-800 ring-1 ring-brand-400'
                                      : 'bg-green-50 border border-green-200 text-slate-700 hover:bg-green-100'
                                  }`}
                                >
                                  {usersData[travel.user_id]?.profile_picture ? (
                                    <img src={usersData[travel.user_id].profile_picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-steel-200 flex items-center justify-center text-[9px] font-medium">
                                      {getUserName(travel).split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                  )}
                                  <span className="font-medium">{getDisplayName(travel)}</span>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    getTravelStatus(travel) === 'sent' ? 'bg-green-500' :
                                    getTravelStatus(travel) === 'draft' ? 'bg-brand-500' : 'bg-steel-400'
                                  }`}></span>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Separator */}
                          {checkIns.length > 0 && checkOuts.length > 0 && (
                            <span className="text-slate-300 mx-1">|</span>
                          )}

                          {/* Departures */}
                          {checkOuts.length > 0 && (
                            <>
                              <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                                <ArrowUp className="h-3.5 w-3.5" />
                                {checkOuts.length}
                              </span>
                              {checkOuts.map(travel => (
                                <div
                                  key={travel.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPeople(prev =>
                                      prev.includes(travel.id)
                                        ? prev.filter(id => id !== travel.id)
                                        : [...prev, travel.id]
                                    );
                                  }}
                                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition-all ${
                                    selectedPeople.includes(travel.id)
                                      ? 'bg-brand-200 text-brand-800 ring-1 ring-brand-400'
                                      : 'bg-orange-50 border border-orange-200 text-slate-700 hover:bg-orange-100'
                                  }`}
                                >
                                  {usersData[travel.user_id]?.profile_picture ? (
                                    <img src={usersData[travel.user_id].profile_picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-steel-200 flex items-center justify-center text-[9px] font-medium">
                                      {getUserName(travel).split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                  )}
                                  <span className="font-medium">{getDisplayName(travel)}</span>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    getTravelStatus(travel) === 'sent' ? 'bg-green-500' :
                                    getTravelStatus(travel) === 'draft' ? 'bg-brand-500' : 'bg-steel-400'
                                  }`}></span>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </div>

                    {/* Status indicators */}
                    <div className="w-16 flex-shrink-0 p-2 flex flex-col items-center justify-center gap-0.5 border-l border-steel-100 text-[10px]">
                      {travels.length > 0 && (
                        <>
                          {sentCount > 0 && (
                            <span className="text-green-600 font-medium">{sentCount} sent</span>
                          )}
                          {pendingCount > 0 && (
                            <span className="flex items-center gap-0.5 text-red-600 font-medium">
                              <span className="w-1 h-1 bg-red-500 rounded-full animate-pulse"></span>
                              {pendingCount}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Sidebar - Day Details */}
        <div className="w-96 bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden flex flex-col">
          {/* Sidebar Header */}
          <div className="px-5 py-4 border-b border-steel-100 bg-gradient-to-r from-steel-50 to-white">
            {selectedTimelineDay ? (
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-slate-900">
                    {selectedTimelineDay.toLocaleDateString('en-US', { weekday: 'long' })}
                  </h4>
                  <p className="text-sm text-slate-500">
                    {selectedTimelineDay.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                {getTravelsForDate(selectedTimelineDay).length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => selectAllForDate(selectedTimelineDay)}
                      className="p-2 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-colors"
                      title="Select all"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setFilterByDate(selectedTimelineDay);
                        setViewMode('table');
                      }}
                      className="p-2 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-colors"
                      title="View in table"
                    >
                      <List className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-slate-500">Select a day to view details</p>
              </div>
            )}
          </div>

          {/* Day Content */}
          <div className="flex-1 overflow-y-auto">
            {selectedTimelineDay ? (
              getTravelsForDate(selectedTimelineDay).length > 0 ? (
                <div className="p-3 space-y-2">
                  {getTravelsForDate(selectedTimelineDay).map(travel => {
                    let projectName = '';
                    if (travel.address && !isAutoGeneratedAddress(travel.address)) {
                      const projectMatch = travel.address.match(/^(.+?)\s*-\s*/);
                      if (projectMatch) projectName = projectMatch[1].trim();
                    }
                    const status = getTravelStatus(travel);
                    const isCheckIn = travel.travel_type === 'check_in';

                    return (
                      <div
                        key={travel.id}
                        onClick={() => {
                          setSelectedPeople(prev =>
                            prev.includes(travel.id)
                              ? prev.filter(id => id !== travel.id)
                              : [...prev, travel.id]
                          );
                        }}
                        className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
                          selectedPeople.includes(travel.id)
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-steel-200 bg-white hover:border-steel-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedPeople.includes(travel.id)}
                            onChange={() => {}}
                            className="h-3.5 w-3.5 text-brand-600 rounded border-steel-300"
                          />

                          <div className="relative flex-shrink-0">
                            {usersData[travel.user_id]?.profile_picture ? (
                              <img
                                src={usersData[travel.user_id].profile_picture}
                                alt=""
                                className="w-8 h-8 rounded-xl object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-xl bg-steel-100 flex items-center justify-center text-slate-500 text-xs font-semibold">
                                {getUserName(travel).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ${
                              isCheckIn ? 'bg-green-500' : 'bg-orange-500'
                            }`}>
                              {isCheckIn ? (
                                <ArrowDown className="h-2.5 w-2.5 text-white" />
                              ) : (
                                <ArrowUp className="h-2.5 w-2.5 text-white" />
                              )}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPersonHistory(travel.user_id, getUserName(travel));
                                }}
                                className="font-medium text-sm text-slate-900 hover:text-brand-600 transition-colors truncate"
                              >
                                {getDisplayName(travel)}
                              </button>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                status === 'sent' ? 'bg-green-500' :
                                status === 'draft' ? 'bg-brand-500' : 'bg-steel-400'
                              }`}></span>
                            </div>
                            {projectName && (
                              <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                {projectName}
                              </p>
                            )}
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPeople([travel.id]);
                              openAssignModal([travel.id]);
                            }}
                            className="p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="w-16 h-16 bg-steel-100 rounded-full flex items-center justify-center mb-4">
                    <Calendar className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">No travels scheduled</p>
                  <p className="text-sm text-slate-400 mt-1">This day is free</p>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="w-16 h-16 bg-steel-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-500 font-medium">Select a day</p>
                <p className="text-sm text-slate-400 mt-1">Click on any row to see details</p>
              </div>
            )}
          </div>

          {/* Sidebar Footer - Actions */}
          {selectedPeople.length > 0 && (
            <div className="px-4 py-3 border-t border-steel-100 bg-steel-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-700">
                  {selectedPeople.length} selected
                </span>
                <button
                  onClick={() => setSelectedPeople([])}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Clear
                </button>
              </div>
              <div className="flex gap-2">
                {(() => {
                  const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id));
                  const allAreDrafts = selectedTravels.every(t => getTravelStatus(t) === 'draft');
                  if (allAreDrafts) {
                    return (
                      <button
                        onClick={handleMarkAsPlanned}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Send
                      </button>
                    );
                  }
                  return null;
                })()}
                <button
                  onClick={() => openAssignModal()}
                  className="flex-1 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Configure
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ FULL MAP VIEW ============
  const FullMapView = () => {
    const [mapFilter, setMapFilter] = useState('all'); // 'all', 'check_in', 'check_out'
    const [selectedMapProject, setSelectedMapProject] = useState(null);

    // Get unique projects from travels
    const travelProjects = useMemo(() => {
      const projectIds = new Set();
      const projectList = [];

      getFilteredPeople().forEach(travel => {
        const projectId = getTravelProjectId(travel);
        if (projectId && !projectIds.has(projectId)) {
          projectIds.add(projectId);
          const project = projects.find(p => p.id === projectId);
          if (project) {
            projectList.push(project);
          }
        }
      });

      return projectList;
    }, [getFilteredPeople, projects]);

    // Filter travels based on mapFilter and selected project
    const filteredTravels = useMemo(() => {
      let travels = getFilteredPeople();
      if (mapFilter !== 'all') {
        travels = travels.filter(t => t.travel_type === mapFilter);
      }
      if (selectedMapProject) {
        travels = travels.filter(t => getTravelProjectId(t) === selectedMapProject);
      }
      return travels;
    }, [getFilteredPeople, mapFilter, selectedMapProject]);

    // Memoize values passed to TravelMapPanel to prevent infinite loops
    const selectedPeopleIds = useMemo(() => {
      return filteredTravels.map(t => t.id);
    }, [filteredTravels]);

    // Collect unique airports from travels
    const travelAirports = useMemo(() => {
      const airportCodes = new Set();
      filteredTravels.forEach(travel => {
        if (travel.departure_airport) airportCodes.add(travel.departure_airport);
        if (travel.arrival_airport) airportCodes.add(travel.arrival_airport);
      });
      return Array.from(airportCodes);
    }, [filteredTravels]);

    // Stats
    const stats = useMemo(() => {
      const checkIns = filteredTravels.filter(t => t.travel_type === 'check_in').length;
      const checkOuts = filteredTravels.filter(t => t.travel_type === 'check_out').length;
      return { checkIns, checkOuts };
    }, [filteredTravels]);

    return (
      <div className="bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden relative" style={{ height: 'calc(100vh - 140px)' }}>
        {/* Top Controls Bar */}
        <div className="absolute top-3 left-28 right-3 z-[1000] flex items-center justify-between pointer-events-none">
          {/* Left - Filters */}
          <div className="flex items-center gap-2 pointer-events-auto">
            <div className="px-3 py-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-steel-200 flex items-center gap-3">
              <select
                value={mapFilter}
                onChange={(e) => setMapFilter(e.target.value)}
                className="text-sm px-3 py-1.5 border border-steel-300 rounded-xl focus:ring-1 focus:ring-brand-500 bg-white min-w-[120px]"
              >
                <option value="all">All Types</option>
                <option value="check_in">Arrivals</option>
                <option value="check_out">Departures</option>
              </select>
              {travelProjects.length > 1 && (
                <select
                  value={selectedMapProject || ''}
                  onChange={(e) => setSelectedMapProject(e.target.value ? parseInt(e.target.value) : null)}
                  className="text-sm px-3 py-1.5 border border-steel-300 rounded-xl focus:ring-1 focus:ring-brand-500 bg-white min-w-[140px]"
                >
                  <option value="">All Projects</option>
                  {travelProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Right - Stats */}
          <div className="flex items-center gap-2 pointer-events-auto">
            <div className="px-3 py-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-steel-200 flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <ArrowDown className="h-3.5 w-3.5 text-green-500" />
                <span className="font-semibold text-green-700">{stats.checkIns}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <ArrowUp className="h-3.5 w-3.5 text-orange-500" />
                <span className="font-semibold text-orange-700">{stats.checkOuts}</span>
              </span>
              <span className="w-px h-4 bg-steel-300"></span>
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-brand-500" />
                <span className="font-semibold text-brand-700">{travelProjects.length}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-brand-500" />
                <span className="font-semibold text-brand-700">{filteredTravels.length}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Legend - Bottom Left */}
        <div className="absolute bottom-3 left-3 z-[1000]">
          <div className="px-3 py-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-steel-200 text-xs">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow"></span>
                <span className="text-slate-600">Project</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-brand-500 border-2 border-white shadow"></span>
                <span className="text-slate-600">Person</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-brand-500 border-2 border-white shadow"></span>
                <span className="text-slate-600">Airport</span>
              </span>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="h-full">
          <TravelMapPanel
            selectedPeople={selectedPeopleIds}
            allPeople={allPeople}
            project={travelProjects[0] || null}
            projects={travelProjects}
            getUserName={getDisplayName}
            transportType="car"
            usersData={usersData}
            departureTime=""
            departureAirport=""
            destinationAirport=""
            isFullMapView={true}
            airportCodes={travelAirports}
          />
        </div>
      </div>
    );
  };

  // ============ CONFLICTS PANEL ============
  const ConflictsPanel = () => {
    if (conflicts.length === 0) return null;

    return (
      <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-2">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            Attention Required ({conflicts.length})
          </h3>
        </div>
        <div className="space-y-1">
          {conflicts.slice(0, 3).map((conflict, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-2 rounded text-sm ${
                conflict.type === 'urgent' ? 'bg-red-100' : 'bg-amber-100'
              }`}
            >
              <div className="flex items-center space-x-2">
                {conflict.type === 'urgent' ? (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                )}
                <span className={conflict.type === 'urgent' ? 'text-red-700' : 'text-amber-700'}>
                  {conflict.message}
                </span>
              </div>
              <button
                onClick={() => {
                  const ids = conflict.travels.map(t => t.id);
                  setSelectedPeople(ids);
                  openAssignModal(ids);
                }}
                className="text-sm px-2 py-1 bg-white border border-steel-300 rounded hover:bg-steel-50"
              >
                Fix
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-steel-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/daily-task-coordination')}
              className="flex items-center space-x-2 px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-steel-100 rounded text-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <div className="h-5 w-px bg-steel-300"></div>
            <h1 className="text-lg font-black text-slate-900 uppercase tracking-wide">Travel Coordination</h1>
          </div>

          {/* View Mode Toggle & Actions */}
          <div className="flex items-center space-x-2">
            {/* View Mode Buttons */}
            <div className="flex items-center bg-steel-100 rounded p-0.5">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center space-x-2 px-2 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'table' ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <List className="h-4 w-4" />
                <span>Table</span>
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center space-x-2 px-2 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'calendar' ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Calendar className="h-4 w-4" />
                <span>Calendar</span>
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex items-center space-x-2 px-2 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'timeline' ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Clock className="h-4 w-4" />
                <span>Timeline</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center space-x-2 px-2 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'map' ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Map className="h-4 w-4" />
                <span>Map</span>
              </button>
            </div>

            {/* Export Button with Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center space-x-2 px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-steel-100 rounded text-sm transition-colors border border-steel-200"
                title="Export options"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {showExportMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowExportMenu(false)}
                  />
                  <div className="absolute right-0 mt-1 w-40 bg-white rounded-xl shadow-lg border border-steel-200 py-1 z-50">
                    <button
                      onClick={() => {
                        exportTickets();
                        setShowExportMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-steel-100 flex items-center gap-2"
                    >
                      <Ticket className="h-4 w-4" />
                      Export Tickets
                    </button>
                    <button
                      onClick={() => {
                        exportList();
                        setShowExportMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-steel-100 flex items-center gap-2"
                    >
                      <List className="h-4 w-4" />
                      Export List
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* History Button */}
            <button
              onClick={() => {
                loadChangeHistory();
                setShowHistoryModal(true);
              }}
              className="flex items-center space-x-2 px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-steel-100 rounded text-sm transition-colors border border-steel-200"
              title="View Change History"
            >
              <FileText className="h-4 w-4" />
              <span>History</span>
            </button>

            {/* Smart Alerts Toggle - shadcn style */}
            <button
              onClick={() => setShowAlertsPanel(!showAlertsPanel)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${
                showAlertsPanel
                  ? 'bg-brand-500 text-white shadow-sm'
                  : 'bg-white text-slate-700 border border-steel-200 hover:bg-steel-50 hover:border-steel-300'
              }`}
              title="Toggle Action Items Panel"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Actions</span>
              {smartAlerts.filter(a => a.type === 'critical' || a.type === 'urgent').length > 0 && (
                <span className={`ml-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-semibold rounded-full ${
                  showAlertsPanel ? 'bg-white text-slate-900' : 'bg-red-500 text-white'
                }`}>
                  {smartAlerts.filter(a => a.type === 'critical' || a.type === 'urgent').length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Smart Alerts Panel - shadcn/ui style with slide animation */}
      <div
        className={`fixed right-0 top-14 bottom-0 w-96 bg-white/95 backdrop-blur-sm shadow-2xl border-l border-steel-200/80 z-40 overflow-hidden flex flex-col transform transition-transform duration-300 ease-out ${
          showAlertsPanel ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
          {/* Header */}
          <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-xl bg-brand-500 text-white">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">Action Items</h3>
                <p className="text-xs text-slate-500">{smartAlerts.length} items need attention</p>
              </div>
            </div>
            <button
              onClick={() => setShowAlertsPanel(false)}
              className="p-1.5 rounded-xl hover:bg-steel-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Alerts List */}
          <div className="flex-1 overflow-y-auto">
            {smartAlerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <p className="font-medium text-slate-900">All caught up!</p>
                <p className="text-sm text-slate-500 mt-1">No pending items</p>
              </div>
            ) : (
              <div className="divide-y divide-steel-100">
                {smartAlerts.map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    className="px-4 py-3 hover:bg-steel-50/50 transition-colors"
                  >
                    <div className="flex gap-3">
                      {/* Status indicator */}
                      <div className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${
                        alert.type === 'critical' ? 'bg-red-500' :
                        alert.type === 'urgent' ? 'bg-orange-500' :
                        alert.type === 'warning' ? 'bg-yellow-500' :
                        alert.isStats ? 'bg-violet-500' :
                        'bg-brand-500'
                      }`} />

                      <div className="flex-1 min-w-0">
                        {/* Title row */}
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-medium text-slate-900 leading-tight">
                            {alert.title}
                          </h4>
                          {alert.daysUntil !== undefined && alert.daysUntil <= 2 && (
                            <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium ${
                              alert.daysUntil === 0 ? 'bg-red-100 text-red-700' :
                              alert.daysUntil === 1 ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {alert.daysUntil === 0 ? 'Today' : alert.daysUntil === 1 ? 'Tomorrow' : `${alert.daysUntil}d`}
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                          {alert.description}
                        </p>

                        {/* Issues as clickable items with names */}
                        {alert.issues && alert.issues.length > 0 && (
                          <div className="flex flex-col gap-1 mt-2">
                            {alert.issues.map((issue, i) => {
                              // Handle both old string format and new object format
                              if (typeof issue === 'string') {
                                const isSuccess = issue === 'All configured and sent';
                                return (
                                  <span
                                    key={i}
                                    className={`text-xs ${isSuccess ? 'px-2 py-1 bg-green-100 text-green-700 rounded-xl font-medium' : 'text-slate-600'}`}
                                  >
                                    {isSuccess ? '✓ ' : ''}{issue}
                                  </span>
                                );
                              }

                              // New object format with travels
                              return (
                                <div key={i} className="flex items-start gap-2">
                                  <span className="text-xs text-slate-500 whitespace-nowrap pt-0.5">{issue.label}:</span>
                                  <div className="flex flex-wrap gap-1">
                                    {issue.travels.map((travel, j) => (
                                      <button
                                        key={j}
                                        onClick={() => {
                                          if (issue.type === 'missing_accommodation') {
                                            const params = new URLSearchParams();
                                            params.set('users', travel.user_id);
                                            navigate(`/daily-task-coordination/accommodation?${params.toString()}`);
                                          } else {
                                            openAssignModalByProject([travel]);
                                          }
                                        }}
                                        className="text-xs px-1.5 py-0.5 rounded bg-steel-100 text-slate-700 hover:bg-steel-200 hover:text-slate-900 transition-colors"
                                        title={`Configure ${getUserName(travel)}`}
                                      >
                                        {getUserName(travel).split(' ')[0]}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Action buttons */}
                        {alert.travels && alert.travels.length > 0 && !alert.isStats && (
                          <div className="flex items-center gap-2 mt-2.5">
                            <details className="relative">
                              <summary className="text-xs px-2.5 py-1 rounded-xl border border-steel-200 bg-white text-slate-700 hover:bg-steel-50 hover:border-steel-300 transition-colors font-medium cursor-pointer list-none">
                                Select ▾
                              </summary>
                              <div className="absolute left-0 mt-1 bg-white border border-steel-200 rounded-xl shadow-lg z-10 min-w-[160px] py-1">
                                <button
                                  onClick={(e) => {
                                    e.target.closest('details').removeAttribute('open');
                                    const ids = alert.travels.map(t => t.id);
                                    setSelectedPeople(ids);
                                    setShowAlertsPanel(false);
                                  }}
                                  className="w-full text-left text-xs px-3 py-1.5 hover:bg-steel-100 text-slate-700"
                                >
                                  Select all ({alert.travels.length})
                                </button>
                                <div className="border-t border-steel-100 my-1"></div>
                                {alert.travels.map((travel, i) => (
                                  <button
                                    key={i}
                                    onClick={(e) => {
                                      e.target.closest('details').removeAttribute('open');
                                      setSelectedPeople([travel.id]);
                                      setShowAlertsPanel(false);
                                    }}
                                    className="w-full text-left text-xs px-3 py-1.5 hover:bg-steel-100 text-slate-700 flex items-center gap-2"
                                  >
                                    {usersData[travel.user_id]?.profile_picture ? (
                                      <img src={usersData[travel.user_id].profile_picture} alt="" className="h-4 w-4 rounded-full object-cover" />
                                    ) : (
                                      <div className="h-4 w-4 rounded-full bg-steel-200"></div>
                                    )}
                                    {getUserName(travel)}
                                  </button>
                                ))}
                              </div>
                            </details>
                            {alert.travels.length <= 10 && (
                              <button
                                onClick={() => openAssignModalByProject(alert.travels)}
                                className="text-xs px-2.5 py-1 rounded-xl bg-brand-500 text-white hover:bg-brand-600 transition-colors font-medium"
                              >
                                Configure all
                              </button>
                            )}
                          </div>
                        )}

                        {/* Expandable list */}
                        {alert.expandable && alert.travels && alert.travels.length > 0 && (
                          <details className="mt-2 group">
                            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 select-none flex items-center gap-1">
                              <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                              View {alert.travels.length} people
                            </summary>
                            <div className="mt-2 pl-4 space-y-1 border-l-2 border-steel-100 max-h-80 overflow-y-auto">
                              {alert.travels.map(t => {
                                const missingTicket = t.transport_type === 'plane' && !t.flight_number && !t.attachment_flight;
                                const notConfigured = getTravelStatus(t) === 'pending';
                                const hasIssue = missingTicket || notConfigured;
                                const inputId = `quick-upload-${t.id}`;

                                return (
                                  <div
                                    key={t.id}
                                    className={`text-xs flex items-center justify-between py-1 px-1.5 rounded ${
                                      hasIssue ? 'bg-red-50 border border-red-200' : ''
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5 truncate">
                                      <span className={hasIssue ? 'text-red-700 font-medium' : 'text-slate-700'}>{getUserName(t)}</span>
                                      {missingTicket && (
                                        <span className="text-red-500" title="Missing flight ticket">✈️</span>
                                      )}
                                      {notConfigured && (
                                        <span className="text-orange-500" title="Not configured">⚙️</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                      {/* Quick upload button for missing tickets */}
                                      {missingTicket && (
                                        <>
                                          <input
                                            type="file"
                                            id={inputId}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            className="hidden"
                                            disabled={uploadingTicketId === t.id}
                                            onChange={(e) => {
                                              if (e.target.files?.[0]) {
                                                handleQuickTicketUpload(t.id, e.target.files[0]);
                                              }
                                            }}
                                          />
                                          {uploadingTicketId === t.id ? (
                                            <span className="px-1.5 py-0.5 bg-steel-400 text-white rounded text-xs flex items-center gap-1">
                                              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                              </svg>
                                            </span>
                                          ) : uploadSuccessId === t.id ? (
                                            <span className="px-1.5 py-0.5 bg-green-600 text-white rounded text-xs flex items-center gap-1">
                                              ✓
                                            </span>
                                          ) : (
                                            <label
                                              htmlFor={inputId}
                                              className="px-1.5 py-0.5 bg-brand-500 text-white rounded text-xs cursor-pointer hover:bg-brand-600 transition-colors"
                                              title="Upload ticket"
                                            >
                                              Upload
                                            </label>
                                          )}
                                        </>
                                      )}
                                      <span className="text-slate-400 tabular-nums">
                                        {new Date(t.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-steel-100 bg-steel-50/50">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">
                {smartAlerts.filter(a => a.type !== 'info' && !a.isStats).length} action items
              </span>
              <div className="flex items-center gap-3">
                {smartAlerts.filter(a => a.type === 'critical').length > 0 && (
                  <span className="flex items-center gap-1 text-red-600 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    {smartAlerts.filter(a => a.type === 'critical').length} critical
                  </span>
                )}
                {smartAlerts.filter(a => a.type === 'urgent').length > 0 && (
                  <span className="flex items-center gap-1 text-orange-600 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    {smartAlerts.filter(a => a.type === 'urgent').length} urgent
                  </span>
                )}
              </div>
            </div>
          </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 overflow-auto p-3 transition-all duration-300 ${showAlertsPanel ? 'mr-96' : ''}`}>
        {/* Summary Dashboard - only for Table view */}
        {viewMode === 'table' && <SummaryDashboard />}

        {/* Daily Summary Panel - shows when a date is selected */}
        {summaryDate && <DailySummaryPanel />}

        {/* Smart Suggestions + Conflicts - side by side, only for Table and Calendar */}
        {viewMode === 'table' && (smartSuggestions.length > 0 || conflicts.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            {/* Smart Suggestions */}
            {smartSuggestions.length > 0 && (
              <div className="bg-white border border-steel-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-brand-50 to-steel-50 border-b-2 border-brand-500">
                  <h3 className="text-xs font-black text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                    <div className="p-1.5 bg-brand-500 rounded-lg">
                      <TrendingUp className="h-3.5 w-3.5 text-white" />
                    </div>
                    Pending Configuration
                    <span className="ml-auto px-2.5 py-1 bg-brand-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-brand-500/20">
                      {smartSuggestions.length}
                    </span>
                  </h3>
                </div>
                <div className="divide-y divide-steel-100 max-h-52 overflow-y-auto">
                  {smartSuggestions.map((suggestion, index) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const suggestionDate = new Date(suggestion.date);
                    suggestionDate.setHours(0, 0, 0, 0);
                    const isPast = suggestionDate <= today;

                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-steel-50 transition-colors ${
                          isPast ? 'bg-red-50/50' : ''
                        }`}
                      >
                        {/* Date badge */}
                        <div className={`flex flex-col items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 ${
                          isPast ? 'bg-red-100 text-red-700' : 'bg-steel-100 text-slate-700'
                        }`}>
                          <span className="text-lg font-bold leading-none">
                            {suggestion.date.getDate()}
                          </span>
                          <span className="text-[10px] font-medium uppercase">
                            {suggestion.date.toLocaleDateString('en-GB', { month: 'short' })}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 truncate">
                              {suggestion.projectName}
                            </span>
                            <span className={`text-sm ${
                              suggestion.travelType === 'check_in' ? 'text-green-600' :
                              suggestion.travelType === 'check_out' ? 'text-orange-600' : 'text-slate-500'
                            }`}>
                              {suggestion.travelType === 'check_in' ? '🛬' :
                               suggestion.travelType === 'check_out' ? '🛫' :
                               `🛬${suggestion.checkInCount}/🛫${suggestion.checkOutCount}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {suggestion.peopleCount} {suggestion.peopleCount === 1 ? 'person' : 'people'}
                            </span>
                            {isPast && (
                              <span className="text-xs text-red-600 font-medium">• Overdue</span>
                            )}
                          </div>
                        </div>

                        {/* Action button */}
                        <button
                          onClick={() => {
                            const ids = suggestion.travels.map(t => t.id);
                            setSelectedPeople(ids);
                            openAssignModal(ids);
                          }}
                          className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-colors flex-shrink-0 shadow-lg ${
                            isPast
                              ? 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20'
                              : 'bg-brand-500 text-white hover:bg-brand-600 shadow-brand-500/20'
                          }`}
                        >
                          {isPast ? 'Fix' : 'Create'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Conflicts */}
            {conflicts.length > 0 && (
              <div className="bg-white border border-steel-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b-2 border-amber-500">
                  <h3 className="text-xs font-black text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                    <div className="p-1.5 bg-amber-500 rounded-lg">
                      <AlertTriangle className="h-3.5 w-3.5 text-white" />
                    </div>
                    Needs Attention
                    <span className="ml-auto px-2.5 py-1 bg-amber-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-amber-500/20">
                      {conflicts.length}
                    </span>
                  </h3>
                </div>
                <div className="divide-y divide-steel-100 max-h-52 overflow-y-auto">
                  {conflicts.map((conflict, index) => {
                    const isUrgent = conflict.type === 'urgent';
                    // Extract date from message if available
                    const dateMatch = conflict.message.match(/(\d{2})\.(\d{2})\.(\d{4})/);
                    let travelDate = null;
                    if (dateMatch) {
                      travelDate = new Date(dateMatch[3], dateMatch[2] - 1, dateMatch[1]);
                    }
                    // Extract person name (surname first)
                    const personName = conflict.travels?.[0] ? getDisplayName(conflict.travels[0]) : '';

                    return (
                      <div
                        key={index}
                        className={`flex items-center gap-3 px-4 py-2.5 hover:bg-steel-50 transition-colors ${
                          isUrgent ? 'bg-red-50/50' : ''
                        }`}
                      >
                        {/* Date badge or icon */}
                        {travelDate ? (
                          <div className={`flex flex-col items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 ${
                            isUrgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            <span className="text-lg font-bold leading-none">
                              {travelDate.getDate()}
                            </span>
                            <span className="text-[10px] font-medium uppercase">
                              {travelDate.toLocaleDateString('en-GB', { month: 'short' })}
                            </span>
                          </div>
                        ) : (
                          <div className={`flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 ${
                            isUrgent ? 'bg-red-100' : 'bg-amber-100'
                          }`}>
                            {isUrgent ? (
                              <AlertCircle className="h-5 w-5 text-red-600" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-amber-600" />
                            )}
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 truncate">
                              {personName || 'Travel'}
                            </span>
                            {isUrgent && (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded uppercase">
                                Urgent
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            Needs configuration
                          </p>
                        </div>

                        {/* Action button */}
                        <button
                          onClick={() => {
                            const ids = conflict.travels.map(t => t.id);
                            setSelectedPeople(ids);
                            openAssignModal(ids);
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-colors flex-shrink-0 ${
                            isUrgent
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-amber-500 text-white hover:bg-amber-600'
                          }`}
                        >
                          Fix
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* View Content based on viewMode */}
        {viewMode === 'calendar' ? (
          <CalendarView />
        ) : viewMode === 'timeline' ? (
          <TimelineView />
        ) : viewMode === 'map' ? (
          <FullMapView />
        ) : (
          /* Table View (default) */
          <>
        {/* Single Table with Tabs */}
        <div className="bg-white rounded-xl shadow mb-6">
          {/* Date Filter from Calendar/Timeline click */}
          {filterByDate && (
            <div className="mx-4 mt-3 px-4 py-2.5 bg-brand-50 border border-brand-200 rounded-xl flex items-center justify-between">
              <span className="text-sm text-brand-700">
                Showing travels for: <strong>{filterByDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
              </span>
              <button
                onClick={() => setFilterByDate(null)}
                className="px-3 py-1 text-sm text-brand-600 hover:text-brand-800 hover:bg-brand-100 rounded-xl flex items-center transition-colors"
              >
                <X className="h-4 w-4 mr-1" />
                Clear filter
              </button>
            </div>
          )}
          {/* Filters */}
          <div className="px-4 py-3 border-b border-steel-100 bg-white">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {/* Status Filter (multi-select) */}
              <div className="flex items-center space-x-1 bg-steel-50 px-3 py-2 rounded-xl border border-steel-200">
                <label className="font-medium text-slate-600 text-xs uppercase tracking-wide mr-1">Status:</label>
                <button
                  onClick={() => setFilterByStatus([])}
                  className={`px-2.5 py-1 text-sm rounded transition-colors ${
                    filterByStatus.length === 0
                      ? 'bg-brand-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-steel-100 border border-steel-200'
                  }`}
                >
                  All
                </button>
                {[
                  { value: 'pending', label: 'Pending' },
                  { value: 'draft', label: 'Draft' },
                  { value: 'sent', label: 'Sent' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setFilterByStatus(prev => {
                        if (prev.includes(opt.value)) {
                          // Remove if already selected
                          const newFilter = prev.filter(v => v !== opt.value);
                          return newFilter;
                        } else {
                          // Add to selection
                          return [...prev, opt.value];
                        }
                      });
                    }}
                    className={`px-2.5 py-1 text-sm rounded transition-colors ${
                      filterByStatus.includes(opt.value)
                        ? 'bg-brand-500 text-white'
                        : 'bg-white text-slate-600 hover:bg-steel-100 border border-steel-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Date Filter */}
              <div className="flex items-center space-x-2 bg-steel-50 px-3 py-2 rounded-xl border border-steel-200">
                <label className="font-medium text-slate-600 text-xs uppercase tracking-wide">Date:</label>
                <input
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                  className="px-2 py-1 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                />
                <span className="text-slate-400 font-medium">→</span>
                <input
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                  className="px-2 py-1 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                />
                {(dateFilter.startDate || dateFilter.endDate) && (
                  <button
                    onClick={() => setDateFilter({ startDate: '', endDate: '' })}
                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Project Filter */}
              <div className="flex items-center space-x-2 bg-steel-50 px-3 py-2 rounded-xl border border-steel-200">
                <label className="font-medium text-slate-600 text-xs uppercase tracking-wide">Project:</label>
                <select
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="px-3 py-1 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white min-w-[250px]"
                >
                  <option value="">All Projects</option>
                  {projects.filter(p => p.is_active !== false).map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {projectFilter && (
                  <button
                    onClick={() => setProjectFilter('')}
                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Person Search */}
              <div className="flex items-center space-x-2 bg-steel-50 px-3 py-2 rounded-xl border border-steel-200">
                <label className="font-medium text-slate-600 text-xs uppercase tracking-wide">Search:</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Name..."
                    className="px-3 py-1 pl-8 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white w-44"
                  />
                  <Users className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Header */}
          <div className="bg-steel-50 border-b border-steel-200">
            <div className="flex items-center justify-between px-4 py-2">
              {/* Left side - Tabs */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => {
                    setActiveTab('check_in');
                    setSelectedPeople([]);
                  }}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
                    activeTab === 'check_in'
                      ? 'bg-yellow-500 text-white shadow-md'
                      : 'bg-white text-slate-600 hover:bg-yellow-50 hover:text-yellow-700 border border-steel-200'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Check-In</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === 'check_in' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {allPeople.filter(p => p.travel_type === 'check_in' && getTravelStatus(p) !== 'sent').length}
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setActiveTab('check_out');
                    setSelectedPeople([]);
                  }}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
                    activeTab === 'check_out'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-white text-slate-600 hover:bg-orange-50 hover:text-orange-700 border border-steel-200'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Check-Out</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === 'check_out' ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {allPeople.filter(p => p.travel_type === 'check_out' && getTravelStatus(p) !== 'sent').length}
                    </span>
                  </div>
                </button>
              </div>

              {/* Right side - Actions */}
              <div className="flex items-center space-x-3">
                {/* Selection info badge */}
                {selectedPeople.length > 0 && (
                  <span className="px-3 py-1 bg-brand-100 text-brand-800 rounded-full text-sm font-medium">
                    {selectedPeople.length} selected
                  </span>
                )}

                {/* Action buttons when selected */}
                {selectedPeople.length > 0 && (
                  <>
                    {/* Show "Send" button if all selected are drafts */}
                    {(() => {
                      const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id));
                      const allAreDrafts = selectedTravels.every(t => getTravelStatus(t) === 'draft');

                      if (allAreDrafts) {
                        return (
                          <button
                            onClick={handleMarkAsPlanned}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium flex items-center space-x-2 shadow-sm transition-colors"
                          >
                            <Save className="h-4 w-4" />
                            <span>Send</span>
                          </button>
                        );
                      }
                      return null;
                    })()}
                    <button
                      onClick={() => openAssignModal()}
                      className="px-3 py-1.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 text-sm font-medium flex items-center space-x-2 shadow-sm transition-colors"
                    >
                      <Users className="h-4 w-4" />
                      <span>Assign</span>
                    </button>
                  </>
                )}

                {/* Divider */}
                <div className="h-6 w-px bg-steel-300"></div>

                {/* Select All / Deselect button */}
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1.5 bg-white border border-steel-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-steel-50 hover:border-steel-400 transition-colors"
                >
                  {getFilteredPeople().length > 0 && getFilteredPeople().every(p => selectedPeople.includes(p.id)) ? 'Deselect All' : 'Select All'}
                </button>

                {/* Clear button */}
                {selectedPeople.length > 0 && (
                  <button
                    onClick={() => setSelectedPeople([])}
                    className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-xl text-sm font-medium text-red-600 hover:bg-red-100 hover:border-red-300 transition-colors flex items-center space-x-1"
                  >
                    <X className="h-4 w-4" />
                    <span>Clear</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-auto">
            {getFilteredPeople().length === 0 ? (
              <div className="p-4 text-center text-slate-500 text-sm">
                No {activeTab === 'check_in' ? 'check-ins' : 'check-outs'} scheduled
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-brand-50 to-steel-50 border-b-2 border-brand-500">
                  <tr>
                    <th className="w-8 px-2 py-3"></th>
                    <th
                      className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-white/50 select-none"
                      onClick={() => handleSort('person')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Person</span>
                        <SortIcon columnKey="person" />
                      </div>
                    </th>
                    <th
                      className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-white/50 select-none"
                      onClick={() => handleSort('start_date')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Date</span>
                        <SortIcon columnKey="start_date" />
                      </div>
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Home Address</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide min-w-[160px]">Project</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Status</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Destination</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Transport</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Driver</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Notes</th>
                    <th className="px-2 py-3 text-left text-xs font-black text-slate-700 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {getSortedPeople().map(travel => (
                    <tr
                      key={travel.id}
                      onClick={() => togglePersonSelection(travel.id)}
                      onMouseEnter={() => setHoveredTravelId(travel.id)}
                      onMouseLeave={() => setHoveredTravelId(null)}
                      className={`border-b border-steel-100 last:border-b-0 hover:bg-brand-50/30 cursor-pointer transition-colors ${selectedPeople.includes(travel.id) ? 'bg-brand-50 hover:bg-brand-100' : ''} ${hoveredTravelId === travel.id ? 'ring-1 ring-brand-300' : ''}`}
                    >
                      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedPeople.includes(travel.id)}
                          onChange={() => togglePersonSelection(travel.id)}
                          className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center space-x-2">
                          {usersData[travel.user_id]?.profile_picture ? (
                            <img
                              src={usersData[travel.user_id].profile_picture}
                              alt=""
                              className="h-7 w-7 rounded-full object-cover border border-steel-200"
                            />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-steel-200 flex items-center justify-center text-slate-500 text-xs font-medium border border-steel-300">
                              {getUserName(travel).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openPersonHistory(travel.user_id, getUserName(travel));
                            }}
                            className="text-slate-900 font-medium hover:text-brand-600 hover:underline transition-colors text-left"
                            title="View travel history"
                          >
                            {getDisplayName(travel)}
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">
                        {(() => {
                          const date = new Date(travel.start_date);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          return `${day}.${month}`;
                        })()}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600 truncate max-w-[150px]" title={usersData[travel.user_id]?.personal_address || ''}>
                        {usersData[travel.user_id]?.personal_address || <span className="text-slate-400">-</span>}
                      </td>
                      <td className="px-2 py-1.5 min-w-[160px]">
                        {(() => {
                          // Try to extract project name from address first
                          let projectName = '';
                          if (travel.address && !isAutoGeneratedAddress(travel.address)) {
                            const projectMatch = travel.address.match(/^(.+?)\s*-\s*/);
                            if (projectMatch) {
                              projectName = projectMatch[1].trim();
                            }
                          }

                          // If no project name from address, try to auto-detect based on dates
                          if (!projectName && assignments.length > 0) {
                            const userId = travel.user_id;
                            const travelDate = new Date(travel.start_date);
                            const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                            const travelType = travel.travel_type;

                            const userAssignments = assignments.filter(a => {
                              const aUserId = typeof a.user === 'object' ? a.user?.id : a.user;
                              return aUserId === userId || a.user_id === userId;
                            });

                            let matchingAssignment = null;
                            if (travelType === 'check_in') {
                              matchingAssignment = userAssignments.find(assignment => {
                                const projectStart = new Date(assignment.start_date);
                                const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
                                const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));
                                return daysBefore >= 0 && daysBefore <= 2;
                              });
                            } else if (travelType === 'check_out') {
                              matchingAssignment = userAssignments.find(assignment => {
                                const projectEnd = new Date(assignment.end_date);
                                const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
                                const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));
                                return daysAfter >= 0 && daysAfter <= 2;
                              });
                            }

                            if (matchingAssignment) {
                              const projectId = matchingAssignment.project_id || matchingAssignment.project;
                              const project = projects.find(p => p.id === projectId);
                              if (project) {
                                projectName = project.name;
                              }
                            }
                          }

                          return projectName ? (
                            <span className="text-slate-900 font-medium" title={projectName}>
                              {projectName}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-1.5">
                        {(() => {
                          const status = getTravelStatus(travel);
                          if (status === 'sent') {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800">
                                Sent
                              </span>
                            );
                          } else if (status === 'draft') {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-brand-100 text-brand-800">
                                Draft
                              </span>
                            );
                          } else {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-steel-100 text-slate-600">
                                Pending
                              </span>
                            );
                          }
                        })()}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600 truncate max-w-[120px]">{getCleanAddress(travel) || '-'}</td>
                      <td className="px-2 py-1.5 text-slate-600">
                        {travel.transport_type && !isAutoGeneratedAddress(travel.address) ? (
                          <div className="flex items-center gap-1">
                            {travel.transport_type === 'plane' ? (
                              <>
                                <Plane className="h-4 w-4" />
                                {(travel.departure_airport || travel.arrival_airport) && (
                                  <span className="text-xs text-brand-600">
                                    {travel.departure_airport || '?'}→{travel.arrival_airport || '?'}
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <Car className="h-4 w-4" />
                                {travel.car?.plate_number && (
                                  <span className="text-xs text-slate-500">
                                    {travel.car.plate_number}
                                  </span>
                                )}
                                {travel.car_id && (() => {
                                  const capacity = getCarCapacity(travel.car_id, travel.start_date);
                                  if (capacity) {
                                    return (
                                      <span className={`text-xs px-1 rounded ${
                                        capacity.isFull ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                      }`}>
                                        {capacity.used}/{capacity.max}
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">
                        {travel.driver_id ? (
                          <span className="text-xs bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded">
                            {travel.driver_name || `User ${travel.driver_id}`}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setNoteModal({
                              show: true,
                              travelId: travel.id,
                              note: travel.planner_note || '',
                              isEditing: !travel.planner_note
                            });
                          }}
                          className={`text-xs ${travel.planner_note ? 'text-brand-600 hover:text-brand-800' : 'text-slate-400 hover:text-brand-600'}`}
                          title={travel.planner_note || 'Add planner note'}
                        >
                          {travel.planner_note ? '📝' : '+ Add'}
                        </button>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // If this person is already selected, use all selected people
                              // Otherwise, find all people in the same travel group (same date + project + type)
                              const isAlreadySelected = selectedPeople.includes(travel.id);
                              let peopleToShow;

                              if (isAlreadySelected && selectedPeople.length > 1) {
                                peopleToShow = selectedPeople;
                              } else {
                                // Find all people in the same group
                                const travelDate = new Date(travel.start_date).toDateString();
                                const travelProjectId = getTravelProjectId(travel);
                                const travelType = travel.travel_type;

                                const sameGroupTravels = allPeople.filter(t => {
                                  const tDate = new Date(t.start_date).toDateString();
                                  const tProjectId = getTravelProjectId(t);
                                  return tDate === travelDate &&
                                         tProjectId === travelProjectId &&
                                         t.travel_type === travelType;
                                });

                                peopleToShow = sameGroupTravels.length > 1
                                  ? sameGroupTravels.map(t => t.id)
                                  : [travel.id];
                              }

                              setSelectedPeople(peopleToShow);
                              openAssignModal(peopleToShow);
                            }}
                            className="p-1 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded"
                            title="View / Edit"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {(travel.attachment_flight || travel.attachment_ferry) && (
                            <a
                              href={travel.attachment_flight || travel.attachment_ferry}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                              title="View ticket"
                            >
                              <Ticket className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
          </>
        )}

        {/* Planner Note Modal */}
        {noteModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setNoteModal({ show: false, travelId: null, note: '', isEditing: false })}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="px-4 py-3 border-b border-steel-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  📝 {noteModal.isEditing ? (noteModal.note ? 'Edit Planner Note' : 'Add Planner Note') : 'Planner Note'}
                </h3>
                <button
                  onClick={() => setNoteModal({ show: false, travelId: null, note: '', isEditing: false })}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4">
                {noteModal.isEditing ? (
                  <textarea
                    value={noteModal.note}
                    onChange={(e) => setNoteModal(prev => ({ ...prev, note: e.target.value }))}
                    className="w-full h-32 p-3 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                    placeholder="Enter note..."
                    autoFocus
                  />
                ) : (
                  <div className="p-3 bg-steel-50 rounded-xl min-h-[80px] whitespace-pre-wrap text-slate-700">
                    {noteModal.note || <span className="text-slate-400 italic">No note</span>}
                  </div>
                )}
              </div>
              <div className="px-4 py-3 border-t border-steel-200 flex justify-end gap-2">
                {noteModal.isEditing ? (
                  <>
                    <button
                      onClick={() => setNoteModal({ show: false, travelId: null, note: '', isEditing: false })}
                      className="px-4 py-2 text-slate-700 bg-steel-100 rounded-xl hover:bg-steel-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSaveNote(noteModal.travelId, noteModal.note)}
                      className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600"
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setNoteModal(prev => ({ ...prev, isEditing: true }))}
                      className="px-4 py-2 text-slate-700 bg-steel-100 rounded-xl hover:bg-steel-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setNoteModal({ show: false, travelId: null, note: '', isEditing: false })}
                      className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600"
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Assignment Modal */}
        {showModal && selectedPeople.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-[1700px] w-full h-[95vh] flex flex-col">
              <div className="px-6 py-3 border-b border-steel-200 flex items-center justify-between flex-shrink-0 bg-white">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-brand-600" />
                    Assign Details to {selectedPeople.length} Selected {selectedPeople.length === 1 ? 'Person' : 'People'}
                  </h3>
                  {/* Show current project name */}
                  {(() => {
                    const firstSelected = allPeople.find(t => selectedPeople.includes(t.id));
                    if (!firstSelected) return null;

                    // Check direct properties first
                    let projectName = firstSelected.project_name || firstSelected.project?.name;

                    // If not found, check assignments (same logic as auto-detect)
                    if (!projectName) {
                      const travelDate = new Date(firstSelected.start_date);
                      const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                      const userAssignments = assignments.filter(a => a.user_id === firstSelected.user_id);

                      let matchingAssignment = null;
                      if (firstSelected.travel_type === 'check_in') {
                        matchingAssignment = userAssignments.find(a => {
                          const projectStart = new Date(a.start_date);
                          const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
                          const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));
                          return daysBefore >= 0 && daysBefore <= 2;
                        });
                      } else if (firstSelected.travel_type === 'check_out') {
                        matchingAssignment = userAssignments.find(a => {
                          const projectEnd = new Date(a.end_date);
                          const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
                          const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));
                          return daysAfter >= 0 && daysAfter <= 2;
                        });
                      }

                      if (matchingAssignment) {
                        const projectId = matchingAssignment.project_id || matchingAssignment.project;
                        const project = projects.find(p => p.id === projectId);
                        projectName = project?.name;
                      }
                    }

                    return projectName ? (
                      <span className="px-2 py-1 bg-brand-100 text-brand-700 text-sm rounded-xl font-medium">
                        {projectName}
                      </span>
                    ) : null;
                  })()}
                  {/* Show queue info if there are more projects */}
                  {projectConfigQueue.length > 0 && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-sm rounded-xl">
                      +{projectConfigQueue.length} more project{projectConfigQueue.length > 1 ? 's' : ''} to configure
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setProjectConfigQueue([]); // Clear queue when closing via X
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex">
                {/* LEFT SIDE - Selected People + Settings */}
                <div className="flex-1 flex flex-col p-4 overflow-hidden">
                  {/* Selected People Section */}
                  <div className="mb-3 p-3 bg-steel-50 rounded-xl border border-steel-200 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-900">Selected People ({selectedPeople.length})</h4>
                    <select
                      onChange={(e) => {
                        const personId = parseInt(e.target.value);
                        if (personId && !selectedPeople.includes(personId)) {
                          setSelectedPeople(prev => [...prev, personId]);
                        }
                        e.target.value = ''; // Reset dropdown
                      }}
                      className="text-sm px-3 py-1.5 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value="">+ Add person...</option>
                      {getFilteredPeople()
                        .filter(t => !selectedPeople.includes(t.id))
                        .map(travel => (
                          <option key={travel.id} value={travel.id}>
                            {getDisplayName(travel)} - {(() => {
                              const date = new Date(travel.start_date);
                              return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
                            })()}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedPeople.map(travelId => {
                      const travel = allPeople.find(t => t.id === travelId);
                      if (!travel) return null;
                      return (
                        <div key={travelId} className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-xl border border-steel-300">
                          <span className="text-sm font-medium text-slate-900">{getDisplayName(travel)}</span>
                          <span className="text-sm text-slate-500">
                            {(() => {
                              const date = new Date(travel.start_date);
                              return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
                            })()}
                          </span>
                          {selectedPeople.length > 1 && (
                            <button
                              onClick={() => setSelectedPeople(prev => prev.filter(id => id !== travelId))}
                              className="text-slate-400 hover:text-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Travel Compatibility Alerts */}
                  {selectedPeople.length > 1 && (() => {
                    const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);

                    // Check dates
                    const dates = [...new Set(selectedTravels.map(t => t.start_date))];
                    const sameDate = dates.length === 1;

                    // Check travel types
                    const types = [...new Set(selectedTravels.map(t => t.travel_type))];
                    const sameType = types.length === 1;

                    // Get projects from addresses or assignments
                    const getProjectForTravel = (travel) => {
                      // 1. Try from address
                      if (travel.address && !isAutoGeneratedAddress(travel.address)) {
                        const match = travel.address.match(/^(.+?)\s*-\s*/);
                        if (match) return match[1].trim();
                      }

                      // 2. Try from assignments with flexible date matching
                      const userId = travel.user_id;
                      const travelDate = new Date(travel.start_date);
                      const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
                      const userAssigns = assignments.filter(a => {
                        const aUserId = typeof a.user === 'object' ? a.user?.id : a.user;
                        return aUserId === userId || a.user_id === userId;
                      });

                      // For check_in: look for projects starting around this date
                      // For check_out: look for projects ending around this date
                      const matching = userAssigns.find(a => {
                        const start = new Date(a.start_date);
                        const end = new Date(a.end_date);
                        const startOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                        const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

                        if (travel.travel_type === 'check_in') {
                          // Check-in: travel date should be 0-2 days before project start
                          const daysBefore = Math.floor((startOnly - travelDateOnly) / (1000 * 60 * 60 * 24));
                          return daysBefore >= 0 && daysBefore <= 2;
                        } else {
                          // Check-out: travel date should be 0-2 days after project end
                          const daysAfter = Math.floor((travelDateOnly - endOnly) / (1000 * 60 * 60 * 24));
                          return daysAfter >= 0 && daysAfter <= 2;
                        }
                      });

                      if (matching) {
                        const proj = projects.find(p => p.id === (matching.project_id || matching.project));
                        if (proj) return proj.name;
                      }

                      // 3. Fallback: try to find any project for this user in nearby dates
                      const nearbyAssign = userAssigns.find(a => {
                        const start = new Date(a.start_date);
                        const end = new Date(a.end_date);
                        const daysDiff = Math.abs((travelDate - start) / (1000 * 60 * 60 * 24));
                        return daysDiff <= 7; // Within a week
                      });

                      if (nearbyAssign) {
                        const proj = projects.find(p => p.id === (nearbyAssign.project_id || nearbyAssign.project));
                        if (proj) return proj.name;
                      }

                      return null;
                    };

                    const projectNames = selectedTravels.map(t => getProjectForTravel(t)).filter(Boolean);
                    const uniqueProjects = [...new Set(projectNames)];
                    const sameProject = uniqueProjects.length <= 1;
                    const hasProject = uniqueProjects.length > 0;

                    const allGood = sameDate && sameType && sameProject;

                    return (
                      <div className={`mb-3 p-2 rounded-xl border flex-shrink-0 ${allGood ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex flex-wrap gap-3 text-xs">
                          {/* Date check */}
                          <div className={`flex items-center gap-1 ${sameDate ? 'text-green-700' : 'text-amber-700'}`}>
                            {sameDate ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                            <span>
                              {sameDate
                                ? `Same date: ${new Date(dates[0]).toLocaleDateString('pl-PL')}`
                                : `Different dates! (${dates.length})`
                              }
                            </span>
                          </div>

                          {/* Type check */}
                          <div className={`flex items-center gap-1 ${sameType ? 'text-green-700' : 'text-amber-700'}`}>
                            {sameType ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                            <span>
                              {sameType
                                ? `All ${types[0] === 'check_in' ? 'Check-in' : 'Check-out'}`
                                : 'Mixed check-in/out!'
                              }
                            </span>
                          </div>

                          {/* Project check */}
                          <div className={`flex items-center gap-1 ${sameProject ? 'text-green-700' : 'text-amber-700'}`}>
                            {sameProject ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                            <span>
                              {hasProject
                                ? (sameProject
                                    ? `Same project: ${uniqueProjects[0]}`
                                    : `Different projects! (${uniqueProjects.join(', ')})`
                                  )
                                : 'Projects unknown'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                  {/* Two Column Layout for Settings */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">

                    {/* LEFT COLUMN - Main Settings */}
                  <div className="space-y-4 overflow-y-auto">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-3 pb-2 border-b flex items-center">
                        <Users className="h-4 w-4 mr-2 text-brand-600" />
                        General Settings
                      </h4>

                      {/* Transport Type */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Transport Type *
                        </label>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, transportType: 'car' }))}
                            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border-2 transition-colors ${
                              formData.transportType === 'car'
                                ? 'border-brand-600 bg-brand-50 text-brand-700'
                                : 'border-steel-300 hover:border-steel-400'
                            }`}
                          >
                            <Car className="h-5 w-5" />
                            <span className="font-medium">Car</span>
                          </button>
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, transportType: 'plane' }))}
                            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border-2 transition-colors ${
                              formData.transportType === 'plane'
                                ? 'border-brand-600 bg-brand-50 text-brand-700'
                                : 'border-steel-300 hover:border-steel-400'
                            }`}
                          >
                            <Plane className="h-5 w-5" />
                            <span className="font-medium">Plane</span>
                          </button>
                        </div>
                      </div>

                      {/* Car Selection */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Car {formData.transportType === 'plane' ? '(optional)' : ''}
                        </label>
                        <select
                          value={formData.selectedCar}
                          onChange={(e) => setFormData(prev => ({ ...prev, selectedCar: e.target.value, driverId: '' }))}
                          className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        >
                          <option value="">Choose a car...</option>
                          <option value="own_car" className="font-medium text-brand-600">🚙 Own Car (private vehicle)</option>
                          <optgroup label="Company Cars">
                            {availableCars.map(car => (
                              <option key={car.id} value={car.id}>
                                {car.car_brand} {car.car_model} ({car.plate_number})
                              </option>
                            ))}
                          </optgroup>
                        </select>
                        {formData.selectedCar === 'own_car' && (
                          <p className="mt-1 text-sm text-brand-600">Employee will use their own private vehicle</p>
                        )}
                        {availableCars.length === 0 && selectedPeople.length > 0 && formData.selectedCar !== 'own_car' && (
                          <p className="mt-1 text-sm text-amber-600">No company cars available for the selected dates</p>
                        )}
                        {formData.transportType === 'plane' && availableCars.length > 0 && (
                          <p className="mt-1 text-sm text-slate-500">Select if company provides airport drop-off</p>
                        )}
                      </div>

                      {/* Driver Selection - only show when company car is selected (not for own car) */}
                      {formData.selectedCar && formData.selectedCar !== 'own_car' && formData.transportType === 'car' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Driver (optional)
                          </label>
                          <select
                            value={formData.driverId}
                            onChange={(e) => setFormData(prev => ({ ...prev, driverId: e.target.value }))}
                            className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          >
                            <option value="">Select driver...</option>
                            {selectedPeople.map(id => {
                              const travel = allPeople.find(t => t.id === id);
                              if (!travel) return null;
                              return (
                                <option key={id} value={travel.user_id}>
                                  {getUserName(travel)}
                                </option>
                              );
                            })}
                          </select>
                          <p className="mt-1 text-xs text-slate-500">
                            Who will drive this car?
                          </p>
                        </div>
                      )}

                      {/* Project Selection */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Project (optional)
                        </label>
                        <select
                          value={formData.projectId}
                          onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value }))}
                          className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        >
                          <option value="">Choose a project...</option>
                          {projects.filter(p => p.is_active !== false).map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                        {formData.projectId && (
                          <div className="mt-2 p-2 bg-brand-50 rounded text-sm text-brand-700">
                            Project address will be automatically added
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Notes (optional)
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                          rows={4}
                          className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          placeholder="Add any additional notes or instructions..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* MIDDLE COLUMN - Transport Details & Tickets */}
                  <div className="space-y-4 overflow-y-auto">

                    {/* Flight-specific fields */}
                    {formData.transportType === 'plane' && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b flex items-center">
                          <Plane className="h-4 w-4 mr-2 text-brand-600" />
                          Flight Details
                        </h4>
                        <div className="space-y-4">
                          {/* Airport Selection */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-brand-700 mb-2">
                                🛫 Departure Airport
                              </label>
                              <select
                                value={formData.departureAirport}
                                onChange={(e) => setFormData(prev => ({ ...prev, departureAirport: e.target.value }))}
                                className="w-full px-3 py-2 border border-brand-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              >
                                <option value="">-- Select departure --</option>
                                <optgroup label="Poland">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'Poland').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Germany">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'Germany').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="France">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'France').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="UK">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'UK').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Other">
                                  {Object.entries(AIRPORTS).filter(([, a]) => !['Poland', 'Germany', 'France', 'UK'].includes(a.country)).map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name} ({airport.country})</option>
                                  ))}
                                </optgroup>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-red-700 mb-2">
                                🛬 Destination Airport
                              </label>
                              <select
                                value={formData.destinationAirport}
                                onChange={(e) => setFormData(prev => ({ ...prev, destinationAirport: e.target.value }))}
                                className="w-full px-3 py-2 border border-red-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                              >
                                <option value="">-- Select destination --</option>
                                <optgroup label="Poland">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'Poland').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Germany">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'Germany').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="France">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'France').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="UK">
                                  {Object.entries(AIRPORTS).filter(([, a]) => a.country === 'UK').map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Other">
                                  {Object.entries(AIRPORTS).filter(([, a]) => !['Poland', 'Germany', 'France', 'UK'].includes(a.country)).map(([code, airport]) => (
                                    <option key={code} value={code}>{code} - {airport.name} ({airport.country})</option>
                                  ))}
                                </optgroup>
                              </select>
                            </div>
                          </div>

                          {/* Flight Route Preview */}
                          {formData.departureAirport && formData.destinationAirport && (
                            <div className="bg-brand-50 rounded-xl p-3 border border-brand-200">
                              <div className="flex items-center justify-center gap-2 text-sm">
                                <span className="font-bold text-brand-700">{formData.departureAirport}</span>
                                <span className="text-brand-400">→</span>
                                <Plane className="h-4 w-4 text-brand-600" />
                                <span className="text-brand-400">→</span>
                                <span className="font-bold text-red-700">{formData.destinationAirport}</span>
                              </div>
                              <div className="text-xs text-center text-slate-500 mt-1">
                                {AIRPORTS[formData.departureAirport]?.city} → {AIRPORTS[formData.destinationAirport]?.city}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">
                                Flight Number
                              </label>
                              <input
                                type="text"
                                value={formData.flightNumber}
                                onChange={(e) => setFormData(prev => ({ ...prev, flightNumber: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                placeholder="LO123"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">
                                Departure
                              </label>
                              <input
                                type="time"
                                value={formData.departureTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, departureTime: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">
                                Arrival
                              </label>
                              <input
                                type="time"
                                value={formData.arrivalTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, arrivalTime: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                              />
                            </div>
                          </div>

                          {/* Organized Transport Section */}
                          <div className="mt-4 pt-4 border-t border-steel-200">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.organizedTransport}
                                onChange={(e) => setFormData(prev => ({ ...prev, organizedTransport: e.target.checked }))}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                              />
                              <span className="text-sm font-medium text-slate-700">
                                🚐 Organized Transport (external company)
                              </span>
                            </label>

                            {formData.organizedTransport && (
                              <div className="mt-3 p-3 bg-green-50 rounded-xl border border-green-200 space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-green-700 mb-1">
                                    Transport Company
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.transportCompany}
                                    onChange={(e) => setFormData(prev => ({ ...prev, transportCompany: e.target.value }))}
                                    className="w-full px-3 py-2 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    placeholder="e.g. Transfer Express"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-sm font-medium text-green-700 mb-1">
                                      📍 {selectedPeople.length > 0 && allPeople.find(t => t.id === selectedPeople[0])?.travel_type === 'check_in' ? 'Meeting Point' : 'Pickup Point'}
                                    </label>
                                    <select
                                      value={travelLocations.find(loc => loc.address === formData.meetingPoint)?.id || ''}
                                      onChange={(e) => {
                                        if (e.target.value !== '') {
                                          const location = travelLocations.find(loc => loc.id === parseInt(e.target.value));
                                          if (location) {
                                            setFormData(prev => ({ ...prev, meetingPoint: location.address }));
                                          }
                                        }
                                      }}
                                      className="w-full px-2 py-1.5 mb-1 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-xs"
                                    >
                                      <option value="">-- Saved --</option>
                                      {travelLocations
                                        .filter(loc => loc.location_type === 'pickup' || loc.location_type === 'both')
                                        .map(loc => (
                                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                                        ))
                                      }
                                    </select>
                                    <input
                                      type="text"
                                      value={formData.meetingPoint}
                                      onChange={(e) => setFormData(prev => ({ ...prev, meetingPoint: e.target.value }))}
                                      className="w-full px-2 py-1.5 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                      placeholder="Type address..."
                                    />
                                    {formData.meetingPoint && !travelLocations.find(loc => loc.address === formData.meetingPoint) && (
                                      <button
                                        type="button"
                                        onClick={() => setShowAddLocationModal({ show: true, type: 'pickup', defaultValue: formData.meetingPoint })}
                                        className="mt-1 px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                                      >
                                        💾 Save
                                      </button>
                                    )}
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-green-700 mb-1">
                                      🏠 Dropoff Location
                                    </label>
                                    <select
                                      value={travelLocations.find(loc => loc.address === formData.dropoffLocation)?.id || ''}
                                      onChange={(e) => {
                                        if (e.target.value !== '') {
                                          const location = travelLocations.find(loc => loc.id === parseInt(e.target.value));
                                          if (location) {
                                            setFormData(prev => ({ ...prev, dropoffLocation: location.address }));
                                          }
                                        }
                                      }}
                                      className="w-full px-2 py-1.5 mb-1 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-xs"
                                    >
                                      <option value="">-- Saved --</option>
                                      {travelLocations
                                        .filter(loc => loc.location_type === 'dropoff' || loc.location_type === 'both')
                                        .map(loc => (
                                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                                        ))
                                      }
                                    </select>
                                    <input
                                      type="text"
                                      value={formData.dropoffLocation}
                                      onChange={(e) => setFormData(prev => ({ ...prev, dropoffLocation: e.target.value }))}
                                      className="w-full px-2 py-1.5 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                      placeholder="Type address..."
                                    />
                                    {formData.dropoffLocation && !travelLocations.find(loc => loc.address === formData.dropoffLocation) && (
                                      <button
                                        type="button"
                                        onClick={() => setShowAddLocationModal({ show: true, type: 'dropoff', defaultValue: formData.dropoffLocation })}
                                        className="mt-1 px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                                      >
                                        💾 Save
                                      </button>
                                    )}
                                    {(() => {
                                      const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
                                      const isCheckOut = selectedTravels.some(t => t.travel_type === 'check_out');
                                      if (isCheckOut && selectedTravels.length === 1) {
                                        const homeAddress = usersData[selectedTravels[0].user_id]?.personal_address;
                                        if (homeAddress && !formData.dropoffLocation) {
                                          return (
                                            <button
                                              type="button"
                                              onClick={() => setFormData(prev => ({ ...prev, dropoffLocation: homeAddress }))}
                                              className="mt-1 text-xs text-green-600 hover:text-green-800 underline"
                                            >
                                              Home: {homeAddress.substring(0, 20)}...
                                            </button>
                                          );
                                        }
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-green-700 mb-1">
                                    Meeting Time
                                  </label>
                                  <input
                                    type="time"
                                    value={formData.meetingTime}
                                    onChange={(e) => setFormData(prev => ({ ...prev, meetingTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  />
                                </div>
                                {/* Route summary */}
                                {(formData.meetingPoint || formData.dropoffLocation) && (
                                  <div className="p-2 bg-green-100 rounded-xl text-sm text-green-800">
                                    <span className="font-medium">Route: </span>
                                    {formData.destinationAirport && <span>{formData.destinationAirport}</span>}
                                    {formData.meetingPoint && <span> → {formData.meetingPoint}</span>}
                                    {formData.dropoffLocation && <span> → {formData.dropoffLocation}</span>}
                                    <span className="text-xs text-green-600 ml-2">(see map →)</span>
                                  </div>
                                )}

                                {/* Distance from dropoff to home (when different) */}
                                {dropoffToHomeLoading && (
                                  <div className="p-2 bg-yellow-50 rounded-xl text-sm text-yellow-700 flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Calculating distance to home...
                                  </div>
                                )}
                                {dropoffToHomeDistance && !dropoffToHomeLoading && (
                                  <div className="p-3 bg-brand-50 rounded-xl border border-brand-200">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-sm font-medium text-brand-800">
                                          🚗 From dropoff to home
                                        </div>
                                        <div className="text-lg font-bold text-brand-900">
                                          {dropoffToHomeDistance.km} km • ~{dropoffToHomeDistance.time}
                                        </div>
                                      </div>
                                      <a
                                        href={dropoffToHomeDistance.mapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-1.5 bg-brand-500 text-white text-sm rounded-xl hover:bg-brand-600 flex items-center gap-1"
                                      >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                                        </svg>
                                        Map
                                      </a>
                                    </div>
                                    <div className="text-xs text-brand-600 mt-1">
                                      {formData.dropoffLocation} → Home address
                                    </div>
                                  </div>
                                )}
                                {/* Show home address info when dropoff is set but no distance calculated yet */}
                                {formData.dropoffLocation && !dropoffToHomeDistance && !dropoffToHomeLoading && (() => {
                                  const selectedTravels = selectedPeople.map(id => allPeople.find(t => t.id === id)).filter(Boolean);
                                  const homeAddress = selectedTravels.length > 0 ? usersData[selectedTravels[0].user_id]?.personal_address : null;
                                  if (homeAddress && formData.dropoffLocation.trim().toLowerCase() === homeAddress.trim().toLowerCase()) {
                                    return (
                                      <div className="p-2 bg-green-50 rounded-xl text-xs text-green-700 flex items-center gap-1">
                                        <span>✓</span>
                                        <span>Dropoff is employee's home address</span>
                                      </div>
                                    );
                                  }
                                  if (!homeAddress) {
                                    return (
                                      <div className="p-2 bg-yellow-50 rounded-xl text-xs text-yellow-700 flex items-center gap-1">
                                        <span>⚠️</span>
                                        <span>Employee has no home address set in profile</span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}

                                <div className="text-xs text-green-600 flex items-center gap-1">
                                  <span>ℹ️</span>
                                  <span>This information will be included in the email to employees</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Car-specific section */}
                    {formData.transportType === 'car' && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b flex items-center">
                          <Car className="h-4 w-4 mr-2 text-brand-600" />
                          Car Travel Details
                        </h4>
                        <div className="p-3 bg-brand-50 rounded-xl">
                          <p className="text-sm text-slate-700">
                            If traveling by ferry, you can upload ferry tickets for each person below.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Ticket Upload Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-4 pb-2 border-b flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-brand-600" />
                        {formData.transportType === 'plane' ? 'Flight Tickets' : 'Ferry Tickets (optional)'}
                      </h4>
                      <div className="space-y-3">
                        {selectedPeople.map(travelId => {
                          const travel = allPeople.find(t => t.id === travelId);
                          const existingAttachment = formData.transportType === 'plane'
                            ? travel?.attachment_flight
                            : travel?.attachment_ferry;
                          const newFiles = ticketFiles[travelId] || [];
                          return (
                            <div key={travelId} className="p-3 bg-steel-50 rounded-xl">
                              <div className="mb-2">
                                <span className="text-sm font-medium text-slate-900">{getUserName(travel)}</span>
                              </div>
                              {/* Show existing attachment */}
                              {existingAttachment && (
                                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-xl">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-green-700 flex items-center">
                                      <FileText className="h-4 w-4 mr-1" />
                                      Existing ticket
                                    </span>
                                    <a
                                      href={existingAttachment}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-brand-600 hover:text-brand-800 underline"
                                    >
                                      View
                                    </a>
                                  </div>
                                </div>
                              )}
                              {/* Show new files */}
                              {newFiles.length > 0 && (
                                <div className="mb-2 space-y-1">
                                  {newFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-brand-50 border border-brand-200 rounded text-sm">
                                      <span className="text-brand-700 truncate flex-1">{file.name}</span>
                                      <button
                                        onClick={() => removeFile(travelId, idx)}
                                        className="ml-2 text-red-500 hover:text-red-700"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <input
                                  type="file"
                                  id={`ticket-${travelId}`}
                                  onChange={(e) => handleFileChange(travelId, e.target.files)}
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  className="hidden"
                                  multiple
                                />
                                <label
                                  htmlFor={`ticket-${travelId}`}
                                  className="cursor-pointer flex-1 px-3 py-2 text-sm text-center bg-white border border-steel-300 rounded-xl hover:bg-steel-50 transition-colors"
                                >
                                  + Add File{newFiles.length > 0 ? 's' : ''}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                {/* RIGHT SIDE - Map with Pickup Route Builder */}
                <div className="w-[600px] flex-shrink-0 border-l border-steel-200 bg-steel-50 flex flex-col">
                  {/* Map */}
                  <div className="flex-1">
                    <TravelMapPanel
                      selectedPeople={selectedPeople}
                      allPeople={allPeople}
                      project={formData.projectId ? projects.find(p => p.id === parseInt(formData.projectId)) : null}
                      getUserName={getUserName}
                      transportType={formData.transportType}
                      usersData={usersData}
                      departureTime={formData.departureTime}
                      departureAirport={formData.departureAirport}
                      destinationAirport={formData.destinationAirport}
                      onSetPickupDriver={handleSetPickupDriver}
                      onClearPickupDriver={handleClearPickupDriver}
                      // Organized transport route props
                      organizedTransport={formData.organizedTransport}
                      pickupPoint={formData.meetingPoint}
                      dropoffLocation={formData.dropoffLocation}
                      // Travel type (check_in or check_out)
                      travelType={selectedPeople.length > 0 ? allPeople.find(t => t.id === selectedPeople[0])?.travel_type : null}
                    />
                  </div>
                </div>
              </div>

              {/* Footer with buttons */}
              <div className="flex justify-between px-4 py-3 border-t border-steel-200 flex-shrink-0 bg-steel-50">
                <button
                  onClick={() => setShowEmailPreview(true)}
                  className="flex items-center space-x-2 px-4 py-2 text-brand-700 border border-brand-300 rounded-xl hover:bg-brand-50"
                  title="Preview email before sending"
                >
                  <AlertCircle className="h-4 w-4" />
                  <span>Preview Email</span>
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedPeople([]);
                      setProjectConfigQueue([]); // Clear queue on cancel
                    }}
                    className="px-4 py-2 text-slate-600 border border-steel-300 rounded-xl hover:bg-steel-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await handleSaveAssignments(true);
                      setShowModal(false);
                      // Check if there are more projects to configure
                      setTimeout(() => processNextProjectInQueue(), 300);
                    }}
                    className="flex items-center space-x-2 px-5 py-2 bg-brand-100 text-brand-700 border border-brand-300 rounded-xl hover:bg-brand-200"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Save as Draft{projectConfigQueue.length > 0 ? ` (${projectConfigQueue.length} more)` : ''}</span>
                  </button>
                  <button
                    onClick={async () => {
                      await handleSaveAssignments();
                      setShowModal(false);
                      // Check if there are more projects to configure
                      setTimeout(() => processNextProjectInQueue(), 300);
                    }}
                    className="flex items-center space-x-2 px-5 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600"
                  >
                    <Save className="h-4 w-4" />
                    <span>Save & Send{projectConfigQueue.length > 0 ? ` (${projectConfigQueue.length} more)` : ''}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
              <div className="px-6 py-4 border-b border-steel-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-brand-600" />
                  Change History
                </h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {changeHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No changes recorded yet</p>
                    <p className="text-sm mt-1">Changes will appear here after you save travel assignments</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {changeHistory.map((entry, index) => (
                      <div key={index} className="bg-steel-50 rounded-xl p-4 border border-steel-200 hover:shadow-sm transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                              entry.action === 'saved_sent' ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-700'
                            }`}>
                              {entry.action === 'saved_sent' ? 'Sent' : 'Draft'}
                            </span>
                            {entry.changedBy && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {entry.changedBy}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-slate-400 font-medium">
                            {entry.timestamp.toLocaleString('pl-PL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="text-sm space-y-2">
                          <div className="flex items-start gap-2">
                            <Users className="h-4 w-4 text-slate-400 mt-0.5" />
                            <div>
                              <span className="font-medium text-slate-700">{entry.peopleCount} {entry.peopleCount === 1 ? 'person' : 'people'}</span>
                              <p className="text-slate-500 text-xs mt-0.5">{entry.people.slice(0, 5).join(', ')}{entry.people.length > 5 ? ` +${entry.people.length - 5} more` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-slate-600 pl-6">
                            {entry.transportType && (
                              <span className="flex items-center gap-1.5 bg-steel-100 px-2 py-1 rounded-xl text-xs">
                                {entry.transportType === 'plane' ? <Plane className="h-3.5 w-3.5" /> : <Car className="h-3.5 w-3.5" />}
                                {entry.transportType === 'plane' ? 'Plane' : 'Car'}
                              </span>
                            )}
                            {entry.car && (
                              <span className="text-xs bg-steel-100 px-2 py-1 rounded-xl">{entry.car}</span>
                            )}
                            {entry.project && (
                              <span className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded-xl font-medium">{entry.project}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-6 py-3 border-t border-steel-200 flex justify-end">
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="px-4 py-2 bg-steel-100 text-slate-700 rounded-xl hover:bg-steel-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Person Travel History Modal - shadcn style */}
        {personHistoryModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[85vh] flex flex-col border border-steel-200">
              {/* Header */}
              <div className="px-4 py-3 border-b border-steel-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {usersData[personHistoryModal.userId]?.profile_picture ? (
                    <img
                      src={usersData[personHistoryModal.userId].profile_picture}
                      alt=""
                      className="h-9 w-9 rounded-full object-cover border border-steel-200"
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-steel-100 flex items-center justify-center text-slate-600 text-sm font-medium">
                      {personHistoryModal.userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-slate-900">{personHistoryModal.userName}</h3>
                    <p className="text-xs text-slate-500">Travel history</p>
                  </div>
                </div>
                <button
                  onClick={() => setPersonHistoryModal({ show: false, userId: null, userName: '' })}
                  className="h-8 w-8 flex items-center justify-center rounded-xl hover:bg-steel-100 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {(() => {
                  const travels = getPersonTravelHistory(personHistoryModal.userId);
                  if (travels.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                        <Plane className="h-8 w-8 mb-2" />
                        <p className="text-sm">No travel history</p>
                      </div>
                    );
                  }

                  const checkIns = travels.filter(t => t.travel_type === 'check_in').length;
                  const checkOuts = travels.filter(t => t.travel_type === 'check_out').length;
                  const flights = travels.filter(t => t.transport_type === 'plane').length;

                  return (
                    <div>
                      {/* Stats row */}
                      <div className="px-4 py-3 border-b border-steel-100 flex gap-6 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-400">Total</span>
                          <span className="font-medium text-slate-900">{travels.length}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-400">In</span>
                          <span className="font-medium text-slate-900">{checkIns}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-400">Out</span>
                          <span className="font-medium text-slate-900">{checkOuts}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Plane className="h-3 w-3 text-slate-400" />
                          <span className="font-medium text-slate-900">{flights}</span>
                        </div>
                      </div>

                      {/* Travel list */}
                      <div className="divide-y divide-steel-100">
                        {travels.map((travel, idx) => {
                          const isPast = new Date(travel.start_date) < new Date();
                          const status = getTravelStatus(travel);
                          const isCheckIn = travel.travel_type === 'check_in';

                          return (
                            <div
                              key={travel.id || idx}
                              className={`px-4 py-3 flex items-start gap-3 hover:bg-steel-50 transition-colors ${isPast ? 'opacity-60' : ''}`}
                            >
                              {/* Date column */}
                              <div className="w-14 flex-shrink-0 text-right">
                                <div className="text-sm font-medium text-slate-900">
                                  {new Date(travel.start_date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {new Date(travel.start_date).getFullYear()}
                                </div>
                              </div>

                              {/* Timeline dot */}
                              <div className="flex flex-col items-center pt-1">
                                <div className={`h-2 w-2 rounded-full ${isCheckIn ? 'bg-green-500' : 'bg-orange-500'}`} />
                                <div className="w-px h-full bg-steel-200 mt-1" />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0 pb-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-sm font-medium ${isCheckIn ? 'text-green-700' : 'text-orange-700'}`}>
                                    {isCheckIn ? 'Check-in' : 'Check-out'}
                                  </span>
                                  {travel.transport_type && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-steel-200 text-xs text-slate-600 bg-white">
                                      {travel.transport_type === 'plane' ? <Plane className="h-3 w-3" /> : <Car className="h-3 w-3" />}
                                      {travel.transport_type === 'plane' ? 'Flight' : 'Car'}
                                    </span>
                                  )}
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    status === 'sent' ? 'bg-green-50 text-green-600 border border-green-200' :
                                    status === 'draft' ? 'bg-brand-50 text-brand-600 border border-brand-200' :
                                    'bg-steel-50 text-slate-500 border border-steel-200'
                                  }`}>
                                    {status}
                                  </span>
                                </div>

                                {/* Flight route */}
                                {travel.transport_type === 'plane' && (travel.departure_airport || travel.arrival_airport) && (
                                  <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                                    <span className="font-mono">{travel.departure_airport || '—'}</span>
                                    <ArrowRight className="h-3 w-3" />
                                    <span className="font-mono">{travel.arrival_airport || '—'}</span>
                                    {travel.flight_number && (
                                      <span className="ml-1 px-1 bg-steel-100 rounded font-mono">{travel.flight_number}</span>
                                    )}
                                  </div>
                                )}

                                {/* Project/Address */}
                                {travel.address && (
                                  <div className="mt-1 text-xs text-slate-400 truncate">
                                    {travel.address}
                                  </div>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {travel.attachment_flight && (
                                  <a
                                    href={travel.attachment_flight}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="h-7 w-7 flex items-center justify-center rounded-xl hover:bg-steel-100 text-slate-400 hover:text-slate-600 transition-colors"
                                    title="View ticket"
                                  >
                                    <FileText className="h-3.5 w-3.5" />
                                  </a>
                                )}
                                <button
                                  onClick={() => {
                                    setPersonHistoryModal({ show: false, userId: null, userName: '' });
                                    setSelectedPeople([travel.id]);
                                    setShowModal(true);
                                  }}
                                  className="h-7 w-7 flex items-center justify-center rounded-xl hover:bg-steel-100 text-slate-400 hover:text-slate-600 transition-colors"
                                  title="Configure"
                                >
                                  <Settings className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-steel-200 flex justify-end">
                <button
                  onClick={() => setPersonHistoryModal({ show: false, userId: null, userName: '' })}
                  className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-steel-300 rounded-xl hover:bg-steel-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Preview Modal */}
        {showEmailPreview && selectedPeople.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
              <div className="px-6 py-4 border-b border-steel-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-brand-600" />
                  Email Preview ({selectedPeople.length} {selectedPeople.length === 1 ? 'recipient' : 'recipients'})
                </h3>
                <button
                  onClick={() => setShowEmailPreview(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedPeople.map(id => {
                  const travel = allPeople.find(t => t.id === id);
                  if (!travel) return null;
                  const preview = generateEmailPreview(travel);
                  return (
                    <div key={id} className="border border-steel-200 rounded-xl overflow-hidden">
                      <div className="bg-steel-100 px-4 py-2 border-b border-steel-200">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-800">{getUserName(travel)}</span>
                          <span className="text-sm text-slate-500">{travel.travel_type === 'check_in' ? 'Check-in' : 'Check-out'}</span>
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          <strong>Subject:</strong> {preview.subject}
                        </div>
                      </div>
                      <div className="p-4 bg-white">
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{preview.body}</pre>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-6 py-3 border-t border-steel-200 flex justify-end">
                <button
                  onClick={() => setShowEmailPreview(false)}
                  className="px-4 py-2 bg-steel-100 text-slate-700 rounded-xl hover:bg-steel-200"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attachments Panel Modal */}
        {showAttachmentsPanel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
              <div className="px-6 py-4 border-b border-steel-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-brand-600" />
                  All Attachments
                </h3>
                <button
                  onClick={() => setShowAttachmentsPanel(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {(() => {
                  const travelsWithAttachments = allPeople.filter(t =>
                    t.attachment_flight || t.attachment_ferry
                  );

                  if (travelsWithAttachments.length === 0) {
                    return (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No attachments uploaded yet</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {travelsWithAttachments.map(travel => (
                        <div key={travel.id} className="border border-steel-200 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-slate-800">{getUserName(travel)}</span>
                              <span className="ml-2 text-sm text-slate-500">
                                {new Date(travel.start_date).toLocaleDateString('pl-PL')}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              travel.travel_type === 'check_in' ? 'bg-yellow-100 text-yellow-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {travel.travel_type === 'check_in' ? 'Check-in' : 'Check-out'}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            {travel.attachment_flight && (
                              <a
                                href={travel.attachment_flight}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-brand-50 text-brand-700 rounded hover:bg-brand-100 text-sm"
                              >
                                <Plane className="h-4 w-4" />
                                Flight Ticket
                              </a>
                            )}
                            {travel.attachment_ferry && (
                              <a
                                href={travel.attachment_ferry}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded hover:bg-green-100 text-sm"
                              >
                                <Car className="h-4 w-4" />
                                Ferry Ticket
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="px-6 py-3 border-t border-steel-200 flex justify-end">
                <button
                  onClick={() => setShowAttachmentsPanel(false)}
                  className="px-4 py-2 bg-steel-100 text-slate-700 rounded-xl hover:bg-steel-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ML Prediction Tooltip - Floating bubble on right side - DISABLED FOR NOW */}
        {false && showMlTooltip && mlPrediction && (
          <div
            className="fixed right-4 top-1/4 z-50 animate-in fade-in slide-in-from-right-5 duration-200"
            style={{ maxWidth: '340px' }}
          >
            <div className="bg-white rounded-xl shadow-2xl border border-steel-200 overflow-hidden">
              {/* Header */}
              <div className="bg-brand-500 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-white" />
                  <span className="text-white font-semibold text-sm">Route Analysis</span>
                </div>
                <button
                  onClick={() => setShowMlTooltip(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {mlPredictionLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    <span className="ml-2 text-sm text-slate-500">Analyzing route...</span>
                  </div>
                ) : mlPrediction.error ? (
                  <div className="text-center py-4">
                    <AlertCircle className="h-8 w-8 mx-auto text-orange-400 mb-2" />
                    <p className="text-sm text-slate-600">{mlPrediction.message}</p>
                  </div>
                ) : (
                  <>
                    {/* Route info */}
                    <div className="text-xs text-slate-500 space-y-1">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-3 w-3 mt-0.5 text-green-500 flex-shrink-0" />
                        <span className="line-clamp-1" title={mlPrediction.origin}>{mlPrediction.origin || 'Unknown'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-3 w-3 mt-0.5 text-red-500 flex-shrink-0" />
                        <span className="line-clamp-1" title={mlPrediction.destination}>{mlPrediction.destination || 'Unknown'}</span>
                      </div>
                    </div>

                    {/* Distance badge */}
                    {mlPrediction.distance_km && (
                      <div className="flex items-center justify-center">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-steel-100 text-slate-700 rounded-full text-sm font-medium">
                          {mlPrediction.distance_km} km
                        </span>
                      </div>
                    )}

                    {/* Recommendation */}
                    {mlPrediction.recommended_transport && mlPrediction.recommended_transport !== 'unknown' && (
                      <div className={`p-3 rounded-xl ${
                        mlPrediction.recommended_transport === 'car'
                          ? 'bg-brand-50 border border-brand-200'
                          : 'bg-brand-50 border border-brand-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          {mlPrediction.recommended_transport === 'car' ? (
                            <Car className="h-4 w-4 text-brand-600" />
                          ) : (
                            <Plane className="h-4 w-4 text-brand-600" />
                          )}
                          <span className={`text-sm font-semibold ${
                            mlPrediction.recommended_transport === 'car' ? 'text-brand-700' : 'text-brand-700'
                          }`}>
                            Recommended: {mlPrediction.recommended_transport === 'car' ? 'Car' : 'Plane'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600">{mlPrediction.reason}</p>
                      </div>
                    )}

                    {/* Comparison table */}
                    <div className="space-y-2">
                      {/* Car option */}
                      {mlPrediction.car && mlPrediction.car.duration_minutes && (
                        <div className={`p-3 rounded-xl border ${
                          mlPrediction.recommended_transport === 'car'
                            ? 'bg-brand-50 border-brand-300'
                            : 'bg-steel-50 border-steel-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Car className="h-4 w-4 text-brand-600" />
                            <span className="text-sm font-medium text-slate-800">Car</span>
                            {mlPrediction.recommended_transport === 'car' && (
                              <CheckCircle className="h-4 w-4 text-green-500 ml-auto" />
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-slate-500">Duration:</span>
                              <span className="ml-1 font-medium">{mlPrediction.car.duration_formatted}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Cost:</span>
                              <span className="ml-1 font-medium">{mlPrediction.car.cost_formatted}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Plane option */}
                      {mlPrediction.plane && (
                        <div className={`p-3 rounded-xl border ${
                          mlPrediction.recommended_transport === 'plane'
                            ? 'bg-brand-50 border-brand-300'
                            : 'bg-steel-50 border-steel-200'
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Plane className="h-4 w-4 text-brand-600" />
                            <span className="text-sm font-medium text-slate-800">Plane</span>
                            {mlPrediction.recommended_transport === 'plane' && (
                              <CheckCircle className="h-4 w-4 text-green-500 ml-auto" />
                            )}
                          </div>
                          {mlPrediction.plane.duration_minutes ? (
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-slate-500">Duration:</span>
                                <span className="ml-1 font-medium">{mlPrediction.plane.duration_formatted}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Cost:</span>
                                <span className="ml-1 font-medium">{mlPrediction.plane.cost_formatted}</span>
                              </div>
                              {mlPrediction.plane.message && (
                                <div className="col-span-2 text-slate-400 italic mt-1">
                                  {mlPrediction.plane.message}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400 italic">
                              {mlPrediction.plane.message || 'Not recommended for this distance'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer note */}
                    <div className="text-xs text-slate-400 text-center pt-2 border-t border-steel-100">
                      Estimates based on distance calculation
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add New Location Modal */}
        {showAddLocationModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  Save {showAddLocationModal.type === 'pickup' ? 'Pickup' : 'Dropoff'} Location
                </h3>
                <button
                  onClick={() => setShowAddLocationModal({ show: false, type: 'both', defaultValue: '' })}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formElements = e.target.elements;
                  try {
                    await saveNewTravelLocation({
                      name: formElements.locationName.value,
                      address: formElements.locationAddress.value,
                      location_type: formElements.locationType.value,
                      description: formElements.locationDescription.value || null
                    });
                    setShowAddLocationModal({ show: false, type: 'both', defaultValue: '' });
                  } catch (error) {
                    alert('Error saving location: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Location Name *
                  </label>
                  <input
                    type="text"
                    name="locationName"
                    required
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g. BER Terminal 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Full Address *
                  </label>
                  <input
                    type="text"
                    name="locationAddress"
                    required
                    defaultValue={showAddLocationModal.defaultValue}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g. Berlin Brandenburg Airport, Terminal 1, Willy-Brandt-Platz 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Location Type *
                  </label>
                  <select
                    name="locationType"
                    defaultValue={showAddLocationModal.type}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="pickup">Pickup Point Only</option>
                    <option value="dropoff">Dropoff Location Only</option>
                    <option value="both">Both (Pickup & Dropoff)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    name="locationDescription"
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    rows="2"
                    placeholder="e.g. Near departure hall, look for company sign"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddLocationModal({ show: false, type: 'both', defaultValue: '' })}
                    className="px-4 py-2 text-slate-600 hover:text-slate-800 rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
                  >
                    Save Location
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelCoordination;
